import React, { useState, useEffect, useRef } from "react";
import TermsPage from "./TermsPage";
import PrivacyPage from "./PrivacyPage";
import { createClient } from "@supabase/supabase-js";

/* ═══════════════ SUPABASE CLIENT ═══════════════ */
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  { auth: { persistSession: true, storageKey: 'rezi-auth', storage: window.sessionStorage } }
);

/* ═══════════════ DARK MODE CONTEXT ═══════════════ */
const DarkCtx = React.createContext(false);
function useDark() { return React.useContext(DarkCtx); }

/* ═══════════════ DESIGN TOKENS ═══════════════ */
function makeT(dark) {
  return dark ? {
    navy:     "#0f2530",
    navyHdr:  "#1A3A4A",
    blue:     "#2196C9",
    blueLt:   "#4DAFE0",
    bluePale: "rgba(33,150,201,0.15)",
    gold:     "#F5A623",
    goldLt:   "#F7BC55",
    goldPale: "rgba(245,166,35,0.13)",
    bg:       "#0d1e28",
    surface:  "#142030",
    border:   "rgba(255,255,255,0.10)",
    text:     "#e8f0f5",
    muted:    "#7a9aaa",
    success:  "#2ecc8a", successBg:"rgba(46,204,138,0.13)",
    danger:   "#e05555", dangerBg:"rgba(224,85,85,0.13)",
    info:     "#2196C9", infoBg:"rgba(33,150,201,0.13)",
    inputBg:  "rgba(255,255,255,0.06)",
    inputBorder:"rgba(255,255,255,0.15)",
    cardBg:   "#182c3a",
    dark: true,
  } : {
    navy:     "#1A3A4A",
    navyHdr:  "#1A3A4A",
    blue:     "#2196C9",
    blueLt:   "#4DAFE0",
    bluePale: "#EBF6FD",
    gold:     "#F5A623",
    goldLt:   "#F7BC55",
    goldPale: "#FEF6E7",
    bg:       "#F0F6FA",
    surface:  "#FFFFFF",
    border:   "#D0E4EF",
    text:     "#1A3A4A",
    muted:    "#5A7A8A",
    success:  "#1A7F5A", successBg:"#E8F5EF",
    danger:   "#C0392B", dangerBg:"#FDEAEA",
    info:     "#2196C9", infoBg:"#EBF6FD",
    inputBg:  "#ffffff",
    inputBorder:"#DDE3F0",
    cardBg:   "#ffffff",
    dark: false,
  };
}
// Default light tokens — overridden inside components via useDark()
let T = makeT(false);

const ID_TYPES = ["RSA ID","Traffic Register No.","Foreign ID","Business Reg. No."];
const GENDER_OPTS = ["Male","Female","One-man Business","Private Company","Close Corporation","Other"];
const CATEGORIES = [
  {value:"A",label:"A — Motorcycle / Tricycle / Quadrucycle"},
  {value:"B",label:"B — Light Passenger (< 12 persons)"},
  {value:"C",label:"C — Heavy Passenger (12+ persons)"},
  {value:"K",label:"K — Light Load (GVM ≤ 3 500 kg)"},
  {value:"L",label:"L — Heavy Load (GVM > 3 500 kg, no tow)"},
  {value:"M",label:"M — Heavy Load (GVM > 3 500 kg, with tow)"},
  {value:"U",label:"U — Special Vehicle"},
];
const COUNTRIES = [
  "","South Africa",
  "Afghanistan","Albania","Algeria","Angola","Argentina","Australia","Austria",
  "Azerbaijan","Bangladesh","Belgium","Bolivia","Botswana","Brazil","Bulgaria",
  "Cameroon","Canada","Chile","China","Colombia","Congo (DRC)","Croatia","Cuba",
  "Czech Republic","Denmark","Ecuador","Egypt","Ethiopia","Finland","France",
  "Germany","Ghana","Greece","Guatemala","Hungary","India","Indonesia","Iran",
  "Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan",
  "Kenya","Kuwait","Lebanon","Lesotho","Libya","Malawi","Malaysia","Mali",
  "Mexico","Morocco","Mozambique","Namibia","Netherlands","New Zealand","Nigeria",
  "Norway","Pakistan","Peru","Philippines","Poland","Portugal","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Serbia","Sierra Leone","Singapore","Somalia",
  "Spain","Sri Lanka","Sudan","Swaziland","Sweden","Switzerland","Syria","Taiwan",
  "Tanzania","Thailand","Tunisia","Turkey","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","United States","Venezuela","Vietnam",
  "Yemen","Zambia","Zimbabwe","Other"
];

const BLANK_FORM = {
  sl_idType:"",sl_idNumber:"",sl_idCountry:"",
  sl_surname:"",sl_initials:"",sl_firstNames:"",sl_email:"",
  sl_telDay:"",sl_telDay_code:"",sl_telDay_num:"",
  sl_showProxy:false,
  sl_proxy_idType:"",sl_proxy_idNumber:"",sl_proxy_country:"",
  sl_proxy_surname:"",sl_proxy_initials:"",
  sl_showRep:false,
  sl_rep_idType:"",sl_rep_idNumber:"",sl_rep_country:"",
  sl_rep_surname:"",sl_rep_initials:"",
  th_idType:"",th_idNumber:"",th_idCountry:"",th_gender:"",
  th_surname:"",th_initials:"",th_firstNames:"",th_dob:"",
  th_email:"",
  th_cell:"",th_cell_code:"",th_cell_num:"",
  th_telHome:"",th_telHome_code:"",th_telHome_num:"",
  th_telDay:"",th_telDay_code:"",th_telDay_num:"",
  th_fax:"",th_fax_code:"",th_fax_num:"",
  th_postalAddr:"",th_postalAddr2:"",th_postalAddr3:"",th_postalSuburb:"",th_postalCity:"",th_postalCode:"",
  th_streetAddr:"",th_streetAddr2:"",th_streetAddr3:"",th_streetSuburb:"",th_streetCity:"",th_streetCode:"",
  th_streetSameAsPostal:false,th_noticeAddr:"postal",
  th_showProxy:false,
  th_proxy_idType:"",th_proxy_idNumber:"",th_proxy_country:"",
  th_proxy_surname:"",th_proxy_initials:"",
  th_showRep:false,
  th_rep_idType:"",th_rep_idNumber:"",th_rep_country:"",
  th_rep_surname:"",th_rep_initials:"",
  ow_same:false,
  ow_showProxy:false,
  ow_proxy_idType:"",ow_proxy_idNumber:"",ow_proxy_country:"",
  ow_proxy_surname:"",ow_proxy_initials:"",
  ow_showRep:false,
  ow_rep_idType:"",ow_rep_idNumber:"",ow_rep_country:"",
  ow_rep_surname:"",ow_rep_initials:"",
  ow_idType:"",ow_idNumber:"",ow_idCountry:"",ow_gender:"",
  ow_surname:"",ow_initials:"",ow_firstNames:"",ow_dob:"",
  ow_email:"",ow_telHome:"",ow_telDay:"",ow_cell:"",
  ow_telHome_code:"",ow_telHome_num:"",ow_telDay_code:"",ow_telDay_num:"",
  ow_fax_code:"",ow_fax_num:"",
  ow_postalAddr:"",ow_postalAddr2:"",ow_postalAddr3:"",ow_postalSuburb:"",ow_postalCity:"",ow_postalCode:"",
  ow_streetAddr:"",ow_streetAddr2:"",ow_streetAddr3:"",ow_streetSuburb:"",ow_streetCity:"",ow_streetCode:"",
  ow_noticeAddr:"postal",
  vc_licenceNo:"",vc_vrn:"",vc_chassis:"",vc_make:"",vc_series:"",
  vc_natisModel:"",vc_category:"",vc_driven:"",
  vc_description:"",vc_descOther:"",
  vc_engineNo:"",vc_kw:"",vc_cc:"",
  vc_fuelType:"",vc_fuelOther:"",
  vc_tare:"",vc_gvm:"",
  vc_transmission:"",vc_colour:"",vc_colourOther:"",
  vc_usedFor:"",vc_usedForOther:"",
  vc_sector:"",vc_sectorOther:"",
  vc_odometer:"",vc_keepAddr:"",vc_keepAddr2:"",vc_keepAddr3:"",vc_keepSuburb:"",vc_keepCity:"",vc_keepCode:"",
  vc_dateAcquired:"",vc_ownership:"",vc_publicRoad:"",vc_regReason:"",
  nco_changeReason:"",nco_dateOfChange:"",
  vc_cashSale:false, vc_financeType:"cash", vc_stockType:"tradein",
  rlv_transaction:{ registration:true, licensing:false },
  nco_decl_a_role:"Motor Dealer",nco_decl_a_place:"",nco_decl_a_date:"",nco_decl_a_sig:"",nco_decl_a_confirmed:false,
  decl_a_role:"Motor Dealer",decl_a_place:"",decl_a_date:"",decl_a_sig:"",decl_a_confirmed:false,
  decl_b_role:"Title Holder",decl_b_place:"",decl_b_date:"",decl_b_sig:"",decl_b_confirmed:false,
  decl_c_role:"Owner",decl_c_place:"",decl_c_date:"",decl_c_sig:"",decl_c_confirmed:false,
};

const BLANK_PROFILE = {
  companyName:"",regNumber:"",vatNumber:"",
  email:"",phone:"",
  address:"",address2:"",address3:"",addressSuburb:"",addressCity:"",addressCode:"",
  postalAddr:"",postalAddr2:"",postalAddr3:"",postalSuburb:"",postalCity:"",postalCode:"",
  proxy_idType:"",proxy_idNumber:"",proxy_country:"",
  proxy_surname:"",proxy_initials:"",
};


const BLANK_SLOT = {
  slot_name:"",
  idType:"", idNumber:"", idCountry:"", gender:"",
  surname:"", initials:"", firstNames:"", dob:"",
  email:"", cell:"", telDay:"", telDay_code:"", telDay_num:"", telHome:"", telHome_code:"", telHome_num:"",
  postalAddr:"", postalAddr2:"", postalAddr3:"", postalSuburb:"", postalCity:"", postalCode:"",
  streetAddr:"", streetAddr2:"", streetAddr3:"", streetSuburb:"", streetCity:"", streetCode:"",
  noticeAddr:"postal",
  proxy_idType:"", proxy_idNumber:"", proxy_country:"", proxy_surname:"", proxy_initials:"",
};

// LS kept only for dark mode preference (non-sensitive UI state)
const LS = {
  get:(k,d)=>{ try{ const v=localStorage.getItem(k); return v?JSON.parse(v):d; }catch{ return d; }},
  set:(k,v)=>{ try{ localStorage.setItem(k,JSON.stringify(v)); }catch{} },
  del:(k)=>{ try{ localStorage.removeItem(k); }catch{} },
};

/* ═══════════════ VALIDATION ═══════════════ */
function validateForm(f){
  const e={};
  if(!f.sl_idNumber) e.sl_idNumber="Seller ID number required";
  if(!f.sl_surname)  e.sl_surname="Seller surname required";
  if(!f.th_idNumber) e.th_idNumber="Buyer ID number required";
  if(!f.th_surname)  e.th_surname="Buyer surname required";
  if(!f.vc_chassis)  e.vc_chassis="Chassis number required";
  if(!f.vc_make)     e.vc_make="Vehicle make required";
  if(!f.vc_category) e.vc_category="Vehicle category required";
  return e;
}

/* ═══════════════ HTML FALLBACKS ═══════════════ */
function row(k,v){ return v?`<tr><td>${k}</td><td>${v}</td></tr>`:""; }
function generateRLV(d){
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>RLV</title>
<style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px}
h1{color:#0D1B35;border-bottom:3px solid #C8963E;padding-bottom:10px}
h2{color:#0D1B35;margin-top:20px}table{width:100%;border-collapse:collapse;margin-bottom:20px}
td{padding:8px;border:1px solid #ddd;font-size:13px}tr:nth-child(even){background:#f9f9f9}</style></head>
<body><h1>RLV — Registration &amp; Licensing</h1>
<h2>Part A — Title Holder</h2><table>
${row("ID Type",d.th_idType)}${row("ID Number",d.th_idNumber)}
${row("Surname",d.th_surname)}${row("Initials",d.th_initials)}${row("First Names",d.th_firstNames)}
${row("DOB",d.th_dob)}${row("Email",d.th_email)}
${row("Postal Address",d.th_postalAddr)}${row("",d.th_postalAddr2)}${row("",d.th_postalAddr3)}</table>
<h2>Part B — Owner</h2><table>
${d.ow_same?"<tr><td colspan=2><em>Section B left blank — same as Title Holder (Part A)</em></td></tr>":
`${row("ID Type",d.ow_idType)}${row("ID Number",d.ow_idNumber)}${row("Surname",d.ow_surname)}`}
</table>
<h2>Part C — Vehicle</h2><table>
${row("Chassis",d.vc_chassis)}${row("Make",d.vc_make)}${row("Series",d.vc_series)}
${row("Category",d.vc_category)}${row("Colour",d.vc_colour)}${row("Date Acquired",d.vc_dateAcquired)}
</table></body></html>`;
}
function generateNCO(d){
  const v = (val,fallback) => val||fallback||"";
  const addr = (a,a2,a3,sub,city,code) =>
    [a,a2,a3].filter(Boolean).join(", ") + (sub?", "+sub:"") + (city?", "+city:"") + (code?" "+code:"");
  const idBlock = (type,num,country) => `
    <tr><td class="lbl">Type of Identification</td><td>${v(type)}</td></tr>
    <tr><td class="lbl">Identification Number</td><td>${v(num)}</td></tr>
    ${country?`<tr><td class="lbl">Country of Issue</td><td>${country}</td></tr>`:""}`;
  const proxyBlock = (type,num,country,sur,init) => (sur||num) ? `
    <tr><td colspan="2" class="sec">Executor / Organisation's Proxy / Representative</td></tr>
    ${idBlock(type,num,country)}
    <tr><td class="lbl">Surname &amp; Initials</td><td>${v(sur)} ${v(init)}</td></tr>` : "";

  const noticeAddr = d.ow_noticeAddr==="street" ? "Street address" : "Postal address";
  const postalBuyer  = addr(d.ow_postalAddr,d.ow_postalAddr2,d.ow_postalAddr3,d.ow_postalSuburb,d.ow_postalCity,d.ow_postalCode);
  const streetBuyer  = addr(d.ow_streetAddr,d.ow_streetAddr2,d.ow_streetAddr3,d.ow_streetSuburb,d.ow_streetCity,d.ow_streetCode);
  const dateStr = (iso) => iso ? iso.split("-").reverse().join("/") : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>NCO (2012)</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Arial,sans-serif;max-width:820px;margin:30px auto;padding:20px;font-size:12px;color:#111}
  .form-title{background:#c8a000;color:#fff;padding:10px 16px;font-weight:bold;font-size:14px;letter-spacing:.04em}
  .form-sub{background:#f5e98a;padding:6px 16px;font-size:11px;border-bottom:2px solid #c8a000}
  .part-head{background:#e8d84a;padding:6px 12px;font-weight:bold;font-size:12px;letter-spacing:.05em;
    text-transform:uppercase;border-top:2px solid #c8a000;border-bottom:1px solid #c8a000;margin-top:0}
  table{width:100%;border-collapse:collapse;margin-bottom:0}
  td{padding:5px 8px;border:1px solid #bbb;vertical-align:top;font-size:12px}
  td.lbl{width:220px;background:#fafafa;font-weight:bold;color:#555;font-size:11px}
  td.sec{background:#fef9cc;font-weight:bold;font-size:11px;letter-spacing:.04em;text-transform:uppercase;border-top:2px solid #d4b800}
  td.decl{background:#fff8dc;font-size:11px;line-height:1.6;border-top:2px solid #d4b800}
  .notice{background:#fff8dc;border:1px solid #d4b800;padding:8px 12px;font-size:11px;margin:8px 0 0 0;line-height:1.5}
  .footer{margin-top:24px;border-top:2px solid #c8a000;padding-top:10px;font-size:10px;color:#777;text-align:center}
</style></head>
<body>

<div class="form-title">NCO — NOTIFICATION OF CHANGE OF OWNERSHIP / SALE OF MOTOR VEHICLE &nbsp;|&nbsp; NCO(6)(2012/03)</div>
<div class="form-sub">National Road Traffic Act, 1996 &nbsp;·&nbsp; <strong style="color:#7a5c00">Yellow Form / Geel Vorm</strong></div>

<!-- PART A -->
<div class="part-head">Part A — Particulars of Seller / Owner &nbsp;(A — Besonderhede van Verkoper/Eienaar)</div>
<table>
  ${idBlock(d.sl_idType, d.sl_idNumber, d.sl_idCountry)}
  <tr><td class="lbl">Surname &amp; Initials / Organisation</td><td>${v(d.sl_surname)} ${v(d.sl_initials)}</td></tr>
  <tr><td class="lbl">E-mail Address</td><td>${v(d.sl_email)}</td></tr>
  <tr><td class="lbl">Contact Tel (Day)</td><td>${v(d.sl_telDay_code)}&nbsp;${v(d.sl_telDay_num)}</td></tr>
  ${proxyBlock(d.sl_proxy_idType,d.sl_proxy_idNumber,d.sl_proxy_country,d.sl_proxy_surname,d.sl_proxy_initials)}
  <tr><td class="decl" colspan="2">
    <strong>Declaration / Verklaring — Part A:</strong><br/>
    I, the <strong>${v(d.nco_decl_a_role,"seller")}</strong>, declare that I have disposed of or delivered the motor vehicle in Part C to the buyer in Part B, and that all particulars furnished are true and correct.<br/>
    Signature: ______________ &nbsp;&nbsp; Place: <strong>${v(d.nco_decl_a_place)}</strong> &nbsp;&nbsp; Date: <strong>${dateStr(d.nco_decl_a_date)}</strong>
  </td></tr>
</table>

<!-- PART B -->
<div class="part-head">Part B — Particulars of Buyer &nbsp;(B — Besonderhede van Koper)</div>
<table>
  ${idBlock(d.ow_idType, d.ow_idNumber, d.ow_idCountry)}
  <tr><td class="lbl">Surname &amp; Initials / Organisation</td><td>${v(d.ow_surname)} ${v(d.ow_initials)}</td></tr>
  <tr><td class="lbl">E-mail Address</td><td>${v(d.ow_email)}</td></tr>
  <tr><td class="lbl">Postal Address</td><td>${postalBuyer||"—"}</td></tr>
  <tr><td class="lbl">Street Address</td><td>${streetBuyer||"—"}</td></tr>
  <tr><td class="lbl">Address for Notices</td><td>${noticeAddr}</td></tr>
  <tr><td class="lbl">Contact Tel (Day)</td><td>${v(d.ow_telDay_code)}&nbsp;${v(d.ow_telDay_num)}</td></tr>
  ${proxyBlock(d.ow_proxy_idType,d.ow_proxy_idNumber,d.ow_proxy_country,d.ow_proxy_surname,d.ow_proxy_initials)}
  <tr><td class="decl" colspan="2">
    <strong>Declaration / Verklaring — Part B:</strong><br/>
    I, the <strong>${v(d.decl_c_role,"buyer")}</strong>, declare that I have acquired or taken delivery of the motor vehicle in Part C from the seller in Part A, and that all particulars furnished are true and correct.<br/>
    Signature: ______________ &nbsp;&nbsp; Place: <strong>${v(d.decl_c_place)}</strong> &nbsp;&nbsp; Date: <strong>${dateStr(d.decl_c_date)}</strong>
  </td></tr>
</table>

<!-- PART C -->
<div class="part-head">Part C — Identification of Motor Vehicle &nbsp;(C — Identifikasie van Motorvoertuig)</div>
<table>
  <tr><td class="lbl">Licence Number</td><td>${v(d.vc_licenceNo)}</td></tr>
  <tr><td class="lbl">Vehicle Register Number</td><td>${v(d.vc_vrn)}</td></tr>
  <tr><td class="lbl">Chassis Number / VIN</td><td><strong>${v(d.vc_chassis)}</strong></td></tr>
  <tr><td class="lbl">Make</td><td>${v(d.vc_make)}</td></tr>
  <tr><td class="lbl">Odometer Reading</td><td>${d.vc_odometer ? d.vc_odometer+" km" : "—"}</td></tr>
  <tr><td class="lbl">Reason for Change</td><td>${v(d.nco_changeReason)}</td></tr>
  <tr><td class="lbl">Date of Change</td><td>${dateStr(d.nco_dateOfChange)}</td></tr>
</table>

<div class="notice">
  <strong>Note (2012 NCO):</strong> Seller must furnish this notice to the registering authority within 21 days of sale.
  Buyer must apply for registration on form RLV within 21 days of purchase.
</div>

<div class="footer">NCO(6)(2012/03) — Generated by ReZi · rezi.co.za</div>
</body></html>`;
}

/* ═══════════════ BASE INPUT STYLE ═══════════════ */
const iS = {
  width:"100%",padding:"10px 14px",borderRadius:9,
  border:"1.5px solid var(--rz-input-border,#DDE3F0)",
  fontSize:14,background:"var(--rz-input-bg,#fff)",
  color:"var(--rz-text,#1A3A4A)",outline:"none",boxSizing:"border-box",
};

/* ═══════════════════════════════════════════════════════════════════════
   SHARED FORM COMPONENTS
   All field layout logic lives here. No section may implement its own
   grid, alignment, or label styling — use these components instead.
   ─────────────────────────────────────────────────────────────────────

   FormRow        — canonical label + control layout (foundation)
   AddressField   — 3 lines + suburb / city / code via FormRow
   CharacterGrid  — per-character cell boxes for IDs, chassis, etc.
   DateGrid       — DD / MM / YYYY split-cell date entry
   OwnershipSelector — Cash / Finance / Dealer Stock picker
   PhoneRow       — area code + number via FormRow
   NoticeAddrPicker — postal / street toggle via FormRow
═══════════════════════════════════════════════════════════════════════ */

/**
 * FormRow — the single source of truth for label+field layout.
 * Every other shared component is built on top of this.
 *
 * Props:
 *   label      {string}  — field label text
 *   required   {bool}    — shows red asterisk
 *   labelWidth {string}  — CSS width for label column (default "120px")
 *   children             — input / control content
 *   hint       {string}  — helper text below the control
 *   error      {string}  — validation error in red
 *   style      {object}  — extra styles for outer wrapper
 */
function FormRow({ label, required, labelWidth="120px", children, hint, error, style={} }) {
  return (
    <div style={{ marginBottom:14, ...style }}>
      <div style={{ display:"grid", gridTemplateColumns:`${labelWidth} 1fr`, alignItems:"start", gap:10 }}>
        <div style={{ paddingTop:11, minWidth:0 }}>
          {label !== undefined && (
            <span style={{
              fontSize:11, fontWeight:700, color:"var(--rz-text,#6B7A9F)",
              textTransform:"uppercase", letterSpacing:"0.05em",
              lineHeight:1.3, display:"block", opacity:0.7,
            }}>
              {label}{required && <span style={{color:"#C0392B",marginLeft:3}}>*</span>}
            </span>
          )}
        </div>
        <div style={{ minWidth:0 }}>
          {children}
          {hint && !error && <div style={{fontSize:11,color:"#6B7A9F",marginTop:4,lineHeight:1.4}}>{hint}</div>}
          {error && <div style={{fontSize:11,color:"#C0392B",fontWeight:700,marginTop:4}}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * AddressField — three address lines + suburb / city / code.
 * All address blocks in the form must use this component.
 *
 * Props:
 *   label    {string}  — label beside first line
 *   prefix   {string}  — key prefix, e.g. "th_postal" → th_postalAddr, th_postalAddr2 …
 *   form     {object}
 *   setForm  {fn}
 *   showCode {bool}    — include postal code cell (default true)
 */
function AddressField({ label, prefix, form, setForm, showCode=true }) {
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));
  const inp = (key, ph) => (
    <input value={form[key]||""} onChange={set(key)} placeholder={ph||""}
      style={{ ...iS, marginBottom:5 }} />
  );
  return (
    <FormRow label={label} labelWidth="130px" style={{ marginBottom:16 }}>
      {inp(`${prefix}Addr`,  "Address line 1")}
      {inp(`${prefix}Addr2`, "Address line 2")}
      <input value={form[`${prefix}Addr3`]||""} onChange={set(`${prefix}Addr3`)}
        placeholder="Address line 3"
        style={{ ...iS, marginBottom:5 }} />
      <div style={{ display:"grid", gridTemplateColumns:showCode?"1fr 1fr 80px":"1fr 1fr", gap:6, marginTop:2 }}>
        <input value={form[`${prefix}Suburb`]||""} onChange={set(`${prefix}Suburb`)}
          placeholder="Suburb" style={iS} />
        <input value={form[`${prefix}City`]||""} onChange={set(`${prefix}City`)}
          placeholder="City / Town" style={iS} />
        {showCode && (
          <input value={form[`${prefix}Code`]||""} onChange={set(`${prefix}Code`)}
            placeholder="Code" style={iS} />
        )}
      </div>
    </FormRow>
  );
}

/**
 * CharacterGrid — per-character bordered cells for IDs, chassis numbers.
 * Uses an invisible overlay input on top of visible decoration cells.
 *
 * Props:
 *   label     {string}
 *   required  {bool}
 *   value     {string}
 *   onChange  {fn}       — receives { target: { value } }
 *   maxLength {number}   — number of cells (default 13)
 *   hint      {string}
 *   error     {string}
 */
// eslint-disable-next-line no-unused-vars
function CharacterGrid({ label, required, value="", onChange, maxLength=13, hint, error }) {
  const chars = (value||"").split("").concat(Array(maxLength).fill("")).slice(0, maxLength);
  const handleChange = e => {
    const raw = e.target.value.replace(/\s/g,"").slice(0, maxLength);
    onChange({ target:{ value:raw } });
  };
  return (
    <FormRow label={label} required={required} hint={hint} error={error} style={{ marginBottom:16 }}>
      <div style={{ position:"relative" }}>
        <div style={{ display:"flex", gap:4, pointerEvents:"none", userSelect:"none",
          position:"absolute", top:0, left:0, right:0, bottom:0, padding:"2px 0" }}>
          {chars.map((ch, i) => (
            <div key={i} style={{
              flex:1, height:38, border:`1.5px solid ${error?"#C0392B":"#DDE3F0"}`,
              borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:15, fontWeight:700, color:T.navy,
              background:ch?"#fff":"#F7F9FC",
            }}>{ch}</div>
          ))}
        </div>
        <input value={value||""} onChange={handleChange} maxLength={maxLength}
          style={{ ...iS, opacity:0, height:44, position:"relative", zIndex:1, cursor:"text" }} />
      </div>
    </FormRow>
  );
}

/**
 * DateGrid — DD / MM / YYYY split-cell date entry mapped to ISO string.
 *
 * Props:
 *   label    {string}
 *   required {bool}
 *   value    {string}  — ISO "YYYY-MM-DD"
 *   onChange {fn}      — receives { target: { value: "YYYY-MM-DD" } }
 */
function DateGrid({ label, required, value="", onChange }) {
  return (
    <FormRow label={label} required={required} style={{ marginBottom:16 }}>
      <input
        type="date"
        value={value||""}
        onChange={onChange}
        style={{ ...iS, cursor:"pointer" }}
      />
    </FormRow>
  );
}

/**
 * OwnershipSelector — Cash / Finance / Dealer Stock transaction-type picker.
 * Writes to form.vc_financeType and form.vc_cashSale.
 */
// eslint-disable-next-line no-unused-vars
function OwnershipSelector({ form, setForm }) {
  const opts = [
    { value:"cash",    label:"💵 Cash Sale",    sub:"Buyer = Title Holder",    color:T.success, bg:T.successBg },
    { value:"finance", label:"🏦 Finance",       sub:"Bank holds title",        color:T.info,    bg:T.infoBg },
    { value:"stock",   label:"🏢 Dealer Stock",  sub:"Dealer buys from client", color:"#6A1B9A", bg:"var(--rz-input-bg,#F3E5F5)" },
  ];
  return (
    <FormRow label="Transaction Type" style={{ marginBottom:18 }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {opts.map(opt => {
          const active = form.vc_financeType === opt.value;
          return (
            <div key={opt.value}
              onClick={() => setForm(f => ({ ...f, vc_financeType:opt.value, vc_cashSale:opt.value==="cash", ow_same:false }))}
              style={{ flex:1, minWidth:90, padding:"8px 10px", borderRadius:10, cursor:"pointer",
                textAlign:"center", transition:"all .2s",
                border:`2px solid ${active?opt.color:T.border}`,
                background:active?opt.bg:"#fff" }}>
              <div style={{ fontSize:12, fontWeight:800, color:active?opt.color:T.muted }}>{opt.label}</div>
              <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>{opt.sub}</div>
            </div>
          );
        })}
      </div>
    </FormRow>
  );
}

/**
 * PhoneRow — area code + number pair, using FormRow for alignment.
 *
 * Props:
 *   label   {string}
 *   codeKey {string}  — form key for area code
 *   numKey  {string}  — form key for number
 *   form    {object}
 *   setForm {fn}
 */
function PhoneRow({ label, codeKey, numKey, form, setForm }) {
  const set = key => e => setForm(f => ({ ...f, [key]:e.target.value }));
  return (
    <FormRow label={label} style={{ marginBottom:14 }}>
      <div style={{ display:"grid", gridTemplateColumns:"90px 1fr", gap:8 }}>
        <input value={form[codeKey]||""} onChange={set(codeKey)} placeholder="Code" style={iS} />
        <input value={form[numKey]||""}  onChange={set(numKey)}  placeholder="Number" style={iS} />
      </div>
    </FormRow>
  );
}

/**
 * NoticeAddrPicker — "Send notices to" postal/street toggle, via FormRow.
 *
 * Props:
 *   fieldKey {string}  — e.g. "th_noticeAddr"
 *   form     {object}
 *   setForm  {fn}
 */
function NoticeAddrPicker({ fieldKey, form, setForm }) {
  return (
    <FormRow label="Send notices to" style={{ marginBottom:16 }}>
      <div style={{ display:"flex", gap:8 }}>
        {["postal","street"].map(a => (
          <button key={a} onClick={() => setForm(f => ({ ...f, [fieldKey]:a }))}
            style={{ flex:1, padding:"8px 0", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
              border:`1.5px solid ${form[fieldKey]===a?"#2196C9":"var(--rz-input-border,#DDE3F0)"}`,
              background:form[fieldKey]===a?"#2196C9":"var(--rz-input-bg,#fff)",
              color:form[fieldKey]===a?"#fff":"var(--rz-text,#5A7A8A)" }}>
            {a==="postal"?"Postal":"Street"}
          </button>
        ))}
      </div>
    </FormRow>
  );
}

/**
 * FormRowInput / FormRowSelect — FormRow-wrapped text input and select.
 * All form section fields use these instead of bare inputs.
 */
function FormRowInput({ label, required, fieldKey, form, setForm, type, placeholder, hint, error, style:xs }) {
  return (
    <FormRow label={label} required={required} hint={hint} error={error} style={{ marginBottom:14 }}>
      <input type={type||"text"} value={form[fieldKey]||""}
        onChange={e => setForm(f => ({ ...f, [fieldKey]:e.target.value }))}
        placeholder={placeholder||""}
        style={{ ...iS, ...(xs||{}) }} />
    </FormRow>
  );
}
function FormRowSelect({ label, required, fieldKey, form, setForm, options, hint }) {
  return (
    <FormRow label={label} required={required} hint={hint} style={{ marginBottom:14 }}>
      <select value={form[fieldKey]||""} onChange={e => setForm(f => ({ ...f, [fieldKey]:e.target.value }))}
        style={{ ...iS, cursor:"pointer", appearance:"auto" }}>
        <option value="">— select —</option>
        {options.map(o => typeof o==="string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FormRow>
  );
}

/* ═══════════════ LEGACY PRIMITIVES ═══════════════
   Used outside main form sections (login, profile, dashboard).
   Do NOT use inside TransactionForm — use FormRow-based components there.
═══════════════════════════════════════════════════ */
function Lbl({ children, required }) {
  return <label style={{ display:"block", fontSize:12, fontWeight:700, color:"var(--rz-text,#6B7A9F)",
    textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6, opacity:0.7 }}>
    {children}{required && <span style={{ color:"#C0392B", marginLeft:3 }}>*</span>}
  </label>;
}
function FI({ label, required, value, onChange, type, placeholder, style }) {
  return <div style={{ marginBottom:16 }}>
    {label && <Lbl required={required}>{label}</Lbl>}
    <input type={type||"text"} value={value||""} onChange={onChange}
      placeholder={placeholder||""} style={{ ...iS, ...style }} />
  </div>;
}
function FS({ label, required, value, onChange, options }) {
  return <div style={{ marginBottom:16 }}>
    {label && <Lbl required={required}>{label}</Lbl>}
    <select value={value||""} onChange={onChange}
      style={{ ...iS, cursor:"pointer", appearance:"auto" }}>
      <option value="">— select —</option>
      {options.map(o => typeof o==="string"
        ? <option key={o} value={o}>{o||"— none —"}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>;
}
function Toggle({ label, sub, checked, onChange }) {
  return <div onClick={onChange} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px",
    borderRadius:10, border:`1.5px solid ${checked?"var(--rz-toggle-on,#1A3A4A)":"var(--rz-input-border,#DDE3F0)"}`,
    marginBottom:16, cursor:"pointer",
    background:"var(--rz-input-bg,#fff)", transition:"all .2s" }}>
    <div style={{ width:38, height:22, borderRadius:11,
      background:checked?"#2196C9":"var(--rz-input-border,#DDE3F0)",
      position:"relative", flexShrink:0, transition:"background .2s" }}>
      <div style={{ position:"absolute", top:3, left:checked?18:3, width:16, height:16, borderRadius:"50%",
        background:"#fff", transition:"left .2s" }} />
    </div>
    <div>
      <div style={{ fontSize:14, fontWeight:700, color:"var(--rz-text,#1A3A4A)" }}>{label}</div>
      {sub && <div style={{ fontSize:12, color:"var(--rz-text,#6B7A9F)", marginTop:2, opacity:0.6 }}>{sub}</div>}
    </div>
  </div>;
}
function Btn({ children, onClick, variant, disabled, style }) {
  const base = { padding:"10px 22px", borderRadius:9, fontWeight:700, fontSize:14,
    cursor:disabled?"not-allowed":"pointer", border:"none", transition:"opacity .15s",
    opacity:disabled?.5:1, ...style };
  const variants = {
    gold:{ background:"#F5A623", color:"#1A3A4A" },
    primary:{ background:"#1A3A4A", color:"#fff" },
    outline:{ background:"transparent", border:"1.5px solid var(--rz-input-border,#DDE3F0)", color:"var(--rz-text,#1A3A4A)" },
    success:{ background:"#1A7F5A", color:"#fff" },
  };
  return <button onClick={disabled?undefined:onClick} style={{ ...base, ...(variants[variant]||variants.outline) }}>
    {children}
  </button>;
}
// eslint-disable-next-line no-unused-vars
function InfoBox({ children, color }) {
  const dark = useDark();
  const c = color==="gold"?T.gold:color==="green"?"#1A7F5A":"#1565C0";
  const bg = dark
    ? (color==="gold"?"rgba(245,166,35,.12)":color==="green"?"rgba(26,127,90,.15)":"rgba(21,101,192,.15)")
    : (color==="gold"?"#FFF8EE":color==="green"?"#E8F5EF":"#EEF4FF");
  return <div style={{ background:bg, border:`1px solid ${c}33`, borderLeft:`4px solid ${c}`,
    borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:13, lineHeight:1.7, color:"var(--rz-text,#1A3A4A)" }}>
    {children}
  </div>;
}
function HintBubble({ form, children }) {
  const dark = useDark();
  // form: "rlv" | "nco" | "both"
  const cfg = {
    rlv:  { border:"#2196C9",
            bg: dark?"rgba(33,150,201,0.12)":"#E3F0FF",
            text: dark?"#7ec8e8":"#1565C0" },
    nco:  { border:"#F5A623",
            bg: dark?"rgba(245,166,35,0.12)":"#FFFBEA",
            text: dark?"#f5c86a":"#7A5200" },
    both: { border:"#9c6fdc",
            bg: dark?"rgba(156,111,220,0.12)":"#F0F0FF",
            text: dark?"#c3a4f0":"#4A1580" },
  }[form||"both"];
  const labels = { rlv:"RLV", nco:"NCO", both:"RLV + NCO" };
  const emojis = { rlv:"🔵", nco:"🟡", both:"📋" };
  return (
    <div style={{ display:"flex", gap:10, alignItems:"flex-start",
      background:cfg.bg, border:`1px solid ${cfg.border}55`,
      borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:12, lineHeight:1.6 }}>
      <div style={{ flexShrink:0, marginTop:1, background:cfg.border, color:"#fff",
        borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700, letterSpacing:"0.05em", whiteSpace:"nowrap" }}>
        {emojis[form||"both"]} {labels[form||"both"]}
      </div>
      <div style={{ color:cfg.text }}>{children}</div>
    </div>
  );
}
function SecHead({ children, color }) {
  return <div style={{ fontSize:11, fontWeight:700, color:color||"var(--rz-text,#1A3A4A)",
    textTransform:"uppercase", letterSpacing:"0.08em",
    borderBottom:`1px solid var(--rz-input-border,#DDE3F0)`,
    paddingBottom:6, marginBottom:16, marginTop:8, opacity: color ? 1 : 0.8 }}>{children}</div>;
}
function G2({ children }) { return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>{children}</div>; }
// eslint-disable-next-line no-unused-vars
function G3({ children }) { return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>{children}</div>; }

/* ═══════════════ LOGIN ═══════════════ */
const LOGIN_PHRASES = [
  "Generate RLV & NCO forms in minutes, not 45.",
  "Your dealership details saved. Every time.",
  "Print-ready PDFs. No handwriting required.",
  "Built for South African car dealerships.",
];

function Login({ onLogin, onPricing, onSignup, onForgot, sessionExpiredMsg, onClearExpiredMsg }) {
  /* ── existing auth state ── */
  const [email, setEmail] = useState("");
  const [pass,  setPass]  = useState("");
  const [err,   setErr]   = useState("");
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);
  const passRef  = useRef(null);
  const [emailReady, setEmailReady] = useState(false);
  const [passReady,  setPassReady]  = useState(false);

  /* ── new UI state ── */
  const [typerIdx,      setTyperIdx]      = useState(0);
  const [typerChar,     setTyperChar]     = useState(0);
  const [typerDeleting, setTyperDeleting] = useState(false);
  const [loginVisible,  setLoginVisible]  = useState(false);

  const canvasRef  = useRef(null);
  const mouseRef   = useRef({ x: -9999, y: -9999 });
  const loginRef   = useRef(null);

  // readOnly trick — blocks Chrome autofill
  useEffect(() => {
    const t = setTimeout(() => { setEmailReady(true); setPassReady(true); }, 50);
    return () => clearTimeout(t);
  }, []);

  // ── existing submit ──
  const submit = async () => {
    if (!email || !pass) { setErr("Please enter your email and password."); return; }
    setLoading(true);
    setErr("");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
    if (error) { setErr("Incorrect email or password."); setLoading(false); return; }
    // Revoke all other active sessions for this user — enforces single active session
    fetch("/api/single-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken: data.session.access_token }),
    }).catch(() => {}); // fire-and-forget, don't block login
    // Write session token for backend API validation
    const sessionToken = crypto.randomUUID();
    await supabase.from("users")
      .update({ session_token: sessionToken, last_login: new Date().toISOString() })
      .eq("id", data.user.id);
    sessionStorage.setItem("rezi-session", JSON.stringify({ userId: data.user.id, token: sessionToken }));
    const { data: profile } = await supabase.from("users").select("*").eq("id", data.user.id).single();
    setLoading(false);
    onLogin(profile || { id: data.user.id, email: data.user.email, plan: "Individual", subscribed: false });
  };

  // ── typewriter ──
  useEffect(() => {
    const phrase = LOGIN_PHRASES[typerIdx];
    let timer;
    if (!typerDeleting) {
      if (typerChar < phrase.length) {
        timer = setTimeout(() => setTyperChar(c => c + 1), 45);
      } else {
        timer = setTimeout(() => setTyperDeleting(true), 2400);
      }
    } else {
      if (typerChar > 0) {
        timer = setTimeout(() => setTyperChar(c => c - 1), 20);
      } else {
        setTyperDeleting(false);
        setTyperIdx(i => (i + 1) % LOGIN_PHRASES.length);
      }
    }
    return () => clearTimeout(timer);
  }, [typerChar, typerDeleting, typerIdx]);

  // ── particle canvas ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const SPACING = 36;
    let animId, cols, rows, dots;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      cols = Math.ceil(canvas.width  / SPACING) + 1;
      rows = Math.ceil(canvas.height / SPACING) + 1;
      dots = [];
      for (let r = 0; r < rows; r++)
        for (let c = 0; c < cols; c++)
          dots.push({ x: c * SPACING, y: r * SPACING });
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = e => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("mousemove", onMove);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { x: mx, y: my } = mouseRef.current;
      for (const d of dots) {
        const dist = Math.sqrt((d.x - mx) ** 2 + (d.y - my) ** 2);
        const inf  = Math.max(0, 1 - dist / 140);
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.4 + inf * 2.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(33,150,201,${0.07 + inf * 0.42})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  // ── scroll reveal for login card ──
  useEffect(() => {
    const el = loginRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setLoginVisible(true); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scrollToLogin = () => loginRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div style={{ minHeight:"100vh", background:"#0a1628",
      fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI','Roboto',sans-serif",
      position:"relative", overflowX:"hidden" }}>

      {/* Inject keyframes */}
      <style>{`
        @keyframes rzFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes rzBlink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>

      {/* Particle canvas */}
      <canvas ref={canvasRef} style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%",
        pointerEvents:"none", zIndex:0 }} />

      {/* ══════════════ NAV BAR ══════════════ */}
      <nav style={{ position:"sticky", top:0, zIndex:100, display:"flex", alignItems:"center",
        justifyContent:"space-between", padding:"13px 32px",
        background:"rgba(10,22,40,0.88)", backdropFilter:"blur(14px)",
        borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <img src="/Rezi_Logo.png" alt="ReZi"
            style={{ width:34, height:34, borderRadius:8, objectFit:"cover" }} />
          <span style={{ fontSize:21, fontWeight:900, fontFamily:"'Georgia',serif", letterSpacing:0.5 }}>
            <span style={{ color:"#F5A623" }}>R</span><span style={{ color:"#fff" }}>eZi</span>
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.7)",
            background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.14)",
            borderRadius:999, padding:"5px 13px", letterSpacing:0.3 }}>
            🇿🇦 Built for SA dealerships
          </span>
          <button onClick={scrollToLogin}
            style={{ fontSize:13, fontWeight:700, color:"#fff", background:"#2196C9",
              border:"none", borderRadius:8, padding:"7px 18px", cursor:"pointer" }}
            onMouseEnter={e => e.currentTarget.style.background="#1a7fb5"}
            onMouseLeave={e => e.currentTarget.style.background="#2196C9"}>
            Sign In
          </button>
        </div>
      </nav>

      {/* ══════════════ HERO ══════════════ */}
      <section style={{ position:"relative", zIndex:1, maxWidth:1100, margin:"0 auto",
        padding:"80px 32px 60px", display:"flex", alignItems:"center",
        gap:56, flexWrap:"wrap" }}>

        {/* Left column */}
        <div style={{ flex:"1 1 420px" }}>
          {/* Typewriter line */}
          <div style={{ fontSize:14, fontWeight:600, color:"#2196C9", marginBottom:18,
            minHeight:22, letterSpacing:0.2 }}>
            {LOGIN_PHRASES[typerIdx].slice(0, typerChar)}
            <span style={{ borderRight:"2px solid #2196C9", marginLeft:1,
              animation:"rzBlink 0.8s step-end infinite" }}>&thinsp;</span>
          </div>

          {/* Green badge */}
          <div style={{ display:"inline-flex", alignItems:"center", gap:7,
            background:"rgba(39,174,96,0.14)", border:"1px solid rgba(39,174,96,0.38)",
            borderRadius:999, padding:"5px 14px", marginBottom:22 }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background:"#27AE60",
              display:"inline-block" }} />
            <span style={{ fontSize:12, fontWeight:700, color:"#27AE60", letterSpacing:0.3 }}>
              Free trial — no card needed
            </span>
          </div>

          <h1 style={{ fontSize:"clamp(30px,4.5vw,52px)", fontWeight:900, color:"#fff",
            lineHeight:1.1, margin:"0 0 22px" }}>
            Stop writing RLV forms<br />
            <span style={{ color:"#F5A623" }}>by hand.</span>
          </h1>

          <div style={{ display:"flex", flexDirection:"column", gap:11, marginBottom:34 }}>
            {[
              "Generates complete RLV & NCO forms from one data entry",
              "Dealer details auto-fill — never type them again",
              "Export print-ready PDFs in under 4 minutes",
            ].map((pt, i) => (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <span style={{ color:"#2196C9", fontWeight:800, fontSize:15, lineHeight:1.5 }}>✓</span>
                <span style={{ color:"rgba(255,255,255,0.75)", fontSize:15, lineHeight:1.5 }}>{pt}</span>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <button onClick={onPricing}
              style={{ padding:"14px 28px", background:"#2196C9", color:"#fff", border:"none",
                borderRadius:11, fontWeight:700, fontSize:15, cursor:"pointer",
                boxShadow:"0 8px 24px rgba(33,150,201,0.38)" }}
              onMouseEnter={e => e.currentTarget.style.background="#1a7fb5"}
              onMouseLeave={e => e.currentTarget.style.background="#2196C9"}>
              Start free trial →
            </button>
            <button onClick={scrollToLogin}
              style={{ padding:"14px 28px", background:"transparent",
                color:"rgba(255,255,255,0.8)", border:"1.5px solid rgba(255,255,255,0.22)",
                borderRadius:11, fontWeight:600, fontSize:15, cursor:"pointer" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor="rgba(255,255,255,0.6)"; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(255,255,255,0.22)"; e.currentTarget.style.color="rgba(255,255,255,0.8)"; }}>
              See how it works
            </button>
          </div>
        </div>

        {/* Right column — mock RLV card */}
        <div style={{ flex:"1 1 320px", display:"flex", justifyContent:"center" }}>
          <div style={{ position:"relative", width:"100%", maxWidth:360 }}>
            <div style={{ background:"rgba(255,255,255,0.05)", backdropFilter:"blur(16px)",
              border:"1px solid rgba(255,255,255,0.12)", borderRadius:20,
              padding:"24px 26px", boxShadow:"0 32px 80px rgba(0,0,0,0.5)",
              animation:"rzFloat 3.6s ease-in-out infinite" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.38)",
                letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:16 }}>
                RLV / Application for Registration
              </div>
              {[
                ["Vehicle Make",    "Toyota Hilux 2.8 GD-6"],
                ["VIN Number",      "AHTNE3CD70A000123"],
                ["Engine No.",      "2GD0123456"],
                ["Dealership",      "Cape Motors (Pty) Ltd"],
                ["Registration No.","CA 123-456"],
              ].map(([label, val]) => (
                <div key={label} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)",
                    textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:3 }}>
                    {label}
                  </div>
                  <div style={{ fontSize:13, color:"#fff", fontWeight:500,
                    background:"rgba(255,255,255,0.06)", borderRadius:6, padding:"5px 10px" }}>
                    {val}
                  </div>
                </div>
              ))}
              <div style={{ marginTop:16, display:"flex", gap:8 }}>
                <div style={{ flex:1, background:"#2196C9", borderRadius:8, padding:"8px 0",
                  textAlign:"center", fontSize:12, fontWeight:700, color:"#fff" }}>
                  ✓ RLV Ready
                </div>
                <div style={{ flex:1, background:"rgba(245,166,35,0.15)",
                  border:"1px solid #F5A623", borderRadius:8, padding:"8px 0",
                  textAlign:"center", fontSize:12, fontWeight:700, color:"#F5A623" }}>
                  ✓ NCO Ready
                </div>
              </div>
            </div>
            {/* Gold badge */}
            <div style={{ position:"absolute", top:-14, right:-14,
              background:"linear-gradient(135deg,#F5A623,#e8920d)", borderRadius:12,
              padding:"8px 14px", boxShadow:"0 8px 24px rgba(245,166,35,0.48)",
              textAlign:"center" }}>
              <div style={{ fontSize:17, fontWeight:900, color:"#fff", lineHeight:1 }}>45min</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.85)", fontWeight:600 }}>→ 4min</div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ STATS BAR ══════════════ */}
      <section style={{ position:"relative", zIndex:1,
        background:"rgba(255,255,255,0.03)",
        borderTop:"1px solid rgba(255,255,255,0.07)",
        borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"34px 32px",
          display:"flex", justifyContent:"space-around", flexWrap:"wrap", gap:24 }}>
          {[
            { num:"4 min",   label:"Average form completion" },
            { num:"30 days", label:"Free trial"              },
            { num:"0 hours", label:"Training required"       },
          ].map(({ num, label }) => (
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontSize:34, fontWeight:900, color:"#F5A623", lineHeight:1 }}>{num}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.48)", marginTop:7, fontWeight:500 }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ HOW IT WORKS ══════════════ */}
      <section style={{ position:"relative", zIndex:1, maxWidth:1100, margin:"0 auto",
        padding:"72px 32px" }}>
        <div style={{ textAlign:"center", marginBottom:50 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#2196C9", letterSpacing:"0.2em",
            textTransform:"uppercase", marginBottom:10 }}>How it works</div>
          <h2 style={{ fontSize:"clamp(22px,3vw,36px)", fontWeight:900, color:"#fff", margin:0 }}>
            Three steps. That's it.
          </h2>
        </div>
        <div style={{ display:"flex", gap:24, flexWrap:"wrap", justifyContent:"center" }}>
          {[
            { step:"01", title:"Enter the deal",
              desc:"Type in the vehicle details and customer info once. ReZi remembers your dealership details automatically." },
            { step:"02", title:"ReZi builds the forms",
              desc:"Both RLV and NCO forms are populated instantly. No double entry, no manual calculations." },
            { step:"03", title:"Print and done",
              desc:"Export a clean, print-ready PDF in seconds. Hand it over — job done." },
          ].map(({ step, title, desc }) => (
            <div key={step}
              style={{ flex:"1 1 260px", maxWidth:320, background:"rgba(255,255,255,0.04)",
                border:"1px solid rgba(255,255,255,0.08)", borderRadius:18, padding:"28px 24px",
                transition:"background .2s, border-color .2s" }}
              onMouseEnter={e => { e.currentTarget.style.background="rgba(33,150,201,0.08)"; e.currentTarget.style.borderColor="rgba(33,150,201,0.28)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"; }}>
              <div style={{ fontSize:11, fontWeight:800, color:"#2196C9",
                letterSpacing:"0.15em", marginBottom:12 }}>STEP {step}</div>
              <div style={{ fontSize:18, fontWeight:800, color:"#fff", marginBottom:10 }}>{title}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.52)", lineHeight:1.7 }}>{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ LOGIN CARD ══════════════ */}
      <section ref={loginRef}
        style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column",
          alignItems:"center", padding:"16px 20px 80px",
          opacity:loginVisible ? 1 : 0,
          transform:loginVisible ? "translateY(0)" : "translateY(40px)",
          transition:"opacity 0.7s ease, transform 0.7s ease" }}>

        {/* Divider */}
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:36,
          width:"100%", maxWidth:420 }}>
          <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.09)" }} />
          <span style={{ fontSize:12, color:"rgba(255,255,255,0.32)", fontWeight:600,
            whiteSpace:"nowrap" }}>Sign in to your account</span>
          <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.09)" }} />
        </div>

        <div style={{ background:"rgba(255,255,255,.04)", backdropFilter:"blur(12px)",
          border:"1px solid rgba(255,255,255,.12)", borderRadius:20, padding:"32px 36px",
          width:"100%", maxWidth:420, boxShadow:"0 32px 80px rgba(0,0,0,.4)" }}>

          <div style={{ fontSize:18, fontWeight:700, color:"#fff", marginBottom:22 }}>Sign in to ReZi</div>

          {sessionExpiredMsg && <div style={{ background:"rgba(245,166,35,.12)", color:"#F5A623",
            padding:"10px 14px", borderRadius:9, fontSize:13, marginBottom:16,
            fontWeight:600, border:"1px solid rgba(245,166,35,.35)", display:"flex",
            alignItems:"center", justifyContent:"space-between", gap:8 }}>
            <span>🔒 {sessionExpiredMsg}</span>
            <span onClick={onClearExpiredMsg} style={{ cursor:"pointer", opacity:0.6, fontSize:16, lineHeight:1 }}>×</span>
          </div>}

          {err && <div style={{ background:"rgba(224,85,85,.15)", color:"#ff8a80",
            padding:"10px 14px", borderRadius:9, fontSize:13, marginBottom:16,
            fontWeight:600, border:"1px solid rgba(224,85,85,.3)" }}>⚠ {err}</div>}

          {/* Email */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600,
              color:"rgba(255,255,255,.5)", textTransform:"uppercase",
              letterSpacing:"0.07em", marginBottom:6 }}>Email Address</label>
            <input ref={emailRef} type="email" value={email} placeholder="you@company.co.za"
              autoComplete="off" name="rezi-login-email"
              readOnly={!emailReady}
              onFocus={() => setEmailReady(true)}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key==="Enter" && submit()}
              style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14,
                background:"rgba(255,255,255,.07)", border:"1.5px solid rgba(255,255,255,.15)",
                color:"#fff", outline:"none", boxSizing:"border-box" }} />
          </div>

          {/* Password */}
          <div style={{ marginBottom:22 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600,
              color:"rgba(255,255,255,.5)", textTransform:"uppercase",
              letterSpacing:"0.07em", marginBottom:6 }}>Password</label>
            <input ref={passRef} type="password" value={pass} placeholder="••••••••"
              autoComplete="off" name="rezi-login-pass"
              readOnly={!passReady}
              onFocus={() => setPassReady(true)}
              onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key==="Enter" && submit()}
              style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14,
                background:"rgba(255,255,255,.07)", border:"1.5px solid rgba(255,255,255,.15)",
                color:"#fff", outline:"none", boxSizing:"border-box" }} />
          </div>

          <button onClick={submit} disabled={loading}
            style={{ width:"100%", padding:"13px 0",
              background:loading?"rgba(33,150,201,.5)":"#2196C9",
              color:"#fff", border:"none", borderRadius:11, fontWeight:700, fontSize:15,
              cursor:loading?"not-allowed":"pointer", transition:"background .2s", letterSpacing:0.2 }}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>

          <div style={{ textAlign:"right", marginTop:8, marginBottom:4 }}>
            <span onClick={onForgot}
              style={{ fontSize:12, color:"rgba(255,255,255,.45)", cursor:"pointer",
                textDecoration:"underline" }}
              onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,.8)"}
              onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,.45)"}>
              Forgot password?
            </span>
          </div>

          <button onClick={onPricing}
            style={{ width:"100%", marginTop:4, padding:"13px 0", background:"transparent",
              color:"#F5A623", border:"2px solid #F5A623", borderRadius:11, fontWeight:700,
              fontSize:15, cursor:"pointer", transition:"all .2s", letterSpacing:0.2 }}
            onMouseEnter={e => { e.currentTarget.style.background="#F5A623"; e.currentTarget.style.color="#1A3A4A"; }}
            onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="#F5A623"; }}>
            Sign Up →
          </button>

          {/* Disclaimer */}
          <div style={{ marginTop:20, padding:"12px 14px", background:"rgba(255,255,255,.04)",
            borderRadius:10, border:"1px solid rgba(255,255,255,.08)", fontSize:11,
            color:"rgba(255,255,255,.35)", lineHeight:1.8 }}>
            <strong style={{ color:"rgba(255,255,255,.5)", display:"block", marginBottom:3 }}>⚠ Disclaimer</strong>
            ReZi is an independent digital form preparation service. Not affiliated with or endorsed by
            the South African government or any registering authority.
          </div>

          {/* Legal footer */}
          <div style={{ textAlign:"center", marginTop:16, fontSize:11, color:"rgba(255,255,255,.25)" }}>
            <span onClick={() => window.__reziSetView?.("terms")}
              style={{ color:"rgba(255,255,255,.4)", cursor:"pointer", textDecoration:"underline" }}>
              Terms of Service
            </span>
            {" · "}
            <span onClick={() => window.__reziSetView?.("privacy")}
              style={{ color:"rgba(255,255,255,.4)", cursor:"pointer", textDecoration:"underline" }}>
              Privacy Policy
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════ RESET PASSWORD ═══════════════ */
function ResetPassword({ onDone }) {
  const [pass,    setPass]    = useState("");
  const [pass2,   setPass2]   = useState("");
  const [status,  setStatus]  = useState("idle");
  const [err,     setErr]     = useState("");

  const pwRules = [
    { re: /.{8,}/,         label: "At least 8 characters" },
    { re: /[A-Z]/,         label: "One uppercase letter" },
    { re: /[a-z]/,         label: "One lowercase letter" },
    { re: /[0-9]/,         label: "One number" },
    { re: /[^A-Za-z0-9]/, label: "One special character (!@#$...)" },
  ];
  const pwStrength = pwRules.filter(r => r.re.test(pass)).length;

  const submit = async () => {
    if (pwStrength < pwRules.length) { setErr("Please meet all password requirements."); return; }
    if (pass !== pass2)              { setErr("Passwords do not match."); return; }
    setStatus("loading"); setErr("");
    const { error } = await supabase.auth.updateUser({ password: pass });
    if (error) { setErr(error.message || "Could not update password."); setStatus("idle"); return; }
    setStatus("done");
  };

  const bgStyle = {
    minHeight:"100vh",
    background:"linear-gradient(160deg, #0d1e28 0%, #1A3A4A 55%, #0d2535 100%)",
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20,
  };

  return (
    <div style={bgStyle}>
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <img src="/Rezi_Logo.jpeg" alt="ReZi"
          style={{ width:100, height:100, borderRadius:22, objectFit:"cover",
            boxShadow:"0 8px 32px rgba(0,0,0,.45)", marginBottom:12 }} />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:2 }}>
          <span style={{ fontSize:38, fontWeight:900, color:"#F5A623", fontFamily:"'Georgia',serif" }}>R</span>
          <span style={{ fontSize:38, fontWeight:900, color:"#fff", fontFamily:"'Georgia',serif" }}>EZI</span>
        </div>
      </div>

      <div style={{ background:"rgba(255,255,255,.04)", backdropFilter:"blur(12px)",
        border:"1px solid rgba(255,255,255,.12)", borderRadius:20, padding:"32px 36px",
        width:"100%", maxWidth:420, boxShadow:"0 32px 80px rgba(0,0,0,.4)" }}>

        {status === "done" ? (
          <>
            <div style={{ background:"rgba(46,204,138,.12)", border:"1px solid rgba(46,204,138,.3)",
              borderRadius:10, padding:"16px 18px", color:"#2ecc8a", fontSize:14,
              lineHeight:1.6, marginBottom:20, textAlign:"center" }}>
              ✓ Password updated successfully!
            </div>
            <button onClick={onDone}
              style={{ width:"100%", padding:"13px 0", background:"#2196C9", color:"#fff",
                border:"none", borderRadius:11, fontWeight:700, fontSize:15, cursor:"pointer" }}>
              Sign In →
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize:18, fontWeight:700, color:"#fff", marginBottom:6 }}>Set new password</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,.45)", marginBottom:20 }}>
              Choose a strong password for your ReZi account.
            </div>

            {err && <div style={{ background:"rgba(224,85,85,.15)", color:"#ff8a80", padding:"10px 14px",
              borderRadius:9, fontSize:13, marginBottom:16, fontWeight:600,
              border:"1px solid rgba(224,85,85,.3)" }}>⚠ {err}</div>}

            <div style={{ marginBottom:8 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,.5)",
                textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>New Password</label>
              <input type="password" value={pass} placeholder="Min. 8 characters"
                autoComplete="new-password"
                onChange={e => setPass(e.target.value)}
                style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14,
                  background:"rgba(255,255,255,.07)", border:"1.5px solid rgba(255,255,255,.15)",
                  color:"#fff", outline:"none", boxSizing:"border-box" }} />
            </div>

            {pass.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ display:"flex", gap:4, marginBottom:8 }}>
                  {[1,2,3,4,5].map(n => (
                    <div key={n} style={{ flex:1, height:4, borderRadius:2,
                      background: pwStrength >= n
                        ? pwStrength <= 2 ? "#e05555" : pwStrength <= 3 ? "#F5A623" : "#2ecc8a"
                        : "rgba(255,255,255,.1)",
                      transition:"background .2s" }} />
                  ))}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                  {pwRules.map(r => (
                    <div key={r.label} style={{ fontSize:11, display:"flex", alignItems:"center", gap:6,
                      color: r.re.test(pass) ? "#2ecc8a" : "rgba(255,255,255,.35)" }}>
                      <span>{r.re.test(pass) ? "✓" : "○"}</span>{r.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom:20 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,.5)",
                textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Confirm Password</label>
              <input type="password" value={pass2} placeholder="Repeat password"
                autoComplete="new-password"
                onChange={e => setPass2(e.target.value)}
                onKeyDown={e => e.key==="Enter" && submit()}
                style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14,
                  background:"rgba(255,255,255,.07)", border:"1.5px solid rgba(255,255,255,.15)",
                  color:"#fff", outline:"none", boxSizing:"border-box" }} />
            </div>

            <button onClick={submit} disabled={status==="loading"}
              style={{ width:"100%", padding:"13px 0",
                background:status==="loading"?"rgba(33,150,201,.5)":"#2196C9",
                color:"#fff", border:"none", borderRadius:11, fontWeight:700, fontSize:15,
                cursor:status==="loading"?"not-allowed":"pointer", transition:"background .2s" }}>
              {status==="loading" ? "Updating…" : "Update Password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════ FORGOT PASSWORD ═══════════════ */
function ForgotPassword({ onBack }) {
  const [email,   setEmail]   = useState("");
  const [status,  setStatus]  = useState("idle"); // idle | loading | sent | error
  const [err,     setErr]     = useState("");

  const submit = async () => {
    if (!email) { setErr("Please enter your email address."); return; }
    setStatus("loading"); setErr("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://rezi.co.za/reset-password",
    });
    if (error) { setErr(error.message || "Could not send reset email."); setStatus("error"); return; }
    setStatus("sent");
  };

  const bgStyle = {
    minHeight:"100vh",
    background:"linear-gradient(160deg, #0d1e28 0%, #1A3A4A 55%, #0d2535 100%)",
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20,
  };

  return (
    <div style={bgStyle}>
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <img src="/Rezi_Logo.jpeg" alt="ReZi"
          style={{ width:100, height:100, borderRadius:22, objectFit:"cover",
            boxShadow:"0 8px 32px rgba(0,0,0,.45)", marginBottom:12 }} />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:2, marginBottom:4 }}>
          <span style={{ fontSize:38, fontWeight:900, color:"#F5A623", fontFamily:"'Georgia',serif" }}>R</span>
          <span style={{ fontSize:38, fontWeight:900, color:"#fff", fontFamily:"'Georgia',serif" }}>EZI</span>
        </div>
      </div>

      <div style={{ background:"rgba(255,255,255,.04)", backdropFilter:"blur(12px)",
        border:"1px solid rgba(255,255,255,.12)", borderRadius:20, padding:"32px 36px",
        width:"100%", maxWidth:420, boxShadow:"0 32px 80px rgba(0,0,0,.4)" }}>

        <div style={{ fontSize:18, fontWeight:700, color:"#fff", marginBottom:8 }}>Reset your password</div>

        {status === "sent" ? (
          <>
            <div style={{ background:"rgba(46,204,138,.12)", border:"1px solid rgba(46,204,138,.3)",
              borderRadius:10, padding:"16px 18px", color:"#2ecc8a", fontSize:14, lineHeight:1.6, marginBottom:20 }}>
              ✓ Check your inbox — we sent a reset link to <strong>{email}</strong>
            </div>
            <button onClick={onBack}
              style={{ width:"100%", padding:"13px 0", background:"#2196C9", color:"#fff",
                border:"none", borderRadius:11, fontWeight:700, fontSize:15, cursor:"pointer" }}>
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize:13, color:"rgba(255,255,255,.45)", marginBottom:20, lineHeight:1.6 }}>
              Enter your email address and we'll send you a link to reset your password.
            </div>

            {err && <div style={{ background:"rgba(224,85,85,.15)", color:"#ff8a80", padding:"10px 14px",
              borderRadius:9, fontSize:13, marginBottom:16, fontWeight:600,
              border:"1px solid rgba(224,85,85,.3)" }}>⚠ {err}</div>}

            <div style={{ marginBottom:20 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,.5)",
                textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Email Address</label>
              <input type="email" value={email} placeholder="you@company.co.za"
                autoComplete="off"
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key==="Enter" && submit()}
                style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14,
                  background:"rgba(255,255,255,.07)", border:"1.5px solid rgba(255,255,255,.15)",
                  color:"#fff", outline:"none", boxSizing:"border-box" }} />
            </div>

            <button onClick={submit} disabled={status==="loading"}
              style={{ width:"100%", padding:"13px 0",
                background:status==="loading"?"rgba(33,150,201,.5)":"#2196C9",
                color:"#fff", border:"none", borderRadius:11, fontWeight:700, fontSize:15,
                cursor:status==="loading"?"not-allowed":"pointer", transition:"background .2s" }}>
              {status==="loading" ? "Sending…" : "Send Reset Link →"}
            </button>

            <div style={{ textAlign:"center", marginTop:16 }}>
              <span onClick={onBack}
                style={{ fontSize:13, color:"rgba(255,255,255,.4)", cursor:"pointer", textDecoration:"underline" }}
                onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,.7)"}
                onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,.4)"}>
                ← Back to Sign In
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Pricing({ onBack, onSignup }) {
  const plans = [
    { name:"Individual",  price:"R99",  users:1,  forms:40,  popular:false, color:T.blue,    bg:T.bluePale,
      features:["1 user","40 forms per month","RLV & NCO PDF generation","Email support"] },
    { name:"Small Dealer",price:"R485", users:5,  forms:200, popular:true,  color:T.gold,    bg:T.goldPale,
      features:["Up to 5 users","200 forms per month","RLV & NCO PDF generation","Priority email support","Extra users: R89/user/month"] },
    { name:"Large Dealer",price:"R985", users:10, forms:500, popular:false, color:T.success, bg:"#E8F5EF",
      features:["Up to 10 users","500 forms per month","RLV & NCO PDF generation","Priority support","Extra users: R89/user/month"] },
  ];
  return (
    <div style={{ minHeight:"100vh", background:T.bg }}>
      <div style={{ background:T.navy, padding:"0 36px", height:64, display:"flex",
        alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <img src="/Rezi_Logo.jpeg" alt="ReZi" style={{ height:48, borderRadius:10, objectFit:"cover" }} />
          <div style={{ display:"flex", alignItems:"center", gap:2 }}>
            <span style={{ fontSize:26, fontWeight:900, color:"#F5A623", fontFamily:"'Georgia',serif" }}>R</span>
            <span style={{ fontSize:26, fontWeight:900, color:"#fff", fontFamily:"'Georgia',serif" }}>EZI</span>
          </div>
        </div>
        <button onClick={onBack} style={{ background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.2)",
          borderRadius:9, padding:"8px 16px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>← Back</button>
      </div>
      <div style={{ maxWidth:1040, margin:"0 auto", padding:"60px 24px" }}>
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div style={{ fontSize:42, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif", lineHeight:1.15, marginBottom:12 }}>
            Simple, transparent pricing
          </div>
          <div style={{ fontSize:15, color:T.muted, maxWidth:520, margin:"0 auto", lineHeight:1.7 }}>
            Every plan starts with a <strong>30-day free trial</strong> — no credit card needed, no payment until you're ready. Full RLV & NCO PDF generation included.
          </div>
        </div>
        {/* Don't pay right away banner */}
        <div style={{ background:"linear-gradient(135deg,#E8F5EF,#D6EEF8)", border:"1.5px solid #2196C944",
          borderRadius:14, padding:"16px 28px", marginBottom:32, display:"flex", alignItems:"center",
          gap:16, flexWrap:"wrap", justifyContent:"center", textAlign:"center" }}>
          <span style={{ fontSize:24 }}>🚀</span>
          <div>
            <div style={{ fontSize:15, fontWeight:900, color:T.navy, marginBottom:3 }}>
              Start free — no payment needed today
            </div>
            <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>
              Every plan comes with a <strong style={{ color:T.navy }}>30-day free trial</strong>. No credit card required.
              Only pay when you're 100% ready.
            </div>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:24, marginBottom:40 }}>
          {plans.map(p => (
            <div key={p.name} style={{ background:"#fff", borderRadius:18, overflow:"hidden",
              border:p.popular?`2.5px solid ${T.gold}`:`1px solid ${T.border}`,
              boxShadow:p.popular?"0 8px 40px rgba(200,150,60,.18)":"0 2px 16px rgba(13,27,53,.07)",
              display:"flex", flexDirection:"column" }}>
              {p.popular&&<div style={{ background:T.gold, color:T.navy, textAlign:"center",
                fontSize:11, fontWeight:900, padding:"7px 0", letterSpacing:"0.1em" }}>MOST POPULAR</div>}
              <div style={{ background:p.bg, padding:"24px 28px 20px", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontSize:13, fontWeight:900, color:p.color, textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:6 }}>{p.name}</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:12 }}>
                  <span style={{ fontSize:48, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif", lineHeight:1 }}>{p.price}</span>
                  <span style={{ fontSize:14, color:T.muted }}>/month</span>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <div style={{ background:"#fff", borderRadius:8, padding:"5px 11px", fontSize:12, fontWeight:700,
                    color:p.color, border:`1px solid ${p.color}33` }}>{p.users} user{p.users>1?"s":""}</div>
                  <div style={{ background:"#fff", borderRadius:8, padding:"5px 11px", fontSize:12, fontWeight:700,
                    color:p.color, border:`1px solid ${p.color}33` }}>{p.forms} forms/mo</div>
                </div>
              </div>
              <div style={{ padding:"20px 28px", flex:1 }}>
                {p.features.map(f=>(
                  <div key={f} style={{ fontSize:13, color:T.muted, padding:"7px 0",
                    borderBottom:`1px solid ${T.border}`, display:"flex", gap:10 }}>
                    <span style={{ color:T.success, fontWeight:900, flexShrink:0 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <div style={{ padding:"0 28px 28px" }}>
                <button onClick={() => onSignup(p.name)}
                  style={{ width:"100%", padding:"13px 0", marginTop:8, borderRadius:10, textAlign:"center",
                    background:p.popular?T.gold:T.navy, color:p.popular?T.navy:"#fff",
                    fontWeight:800, fontSize:14, cursor:"pointer", border:"none",
                    boxShadow:p.popular?"0 4px 16px rgba(245,166,35,.35)":"none" }}>
                  🎉 Get Started with Free Trial
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background:"#fff", border:`1px solid ${T.border}`, borderRadius:14, padding:"24px 32px",
          marginBottom:24, display:"flex", alignItems:"center", gap:24, flexWrap:"wrap", justifyContent:"center" }}>
          <div style={{ flex:1, minWidth:220 }}>
            <div style={{ fontSize:15, fontWeight:900, color:T.navy, marginBottom:4 }}>Need more forms?</div>
            <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>
              Go over your allowance? Additional forms are billed at <strong style={{ color:T.navy }}>R15 per form</strong> automatically.
            </div>
          </div>
          <div style={{ background:T.bg, borderRadius:10, padding:"14px 24px", textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:800, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Overage rate</div>
            <div style={{ fontSize:32, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif" }}>R15</div>
            <div style={{ fontSize:12, color:T.muted }}>per form</div>
          </div>
        </div>
        <div style={{ background:"#fff", border:`1px solid ${T.border}`, borderRadius:14, padding:"24px 32px",
          marginBottom:40, display:"flex", alignItems:"center", gap:24, flexWrap:"wrap", justifyContent:"center" }}>
          <div style={{ flex:1, minWidth:220 }}>
            <div style={{ fontSize:15, fontWeight:900, color:T.navy, marginBottom:4 }}>Need more users?</div>
            <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>
              Add extra seats to Small or Large Dealer plans at <strong style={{ color:T.navy }}>R89 per user per month</strong>.
            </div>
          </div>
          <div style={{ background:T.bg, borderRadius:10, padding:"14px 24px", textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:800, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Per extra user</div>
            <div style={{ fontSize:32, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif" }}>R89</div>
            <div style={{ fontSize:12, color:T.muted }}>per month</div>
          </div>
        </div>
        <div style={{ textAlign:"center", fontSize:12, color:T.muted, lineHeight:2 }}>
          All prices include VAT · ReZi is not affiliated with the South African government or any registering authority.
        </div>
      </div>
    </div>
  );
}


function Signup({ onLogin, onBack, initialPlan }) {
  const PLANS = [
    { name:"Individual",   price:"R99",  users:1,  forms:40,  color:T.blue,    bg:T.bluePale,  popular:false },
    { name:"Small Dealer", price:"R485", users:5,  forms:200, color:T.gold,    bg:T.goldPale,  popular:true  },
    { name:"Large Dealer", price:"R985", users:10, forms:500, color:T.success, bg:"#E8F5EF",   popular:false },
  ];
  const [step,    setStep]    = useState(initialPlan ? 1 : 0);
  const [plan,    setPlan]    = useState(initialPlan || "Small Dealer");
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [pass2,   setPass2]   = useState("");
  const [err,     setErr]     = useState("");
  const [loading, setLoading] = useState(false);

  const pwRules = [
    { re: /.{8,}/,      label: "At least 8 characters" },
    { re: /[A-Z]/,      label: "One uppercase letter" },
    { re: /[a-z]/,      label: "One lowercase letter" },
    { re: /[0-9]/,      label: "One number" },
    { re: /[^A-Za-z0-9]/, label: "One special character (!@#$...)" },
  ];
  const pwStrength = pwRules.filter(r => r.re.test(pass)).length;

  const submit = async () => {
    if (!email)               { setErr("Email address is required."); return; }
    if (!pass)                { setErr("Password is required."); return; }
    if (pwStrength < pwRules.length) { setErr("Please meet all password requirements below."); return; }
    if (pass !== pass2)       { setErr("Passwords do not match."); return; }
    setLoading(true); setErr("");
    const { data, error } = await supabase.auth.signUp({
      email,
      password: pass,
      options: { data: { plan } }, // passed to handle_new_auth_user trigger
    });
    if (error) { setErr(error.message || "Could not create account."); setLoading(false); return; }
    // Fetch the auto-created profile row (created by DB trigger)
    const { data: profile } = await supabase.from("users").select("*").eq("id", data.user.id).single();
    setLoading(false);
    onLogin(profile || { id: data.user.id, email, plan, subscribed: false });
  };

  const bgStyle = { minHeight:"100vh",
    background:`linear-gradient(150deg, ${T.navy} 0%, #0D2535 100%)`,
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20 };

  const logoBlock = (
    <div style={{ textAlign:"center", marginBottom:28 }}>
      <img src="/Rezi_Logo.jpeg" alt="ReZi"
        style={{ width:100, height:100, borderRadius:22, objectFit:"cover",
          boxShadow:"0 8px 32px rgba(0,0,0,.45)", marginBottom:12 }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:2, marginBottom:4 }}>
        <span style={{ fontSize:36, fontWeight:900, color:"#F5A623", fontFamily:"'Georgia',serif" }}>R</span>
        <span style={{ fontSize:36, fontWeight:900, color:"#fff",    fontFamily:"'Georgia',serif" }}>EZI</span>
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", letterSpacing:"0.18em", textTransform:"uppercase", fontWeight:800 }}>
        Create your account
      </div>
    </div>
  );

  /* ── STEP 0: Plan selection ── */
  if (step === 0) return (
    <div style={bgStyle}>
      {logoBlock}
      <div style={{ width:"100%", maxWidth:820 }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:"'Georgia',serif", marginBottom:6 }}>Choose your plan</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,.5)" }}>30-day free trial · No credit card required · Cancel anytime</div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))", gap:20, marginBottom:20 }}>
          {PLANS.map(p => (
            <div key={p.name} style={{ background:"#fff", borderRadius:18, overflow:"hidden", display:"flex", flexDirection:"column",
              border:p.popular?`2.5px solid ${T.gold}`:`1px solid rgba(255,255,255,.15)`,
              boxShadow:p.popular?"0 8px 40px rgba(200,150,60,.25)":"0 2px 16px rgba(0,0,0,.18)" }}>
              {p.popular
                ? <div style={{ background:T.gold, color:T.navy, textAlign:"center", fontSize:10, fontWeight:900, padding:"6px 0", letterSpacing:"0.1em" }}>⭐ MOST POPULAR</div>
                : <div style={{ height:28 }} />}
              <div style={{ background:p.bg, padding:"20px 24px 16px", borderBottom:`1px solid ${p.color}22` }}>
                <div style={{ fontSize:11, fontWeight:900, color:p.color, textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:6 }}>{p.name}</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:4 }}>
                  <span style={{ fontSize:44, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif", lineHeight:1 }}>{p.price}</span>
                  <span style={{ fontSize:13, color:T.muted }}>/month</span>
                </div>
                <div style={{ fontSize:12, color:T.muted, marginTop:4 }}>
                  {p.users} user{p.users>1?"s":""} · {p.forms} forms/mo
                </div>
              </div>
              <div style={{ padding:"16px 24px", flex:1, display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ fontSize:12, color:T.muted, display:"flex", gap:8 }}><span style={{ color:T.success }}>✓</span>RLV & NCO PDF generation</div>
                <div style={{ fontSize:12, color:T.muted, display:"flex", gap:8 }}><span style={{ color:T.success }}>✓</span>{p.users} user seat{p.users>1?"s":""} included</div>
                <div style={{ fontSize:12, color:T.muted, display:"flex", gap:8 }}><span style={{ color:T.success }}>✓</span>{p.forms} forms per month</div>
                {p.users>1&&<div style={{ fontSize:12, color:T.muted, display:"flex", gap:8 }}><span style={{ color:T.success }}>✓</span>Extra users: R89/user/mo</div>}
              </div>
              <div style={{ padding:"0 24px 24px" }}>
                <button onClick={() => { setPlan(p.name); setStep(1); }}
                  style={{ width:"100%", padding:"12px 0", borderRadius:10, border:"none", cursor:"pointer",
                    background:p.popular?T.gold:p.color, color:p.popular?T.navy:"#fff",
                    fontWeight:900, fontSize:14, boxShadow:p.popular?"0 4px 16px rgba(245,166,35,.4)":"none",
                    transition:"opacity .15s" }}
                  onMouseOver={e=>e.currentTarget.style.opacity=".88"}
                  onMouseOut={e=>e.currentTarget.style.opacity="1"}>
                  🎉 Get Started with Free Trial
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center", fontSize:13, color:"rgba(255,255,255,.45)" }}>
          Already have an account?{" "}
          <span onClick={onBack} style={{ color:"#F5A623", fontWeight:800, cursor:"pointer" }}>Sign in</span>
        </div>
      </div>
    </div>
  );

  /* ── STEP 1: Email + password only, 7-day trial ── */
  const selected = PLANS.find(p => p.name === plan);
  return (
    <div style={bgStyle}>
      {logoBlock}
      <div style={{ background:"#fff", borderRadius:20, padding:36, width:"100%", maxWidth:440,
        boxShadow:"0 24px 64px rgba(0,0,0,.35)" }}>

        {/* Trial banner */}
        <div style={{ background:"linear-gradient(135deg,#1A3A4A,#2196C9)", borderRadius:12,
          padding:"14px 18px", marginBottom:22, textAlign:"center" }}>
          <div style={{ fontSize:22, fontWeight:900, color:"#F5A623", fontFamily:"'Georgia',serif", marginBottom:2 }}>
            🎉 30-Day Free Trial
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.8)", lineHeight:1.6 }}>
            Full access · No credit card required · Cancel anytime
          </div>
        </div>

        {/* Plan badge */}
        <div style={{ background:selected.bg, border:"1.5px solid "+selected.color+"44", borderRadius:10,
          padding:"10px 14px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:11, fontWeight:900, color:selected.color, textTransform:"uppercase",
              letterSpacing:"0.08em" }}>{selected.name} plan</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:1 }}>{selected.price}/month after trial</div>
          </div>
          <span onClick={() => setStep(0)}
            style={{ fontSize:11, color:T.blue, fontWeight:800, cursor:"pointer" }}>Change</span>
        </div>

        {err && <div style={{ background:T.dangerBg, color:T.danger, padding:"10px 14px", borderRadius:9,
          fontSize:13, marginBottom:16, fontWeight:700 }}>⚠ {err}</div>}

        <FI label="Email Address" type="email" value={email} placeholder="you@company.co.za"
          autoComplete="off" onChange={e => setEmail(e.target.value)} />
        <FI label="Password" type="password" value={pass} placeholder="Min. 8 characters"
          autoComplete="new-password" onChange={e => setPass(e.target.value)} />
        {pass.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", gap:4, marginBottom:8 }}>
              {[1,2,3,4,5].map(n => (
                <div key={n} style={{ flex:1, height:4, borderRadius:2,
                  background: pwStrength >= n
                    ? pwStrength <= 2 ? "#e05555" : pwStrength <= 3 ? "#F5A623" : "#2ecc8a"
                    : "#e0e8ed",
                  transition:"background .2s" }} />
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {pwRules.map(r => (
                <div key={r.label} style={{ fontSize:11, display:"flex", alignItems:"center", gap:6,
                  color: r.re.test(pass) ? "#1A7F5A" : "#aaa" }}>
                  <span style={{ fontSize:10 }}>{r.re.test(pass) ? "✓" : "○"}</span>
                  {r.label}
                </div>
              ))}
            </div>
          </div>
        )}
        <FI label="Confirm Password" type="password" value={pass2} placeholder="Repeat password"
          autoComplete="new-password" onChange={e => setPass2(e.target.value)} />

        <button onClick={submit} disabled={loading}
          style={{ width:"100%", padding:13, background:loading?"#aaa":T.gold, color:T.navy, border:"none",
            borderRadius:10, fontWeight:900, fontSize:15, cursor:loading?"not-allowed":"pointer", marginTop:8 }}>
          {loading ? "Starting trial…" : "Start Free Trial →"}
        </button>
        <div style={{ fontSize:11, color:T.muted, textAlign:"center", marginTop:10, lineHeight:1.6 }}>
          By signing up you agree to our terms of service.
        </div>
        <div style={{ textAlign:"center", marginTop:12, fontSize:13, color:T.muted }}>
          Already have an account?{" "}
          <span onClick={onBack} style={{ color:T.blue, fontWeight:800, cursor:"pointer" }}>Sign in</span>
        </div>
      </div>
    </div>
  );
}


function TrialExpiredWall({ plan, onSignOut, reason }) {
  const isLimit = reason === "limit";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(13,27,53,.97)", zIndex:9999,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:"#fff", borderRadius:24, padding:"40px 36px", maxWidth:480, width:"100%",
        textAlign:"center", boxShadow:"0 32px 80px rgba(0,0,0,.5)" }}>
        <div style={{ fontSize:48, marginBottom:12 }}>{isLimit ? "🔒" : "⏰"}</div>
        <div style={{ fontSize:26, fontWeight:900, color:T.navy, fontFamily:"'Georgia',serif", marginBottom:8 }}>
          {isLimit ? "Trial form limit reached" : "Your free trial has ended"}
        </div>
        <div style={{ fontSize:14, color:T.muted, lineHeight:1.7, marginBottom:28 }}>
          {isLimit
            ? <>You've used all <strong style={{ color:T.navy }}>7 free forms</strong> included in your trial of <strong style={{ color:T.navy }}>{plan}</strong>. Subscribe to keep generating forms.</>
            : <>Your 30-day free trial of <strong style={{ color:T.navy }}>{plan}</strong> has expired. Subscribe now to continue using ReZi and access all your saved transactions.</>
          }
        </div>
        <div style={{ background:T.bg, borderRadius:14, padding:"16px 20px", marginBottom:24, textAlign:"left" }}>
          <div style={{ fontSize:12, fontWeight:900, color:T.muted, textTransform:"uppercase",
            letterSpacing:"0.08em", marginBottom:10 }}>What you get</div>
          {["Full RLV & NCO PDF generation","All saved transactions","Priority support","Cancel anytime"].map(f => (
            <div key={f} style={{ fontSize:13, color:T.navy, padding:"5px 0",
              borderBottom:"1px solid "+T.border, display:"flex", gap:10 }}>
              <span style={{ color:T.success, fontWeight:900 }}>✓</span>{f}
            </div>
          ))}
        </div>
        <div style={{ background:"linear-gradient(135deg,#1A3A4A,#2196C9)", borderRadius:12,
          padding:"14px 20px", marginBottom:20 }}>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.6)", textTransform:"uppercase",
            letterSpacing:"0.1em", marginBottom:4 }}>Coming soon — subscribe via</div>
          <div style={{ fontSize:18, fontWeight:900, color:"#F5A623" }}>Payfast · R{
            plan==="Individual"?"99":plan==="Small Dealer"?"485":"985"
          }/month</div>
        </div>
        <button onClick={onSignOut}
          style={{ background:"transparent", border:"none", color:T.muted, fontSize:13,
            cursor:"pointer", textDecoration:"underline" }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function parseSAID(id) {
  const d = (id||"").replace(/\D/g,"");
  if (d.length !== 13) return null;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    let n = parseInt(d[i]);
    if (i%2===1) { n*=2; if(n>9) n-=9; }
    sum += n;
  }
  if ((sum*9)%10 !== parseInt(d[12])) return null;
  const yy=d.slice(0,2), mm=d.slice(2,4), dd=d.slice(4,6);
  const year = parseInt(yy) <= new Date().getFullYear()%100 ? "20"+yy : "19"+yy;
  const month=parseInt(mm), day=parseInt(dd);
  if (month<1||month>12||day<1||day>31) return null;
  return { dob:`${year}-${mm}-${dd}`, gender:parseInt(d.slice(6,10))>=5000?"Male":"Female" };
}

/* ═══════════════ DECLARATION BLOCK ═══════════════ */
// eslint-disable-next-line no-unused-vars
function DeclarationBlock({ section,roles,roleField,placeField,dateField,sigField,confirmedField,form,setForm,accentColor }) {
  const ac = accentColor||T.navy;
  const iS2 = { width:"100%", padding:"10px 14px", borderRadius:9, border:"1.5px solid #DDE3F0",
    fontSize:14, background:"#fff", color:T.navy, outline:"none", boxSizing:"border-box" };

  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const lastPt = React.useRef(null);

  const getPos = (e,canvas) => {
    const r = canvas.getBoundingClientRect();
    if (e.touches) return { x:e.touches[0].clientX-r.left, y:e.touches[0].clientY-r.top };
    return { x:e.clientX-r.left, y:e.clientY-r.top };
  };
  // eslint-disable-next-line no-unused-vars
  const startDraw = e => { drawing.current=true; lastPt.current=getPos(e,canvasRef.current); e.preventDefault(); };
  // eslint-disable-next-line no-unused-vars
  const draw = e => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas=canvasRef.current, ctx=canvas.getContext("2d");
    const pt=getPos(e,canvas);
    ctx.beginPath(); ctx.moveTo(lastPt.current.x,lastPt.current.y); ctx.lineTo(pt.x,pt.y);
    ctx.strokeStyle=T.navy; ctx.lineWidth=2; ctx.lineCap="round"; ctx.stroke();
    lastPt.current=pt;
  };
  // eslint-disable-next-line no-unused-vars
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current=false;
    setForm(f => ({ ...f, [sigField]:canvasRef.current.toDataURL("image/png") }));
  };
  // eslint-disable-next-line no-unused-vars
  const clearSig = () => {
    canvasRef.current.getContext("2d").clearRect(0,0,460,90);
    setForm(f => ({ ...f, [sigField]:"" }));
  };

  // eslint-disable-next-line no-unused-vars
  const hasSig = !!(form[sigField]&&form[sigField].length>100);
  const confirmed = !!form[confirmedField];

  return (
    <div style={{ background:"#FAFBFF", border:`1.5px solid ${ac}`, borderRadius:14, overflow:"hidden", marginTop:12 }}>
      <div style={{ background:ac, padding:"10px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ color:"#fff", fontSize:12, fontWeight:900, textTransform:"uppercase", letterSpacing:"0.07em" }}>✍ Declaration</div>
        <div style={{ color:"rgba(255,255,255,.75)", fontSize:11, fontWeight:700 }}>{section}</div>
      </div>
      <div style={{ padding:"16px 18px" }}>
        <div style={{ fontSize:11, color:"#6B7A9F", marginBottom:14, lineHeight:1.6, background:T.bg, borderRadius:8, padding:"8px 12px" }}>
          I, the undersigned, declare that all particulars furnished are <strong>true and correct</strong>.
          A false declaration is punishable by a <strong>fine or imprisonment</strong> in terms of the National Road Traffic Act.
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7A9F", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>
            I am signing as
          </label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
            {roles.map(r => {
              const active = form[roleField]===r;
              return (
                <button key={r} onClick={() => setForm(f => ({ ...f, [roleField]:r }))}
                  style={{ padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
                    border:`1.5px solid ${active?ac:"#DDE3F0"}`,
                    background:active?ac:"#fff", color:active?"#fff":"#6B7A9F", transition:"all .15s" }}>
                  {r}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7A9F", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Place / Plek</label>
            <input value={form[placeField]||""} onChange={e => setForm(f => ({ ...f, [placeField]:e.target.value }))}
              placeholder="City or town" style={iS2} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7A9F", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Date</label>
            <input type="date" value={form[dateField]||""} onChange={e => setForm(f => ({ ...f, [dateField]:e.target.value }))}
              style={{ ...iS2, cursor:"pointer", colorScheme:"light" }} />
          </div>
        </div>
        <div onClick={() => setForm(f => ({ ...f, [confirmedField]:!f[confirmedField] }))}
          style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer",
            background:confirmed?"#E8F5EF":"#fff",
            border:`1.5px solid ${confirmed?"#1A7F5A":"#DDE3F0"}`,
            borderRadius:10, padding:"12px 14px", transition:"all .2s" }}>
          <div style={{ width:20, height:20, borderRadius:5, flexShrink:0, marginTop:1,
            border:`2px solid ${confirmed?"#1A7F5A":"#DDE3F0"}`,
            background:confirmed?"#1A7F5A":"#fff",
            display:"flex", alignItems:"center", justifyContent:"center", transition:"all .2s" }}>
            {confirmed && <span style={{ color:"#fff", fontSize:13, lineHeight:1, fontWeight:900 }}>✓</span>}
          </div>
          <div style={{ fontSize:12, color:confirmed?"#1A7F5A":"#6B7A9F", fontWeight:confirmed?700:400, lineHeight:1.5 }}>
            I confirm that the information provided is true and correct and I accept responsibility for this declaration.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ SECTION CARD ═══════════════ */
function SectionCard({ label, letter, children }) {
  const dark = useDark();
  const light = {
    A:{ bg:"#EEF4FF", border:"#1565C0", text:"#1565C0", badge:"#1565C0" },
    B:{ bg:"#E8F5EF", border:"#1A7F5A", text:"#1A7F5A", badge:"#1A7F5A" },
    C:{ bg:"#FFF8EE", border:"#F5A623", text:"#F5A623", badge:"#F5A623" },
  };
  const dk = {
    A:{ bg:"rgba(21,101,192,0.15)", border:"#2196C9", text:"#7ec8e8", badge:"#1565C0" },
    B:{ bg:"rgba(26,127,90,0.15)",  border:"#2ecc8a", text:"#2ecc8a", badge:"#1A7F5A" },
    C:{ bg:"rgba(245,166,35,0.13)", border:"#F5A623", text:"#f5c86a", badge:"#b37d1a" },
  };
  const c = (dark ? dk : light)[letter] || (dark ? dk : light).A;
  return (
    <div style={{ background:"var(--rz-input-bg,#fff)", border:`1.5px solid ${c.border}55`, borderRadius:16,
      display:"flex", flexDirection:"column", overflow:"hidden",
      boxShadow:"0 2px 16px rgba(0,0,0,.10)", minWidth:0 }}>
      <div style={{ background:c.bg, borderBottom:`1px solid ${c.border}44`, padding:"14px 20px",
        display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:10, background:c.badge, color:"#fff",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontWeight:900, fontSize:16, fontFamily:"'Georgia',serif", flexShrink:0 }}>{letter}</div>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:c.text, textTransform:"uppercase", letterSpacing:"0.1em" }}>Section {letter}</div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--rz-text,#1A3A4A)", marginTop:1 }}>{label}</div>
        </div>
      </div>
      <div style={{ padding:"20px 20px 8px", flex:1, overflowY:"auto" }}>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════ PROXY / REP BLOCK ═══════════════
   Reusable block for Organisation's Proxy + optional Representative.
   prefix  = "sl" | "th" | "ow"
   label   = "seller" | "title holder" | "owner"
═══════════════════════════════════════════════════ */
function ProxyRepBlock({ prefix, form, setForm, profile, fillProxyFromProfile }) {
  const tog = k => () => setForm(f => ({ ...f, [k]:!f[k] }));
  const showProxy = form[`${prefix}_showProxy`];
  const showRep   = form[`${prefix}_showRep`];

  const proxyFields = (pfx, label) => (
    <>
      {pfx === `${prefix}_proxy` && profile?.proxy_surname && (
        <div style={{ background:T.infoBg, border:"1px solid #C0D4FF", borderRadius:10,
          padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div style={{ fontSize:12, color:T.info, fontWeight:700 }}>👤 {profile.proxy_surname} {profile.proxy_initials}</div>
          <Btn variant="primary" onClick={() => fillProxyFromProfile(prefix)}
            style={{ padding:"6px 12px", fontSize:11, borderRadius:7, whiteSpace:"nowrap", background:T.info }}>
            Use Dealer Proxy
          </Btn>
        </div>
      )}
      <FormRowSelect label="ID Type"   fieldKey={`${pfx}_idType`}   form={form} setForm={setForm} options={ID_TYPES} />
      <FormRowInput  label="ID Number"  fieldKey={`${pfx}_idNumber`} form={form} setForm={setForm} />
      <FormRowSelect label="Country"   fieldKey={`${pfx}_country`}  form={form} setForm={setForm} options={COUNTRIES} />
      <FormRowInput  label="Surname"   fieldKey={`${pfx}_surname`}  form={form} setForm={setForm} />
      <FormRowInput  label="Initials"  fieldKey={`${pfx}_initials`} form={form} setForm={setForm} />
    </>
  );

  return (
    <>
      <Toggle label="Add Organisation's Proxy"
        sub="Person acting on behalf of the organisation"
        checked={showProxy} onChange={tog(`${prefix}_showProxy`)} />
      {showProxy && (
        <div style={{ background:"var(--rz-input-bg,#F8F9FF)", border:"1px solid var(--rz-input-border,#DDE3F0)", borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
          <SecHead>Organisation's Proxy</SecHead>
          {proxyFields(`${prefix}_proxy`)}
          <Toggle label="Add Organisation's Representative (if different from proxy)"
            sub="Only if the representative differs from the proxy above"
            checked={showRep} onChange={tog(`${prefix}_showRep`)} />
          {showRep && (
            <div style={{ background:"var(--rz-input-bg,#FFF8EE)", border:"1px solid var(--rz-input-border,#E8B86D)", borderRadius:10, padding:"14px 16px", marginTop:8 }}>
              <SecHead>Organisation's Representative</SecHead>
              {proxyFields(`${prefix}_rep`)}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ═══════════════ STANDALONE PDF DOWNLOAD (used by History row buttons) ═══════════════ */
async function downloadFromFormData(form, which) {
  const isStock = form.vc_financeType === "stock";
  const mergedForm = {
    ...form,
    sl_telDay:  (form.sl_telDay_code||"")+(form.sl_telDay_num||""),
    th_cell:    (form.th_cell_code||"")+(form.th_cell_num||""),
    th_telHome: (form.th_telHome_code||"")+(form.th_telHome_num||""),
    th_telDay:  (form.th_telDay_code||"")+(form.th_telDay_num||""),
    th_fax:     (form.th_fax_code||"")+(form.th_fax_num||""),
  };
  const formToSend = mergedForm.th_streetSameAsPostal ? {
    ...mergedForm,
    th_streetAddr:mergedForm.th_postalAddr, th_streetAddr2:mergedForm.th_postalAddr2, th_streetAddr3:mergedForm.th_postalAddr3,
    th_streetSuburb:mergedForm.th_postalSuburb, th_streetCity:mergedForm.th_postalCity, th_streetCode:mergedForm.th_postalCode,
  } : mergedForm;

  const formToSendOwner = isStock ? {
    ...formToSend, ow_same:false,
    ow_idType:formToSend.th_idType, ow_idNumber:formToSend.th_idNumber,
    ow_idCountry:formToSend.th_idCountry, ow_gender:formToSend.th_gender,
    ow_surname:formToSend.th_surname, ow_initials:formToSend.th_initials,
    ow_firstNames:formToSend.th_firstNames, ow_email:formToSend.th_email,
    ow_telDay_code:formToSend.th_telDay_code, ow_telDay_num:formToSend.th_telDay_num,
    ow_postalAddr:formToSend.th_postalAddr, ow_postalAddr2:formToSend.th_postalAddr2, ow_postalAddr3:formToSend.th_postalAddr3,
    ow_postalSuburb:formToSend.th_postalSuburb, ow_postalCity:formToSend.th_postalCity, ow_postalCode:formToSend.th_postalCode,
    ow_streetAddr:formToSend.th_streetAddr||formToSend.th_postalAddr,
    ow_streetAddr2:formToSend.th_streetAddr2||formToSend.th_postalAddr2,
    ow_streetAddr3:formToSend.th_streetAddr3||formToSend.th_postalAddr3,
    ow_streetSuburb:formToSend.th_streetSuburb||formToSend.th_postalSuburb,
    ow_streetCity:formToSend.th_streetCity||formToSend.th_postalCity,
    ow_streetCode:formToSend.th_streetCode||formToSend.th_postalCode,
    ow_noticeAddr:formToSend.th_noticeAddr||"postal",
    ow_showProxy:formToSend.th_showProxy,
    ow_proxy_idNumber:formToSend.th_proxy_idNumber, ow_proxy_country:formToSend.th_proxy_country,
    ow_proxy_surname:formToSend.th_proxy_surname, ow_proxy_initials:formToSend.th_proxy_initials,
  } : formToSend.ow_same ? {
    ...formToSend,
    ow_idType:"", ow_idNumber:"", ow_idCountry:"", ow_gender:"",
    ow_surname:"", ow_initials:"", ow_firstNames:"", ow_dob:"",
    ow_email:"", ow_cell:"", ow_cell_code:"", ow_cell_num:"",
    ow_telHome:"", ow_telHome_code:"", ow_telHome_num:"",
    ow_telDay:"", ow_telDay_code:"", ow_telDay_num:"",
    ow_postalAddr:"", ow_postalAddr2:"", ow_postalAddr3:"",
    ow_postalSuburb:"", ow_postalCity:"", ow_postalCode:"",
    ow_streetAddr:"", ow_streetAddr2:"", ow_streetAddr3:"",
    ow_streetSuburb:"", ow_streetCity:"", ow_streetCode:"",
    ow_noticeAddr:"postal",
  } : formToSend;

  const _rzSess = JSON.parse(sessionStorage.getItem("rezi-session") || "{}");
  try {
    const res = await fetch("/api/fill-pdf", { method:"POST",
      headers:{ "Content-Type":"application/json",
        "X-User-Id": _rzSess.userId || "",
        "X-Session-Token": _rzSess.token || "" },
      body: JSON.stringify({ form:formToSendOwner, which }) });
    if (res.status === 401) { window.__reziForceLogout?.(); return; }
    if (!res.ok) throw new Error();
    const { rlv, nco } = await res.json();
    const dlPDF = (b64, name) => {
      const blob = new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type:"application/pdf" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    };
    const ts = Date.now();
    if (which !== "nco" && rlv) dlPDF(rlv, `RLV_${(formToSendOwner.th_surname||"form").replace(/\s+/g,"_")}_${ts}.pdf`);
    if (which !== "rlv" && nco) setTimeout(() => dlPDF(nco, `NCO_${(formToSendOwner.sl_surname||"form").replace(/\s+/g,"_")}_${ts}.pdf`), 600);
  } catch {
    alert("PDF download failed. Please open the transaction and download from the form.");
  }
}

/* ═══════════════ TRANSACTION FORM ═══════════════ */
function TransactionForm({ user, profile, initial, onSave, onCancel, quickSlots, onFormDownloaded, trialFormsLeft, onAutoSave, editingIndex }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => initial||LS.get("rezi_draft",null)||{...BLANK_FORM});
  const [errors, setErrors] = useState({});
  const [showVal, setShowVal] = useState(false);
  const [dlStatus, setDlStatus] = useState("idle");
  const [savedPulse, setSavedPulse] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!initial) {
      LS.set("rezi_draft", form);
    }
    if (onAutoSave) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        onAutoSave(form, editingIndex ?? null);
        setSavedPulse(true);
        setTimeout(() => setSavedPulse(false), 2000);
      }, 1200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const set = k => e => setForm(f => ({ ...f, [k]:e.target.value }));
  const tog = k => () => setForm(f => ({ ...f, [k]:!f[k] }));
  const isStock = form.vc_financeType==="stock";
  const isDemo  = isStock && form.vc_stockType==="demo";   // demo transfer — RLV only, Sections A + C
  // eslint-disable-next-line no-unused-vars
  const isTradeIn = isStock && form.vc_stockType!=="demo"; // client sells to dealer — NCO + RLV

  const handleIDChange = (idField, idTypeField, dobField, genderField) => e => {
    const val = e.target.value;
    setForm(f => {
      const updates = { [idField]:val };
      if (f[idTypeField]==="RSA ID") {
        const parsed = parseSAID(val);
        if (parsed) {
          if (dobField)    updates[dobField]    = parsed.dob;
          if (genderField) updates[genderField] = parsed.gender;
        }
      }
      return { ...f, ...updates };
    });
  };

  const fillSellerFromProfile = () => {
    if (!profile) return;
    setForm(f => ({ ...f,
      sl_idType: profile.regNumber?"Business Reg. No.":"RSA ID",
      sl_idNumber: profile.regNumber||"",
      sl_surname: profile.companyName||"",
      sl_email: profile.email||"",
      sl_telDay_code: profile.phone?(profile.phone.slice(0,3)):"",
      sl_telDay_num: profile.phone?(profile.phone.slice(3)):"",
    }));
  };

  // Quick fill — map slot fields onto section prefix
  const fillSection = (prefix, slotData) => {
    const m = {
      [`${prefix}_idType`]:       slotData.idType||"",
      [`${prefix}_idNumber`]:     slotData.idNumber||"",
      [`${prefix}_idCountry`]:    slotData.idCountry||"",
      [`${prefix}_gender`]:       slotData.gender||"",
      [`${prefix}_surname`]:      slotData.surname||"",
      [`${prefix}_initials`]:     slotData.initials||"",
      [`${prefix}_firstNames`]:   slotData.firstNames||"",
      [`${prefix}_dob`]:          slotData.dob||"",
      [`${prefix}_email`]:        slotData.email||"",
      [`${prefix}_cell`]:         slotData.cell||"",
      [`${prefix}_telDay`]:       slotData.telDay||"",
      [`${prefix}_telDay_code`]:  slotData.telDay_code||"",
      [`${prefix}_telDay_num`]:   slotData.telDay_num||"",
      [`${prefix}_telHome`]:      slotData.telHome||"",
      [`${prefix}_telHome_code`]: slotData.telHome_code||"",
      [`${prefix}_telHome_num`]:  slotData.telHome_num||"",
      [`${prefix}_postalAddr`]:   slotData.postalAddr||"",
      [`${prefix}_postalAddr2`]:  slotData.postalAddr2||"",
      [`${prefix}_postalAddr3`]:  slotData.postalAddr3||"",
      [`${prefix}_postalSuburb`]: slotData.postalSuburb||"",
      [`${prefix}_postalCity`]:   slotData.postalCity||"",
      [`${prefix}_postalCode`]:   slotData.postalCode||"",
      [`${prefix}_streetAddr`]:   slotData.streetAddr||"",
      [`${prefix}_streetAddr2`]:  slotData.streetAddr2||"",
      [`${prefix}_streetAddr3`]:  slotData.streetAddr3||"",
      [`${prefix}_streetSuburb`]: slotData.streetSuburb||"",
      [`${prefix}_streetCity`]:   slotData.streetCity||"",
      [`${prefix}_streetCode`]:    slotData.streetCode||"",
      [`${prefix}_proxy_idType`]:  slotData.proxy_idType||"",
      [`${prefix}_proxy_idNumber`]:slotData.proxy_idNumber||"",
      [`${prefix}_proxy_country`]: slotData.proxy_country||"",
      [`${prefix}_proxy_surname`]: slotData.proxy_surname||"",
      [`${prefix}_proxy_initials`]:slotData.proxy_initials||"",
    };
    setForm(f => ({ ...f, ...m }));
  };

  const fillProxyFromProfile = prefix => {
    if (!profile?.proxy_surname) return;
    setForm(f => ({ ...f,
      [`${prefix}_proxy_idType`]:   profile.proxy_idType||"",
      [`${prefix}_proxy_idNumber`]: profile.proxy_idNumber||"",
      [`${prefix}_proxy_country`]:  profile.proxy_country||"",
      [`${prefix}_proxy_surname`]:  profile.proxy_surname||"",
      [`${prefix}_proxy_initials`]: profile.proxy_initials||"",
    }));
  };

  const downloadPDFs = async which => {
    setDlStatus("loading");
    const mergedForm = {
      ...form,
      sl_telDay: (form.sl_telDay_code||"")+(form.sl_telDay_num||""),
      th_cell: (form.th_cell_code||"")+(form.th_cell_num||""),
      th_telHome: (form.th_telHome_code||"")+(form.th_telHome_num||""),
      th_telDay: (form.th_telDay_code||"")+(form.th_telDay_num||""),
      th_fax: (form.th_fax_code||"")+(form.th_fax_num||""),
    };
    const formToSend = mergedForm.th_streetSameAsPostal ? {
      ...mergedForm,
      th_streetAddr:form.th_postalAddr, th_streetAddr2:form.th_postalAddr2, th_streetAddr3:form.th_postalAddr3,
      th_streetSuburb:form.th_postalSuburb, th_streetCity:form.th_postalCity, th_streetCode:form.th_postalCode,
    } : mergedForm;

    const formToSendOwner = isStock ? {
      ...formToSend, ow_same:false,
      ow_idType:formToSend.th_idType, ow_idNumber:formToSend.th_idNumber,
      ow_idCountry:formToSend.th_idCountry, ow_gender:formToSend.th_gender,
      ow_surname:formToSend.th_surname, ow_initials:formToSend.th_initials,
      ow_firstNames:formToSend.th_firstNames, ow_email:formToSend.th_email,
      ow_telDay_code:formToSend.th_telDay_code, ow_telDay_num:formToSend.th_telDay_num,
      ow_postalAddr:formToSend.th_postalAddr, ow_postalAddr2:formToSend.th_postalAddr2, ow_postalAddr3:formToSend.th_postalAddr3,
      ow_postalSuburb:formToSend.th_postalSuburb, ow_postalCity:formToSend.th_postalCity, ow_postalCode:formToSend.th_postalCode,
      ow_streetAddr:formToSend.th_streetAddr||formToSend.th_postalAddr,
      ow_streetAddr2:formToSend.th_streetAddr2||formToSend.th_postalAddr2,
      ow_streetAddr3:formToSend.th_streetAddr3||formToSend.th_postalAddr3,
      ow_streetSuburb:formToSend.th_streetSuburb||formToSend.th_postalSuburb,
      ow_streetCity:formToSend.th_streetCity||formToSend.th_postalCity,
      ow_streetCode:formToSend.th_streetCode||formToSend.th_postalCode,
      ow_noticeAddr:formToSend.th_noticeAddr||"postal",
      ow_showProxy:formToSend.th_showProxy,
      ow_proxy_idNumber:formToSend.th_proxy_idNumber, ow_proxy_country:formToSend.th_proxy_country,
      ow_proxy_surname:formToSend.th_proxy_surname, ow_proxy_initials:formToSend.th_proxy_initials,
    } : formToSend.ow_same ? {
      // Cash sale, owner same as title holder — send blank owner fields (Part B left empty on PDF)
      ...formToSend,
      ow_idType:"", ow_idNumber:"", ow_idCountry:"", ow_gender:"",
      ow_surname:"", ow_initials:"", ow_firstNames:"", ow_dob:"",
      ow_email:"", ow_cell:"", ow_cell_code:"", ow_cell_num:"",
      ow_telHome:"", ow_telHome_code:"", ow_telHome_num:"",
      ow_telDay:"", ow_telDay_code:"", ow_telDay_num:"",
      ow_postalAddr:"", ow_postalAddr2:"", ow_postalAddr3:"",
      ow_postalSuburb:"", ow_postalCity:"", ow_postalCode:"",
      ow_streetAddr:"", ow_streetAddr2:"", ow_streetAddr3:"",
      ow_streetSuburb:"", ow_streetCity:"", ow_streetCode:"",
      ow_noticeAddr:"postal",
    } : formToSend;

    const _rzSess = JSON.parse(sessionStorage.getItem("rezi-session") || "{}");
    try {
      const res = await fetch("/api/fill-pdf", { method:"POST",
        headers:{ "Content-Type":"application/json",
          "X-User-Id": _rzSess.userId || "",
          "X-Session-Token": _rzSess.token || "" },
        body:JSON.stringify({ form:formToSendOwner, which }) });
      if (res.status === 401) { window.__reziForceLogout?.(); return; }
      if (!res.ok) throw new Error();
      const { rlv, nco } = await res.json();
      const dlPDF=(b64,name)=>{
        const blob=new Blob([Uint8Array.from(atob(b64),c=>c.charCodeAt(0))],{type:"application/pdf"});
        const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=name; a.click();
      };
      const ts=Date.now();
      if (which!=="nco"&&rlv) dlPDF(rlv,`RLV_${(formToSendOwner.th_surname||"form").replace(/\s+/g,"_")}_${ts}.pdf`);
      if (which!=="rlv"&&nco) setTimeout(()=>dlPDF(nco,`NCO_${(formToSendOwner.sl_surname||"form").replace(/\s+/g,"_")}_${ts}.pdf`),600);
      setDlStatus("done");
      if (onFormDownloaded) onFormDownloaded();
    } catch {
      const dlHTML=(html,name)=>{ const a=document.createElement("a"); a.href="data:text/html;charset=utf-8,"+encodeURIComponent(html); a.download=name; a.click(); };
      if (which!=="nco") dlHTML(generateRLV(formToSendOwner),`RLV_${(formToSendOwner.th_surname||"form").replace(/\s+/g,"_")}_${Date.now()}.html`);
      if (which!=="rlv") setTimeout(()=>dlHTML(generateNCO(formToSendOwner),`NCO_${(formToSendOwner.sl_surname||"form").replace(/\s+/g,"_")}_${Date.now()}.html`),400);
      setDlStatus("done");
    }
  };

  const handleSave = status => { LS.del("rezi_draft"); onSave(form, status); };
  const errs = validateForm(form);
  const hasErrors = Object.keys(errs).length > 0;

  /* ─── STEP 0 — SELLER ─── */
  const stepSeller = (
    <>
      <QuickFillBar slots={quickSlots} onFill={d=>fillSection("sl",d)} />
      {/* ── Dealer Stock sub-type selector ── */}
      {isStock && (
        <div style={{ background:"var(--rz-input-bg,#fff)",
          border:"1px solid rgba(156,111,220,.35)", borderRadius:12,
          overflow:"hidden", marginBottom:20 }}>
          <div style={{ background:"#7c4dcc", padding:"9px 16px" }}>
            <div style={{ color:"#fff", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em" }}>
              🏢 Dealer Stock — What type of transaction is this?
            </div>
          </div>
          <div style={{ padding:"16px 18px", display:"flex", gap:12, flexWrap:"wrap" }}>
            {[
              { value:"tradein", label:"🔄 Trade-In / Purchase from Client",
                sub:"Client sells vehicle to dealer. Requires NCO (yellow) + RLV (blue) — Sections A, B & C.",
                color:"#6A1B9A", bg:"#F3E5F5" },
              { value:"demo",   label:"🚘 Demo / Fleet Transfer to Dealer",
                sub:"Bank or fleet transfers demo vehicle into dealer's name. RLV (blue) only — Sections A & C. No NCO needed.",
                color:"#1565C0", bg:"#E3F0FF" },
            ].map(opt => {
              const active = form.vc_stockType === opt.value;
              return (
                <div key={opt.value}
                  onClick={() => setForm(f => ({ ...f, vc_stockType:opt.value }))}
                  style={{ flex:1, minWidth:200, padding:"14px 16px", borderRadius:12, cursor:"pointer",
                    border:`2px solid ${active?opt.color:"#DDE3F0"}`,
                    background:active?opt.bg:"#FAFBFF", transition:"all .18s",
                    boxShadow:active?"0 2px 10px rgba(0,0,0,.08)":"none" }}>
                  <div style={{ fontSize:13, fontWeight:800, color:active?opt.color:"#0D1B35", marginBottom:4 }}>{opt.label}</div>
                  <div style={{ fontSize:11, color:"#6B7A9F", lineHeight:1.5 }}>{opt.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Demo transfer — skip seller, just show a note */}
      {isDemo && (
        <HintBubble form="rlv">
          <strong>Demo / Fleet Transfer — RLV only.</strong> The bank or fleet company is the current title holder (Section A). There is no change-of-ownership NCO required — only the RLV (blue form) is needed to transfer the vehicle into the dealer's name. Fill in Section A (current title holder / bank) and Section C (vehicle details) then download the RLV only.
        </HintBubble>
      )}

      {/* Seller section only shown for trade-ins */}
      {!isDemo && <>
      <HintBubble form="nco">
        <strong>This is NCO Part A — Seller Details.</strong> The NCO (yellow form) records the change of ownership. Part A captures the <strong>seller's details</strong> — this is typically the motor dealer or private seller transferring the vehicle.
      </HintBubble>

      {profile?.companyName && (
        <div style={{ background:"var(--rz-input-bg,#EBF6FD)", border:"1px solid rgba(33,150,201,.25)", borderRadius:12,
          padding:"14px 18px", marginBottom:22, display:"flex", alignItems:"center", justifyContent:"space-between", gap:16 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:T.info }}>🏢 Profile Saved</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>
              {profile.companyName}{profile.regNumber?" · "+profile.regNumber:""}
            </div>
          </div>
          <Btn variant="primary" onClick={fillSellerFromProfile}
            style={{ padding:"9px 20px", fontSize:13, borderRadius:8, whiteSpace:"nowrap", background:T.info }}>
            Use Dealer as Seller
          </Btn>
        </div>
      )}

      <SecHead>Identification</SecHead>
      <FormRowSelect label="ID Type" fieldKey="sl_idType" form={form} setForm={setForm} options={ID_TYPES} />
      <FormRow label="ID Number" required error={showVal&&errors.sl_idNumber?errors.sl_idNumber:undefined}>
        <input value={form.sl_idNumber||""} onChange={set("sl_idNumber")}
          style={{ ...iS, ...(showVal&&errors.sl_idNumber?{borderColor:"#C0392B"}:{}) }} />
      </FormRow>
      <FormRowSelect label="Country" fieldKey="sl_idCountry" form={form} setForm={setForm}
        options={COUNTRIES} hint="If Foreign ID" />

      <SecHead>Name & Contact</SecHead>
      <FormRowInput label="Surname / Org" required fieldKey="sl_surname" form={form} setForm={setForm}
        error={showVal&&errors.sl_surname?errors.sl_surname:undefined} />
      <FormRowInput label="Initials"    fieldKey="sl_initials"   form={form} setForm={setForm} />
      <FormRowInput label="First Names" fieldKey="sl_firstNames" form={form} setForm={setForm} />
      <FormRowInput label="Email"       fieldKey="sl_email"      form={form} setForm={setForm} type="email" />

      <SecHead>Telephone</SecHead>
      <PhoneRow label="Tel (Day)" codeKey="sl_telDay_code" numKey="sl_telDay_num" form={form} setForm={setForm} />

      <ProxyRepBlock prefix="sl" form={form} setForm={setForm} profile={profile} fillProxyFromProfile={fillProxyFromProfile} />

      <DeclarationBlock
        section={isStock?"NCO Part A — Client (Seller)":"NCO Part A — Seller"}
        roles={["Owner","Motor Dealer","Organisation's Proxy","Organisation's Representative"]}
        roleField="nco_decl_a_role" placeField="nco_decl_a_place" dateField="nco_decl_a_date"
        sigField="nco_decl_a_sig" confirmedField="nco_decl_a_confirmed"
        form={form} setForm={setForm} accentColor={T.gold} />
    </>}  {/* end !isDemo seller block */}
    </>
  );

  /* ─── SECTION A: Title Holder ─── */
  const sectionA = (
    <SectionCard letter="A" label={isStock?"Particulars of Seller / Owner":"Particulars of Title Holder"}>

      {/* ── Possible Transactions ── */}
      {!isStock && (
      <div style={{ background:"var(--rz-input-bg,#fff)",
        border:"1px solid var(--rz-input-border,#D0E4EF)", borderRadius:12,
        overflow:"hidden", marginBottom:20 }}>
        <div style={{ background:"#2196C9", padding:"9px 16px" }}>
          <div style={{ color:"#fff", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em" }}>
            List of Possible Transactions
          </div>
        </div>
        <div style={{ padding:"16px 18px", display:"flex", flexDirection:"column", gap:12 }}>
          {[
            { key:"registration", label:"Registration of motor vehicle by title holder" },
            { key:"licensing",    label:"Licensing of motor vehicle by owner" },
          ].map(opt => {
            const active = !!(form.rlv_transaction && form.rlv_transaction[opt.key]);
            return (
              <div key={opt.key} onClick={() => setForm(f => ({
                  ...f,
                  rlv_transaction: { ...(f.rlv_transaction||{}), [opt.key]: !f.rlv_transaction?.[opt.key] }
                }))}
                style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer",
                  background: active ? "#EEF4FF" : "#FAFBFF",
                  border: `1.5px solid ${active ? "#1565C0" : "#DDE3F0"}`,
                  borderRadius:10, padding:"12px 14px", transition:"all .15s" }}>
                <div style={{ width:20, height:20, borderRadius:4, flexShrink:0, marginTop:1,
                  border:`2px solid ${active ? "#1565C0" : "#DDE3F0"}`,
                  background: active ? "#1565C0" : "#fff",
                  display:"flex", alignItems:"center", justifyContent:"center", transition:"all .15s" }}>
                  {active && <span style={{ color:"#fff", fontSize:13, lineHeight:1, fontWeight:900 }}>✓</span>}
                </div>
                <div style={{ fontSize:13, color: active ? "#1565C0" : "#6B7A9F",
                  fontWeight: active ? 700 : 400, lineHeight:1.5 }}>
                  {opt.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <QuickFillBar slots={quickSlots} onFill={d=>fillSection("th",d)} />
      {isStock
        ? isDemo
          ? <HintBubble form="rlv"><strong>RLV Part A — Current Title Holder (Bank / Fleet).</strong> For a demo transfer, the <strong>bank or fleet company is the current title holder</strong> and must be filled in here. The RLV will transfer the vehicle from the bank into the dealer's name.</HintBubble>
          : <HintBubble form="rlv"><strong>RLV Part A — Dealer (Title Holder).</strong> The RLV (blue form) registers the vehicle. For a trade-in, the <strong>dealership becomes the new title holder</strong> — fill in the dealer's details here.</HintBubble>
        : form.vc_financeType==="cash"
          ? <HintBubble form="rlv"><strong>RLV Part A — Title Holder.</strong> The RLV (blue form) registers the vehicle. For a Cash Sale, the <strong>buyer is the title holder</strong> — the person who will legally own and register the vehicle in their name.</HintBubble>
          : <HintBubble form="rlv"><strong>RLV Part A — Title Holder (Bank / Financier).</strong> The RLV (blue form) registers the vehicle. For a Finance deal, the <strong>bank or finance house holds the title</strong> until the loan is paid off — fill in the financier's details here.</HintBubble>
      }

      {isStock && profile?.companyName && (
        <div style={{ background:T.infoBg, border:"1px solid #C0D4FF", borderRadius:10,
          padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div style={{ fontSize:12, color:T.info, fontWeight:700 }}>🏢 {profile.companyName}</div>
          <Btn variant="primary" onClick={() => setForm(f => ({ ...f,
            th_idType: profile.regNumber?"Business Reg. No.":"RSA ID",
            th_idNumber: profile.regNumber||"",
            th_surname: profile.companyName||"",
            th_email: profile.email||"",
            th_telDay_code: profile.phone?(profile.phone.slice(0,3)):"",
            th_telDay_num: profile.phone?(profile.phone.slice(3)):"",
            th_postalAddr:   profile.postalAddr  || profile.address || "",
            th_postalAddr2:  profile.postalAddr2  || "",
            th_postalAddr3:  profile.postalAddr3  || "",
            th_postalSuburb: profile.postalSuburb || profile.addressSuburb || "",
            th_postalCity:   profile.postalCity   || profile.addressCity   || "",
            th_postalCode:   profile.postalCode   || profile.addressCode   || "",
            th_streetAddr:   profile.address  || "",
            th_streetAddr2:  profile.address2 || "",
            th_streetAddr3:  profile.address3 || "",
            th_streetSuburb: profile.addressSuburb || "",
            th_streetCity:   profile.addressCity   || "",
            th_streetCode:   profile.addressCode   || "",
            th_gender: "one-man business",
          }))} style={{ padding:"6px 12px", fontSize:11, borderRadius:7, whiteSpace:"nowrap", background:T.info }}>
            Use Dealer
          </Btn>
        </div>
      )}

      <SecHead>Identification</SecHead>
      <FormRowSelect label="ID Type" required fieldKey="th_idType" form={form} setForm={setForm} options={ID_TYPES} />
      <FormRow label="ID Number" required error={showVal&&errors.th_idNumber?errors.th_idNumber:undefined}>
        <input value={form.th_idNumber||""}
          onChange={handleIDChange("th_idNumber","th_idType","th_dob","th_gender")}
          maxLength={form.th_idType==="RSA ID"?13:undefined}
          placeholder={form.th_idType==="RSA ID"?"13-digit ID number":"ID / Registration number"}
          style={{ ...iS, ...(showVal&&errors.th_idNumber?{borderColor:"#C0392B"}:{}) }} />
        {form.th_idType==="RSA ID"&&parseSAID(form.th_idNumber)&&(
          <div style={{ marginTop:4, fontSize:11, color:T.success, fontWeight:700 }}>✓ Valid ID · DOB & gender auto-filled</div>
        )}
        {form.th_idType==="RSA ID"&&form.th_idNumber&&form.th_idNumber.length===13&&!parseSAID(form.th_idNumber)&&(
          <div style={{ marginTop:4, fontSize:11, color:T.danger, fontWeight:700 }}>⚠ Checksum invalid</div>
        )}
      </FormRow>
      <FormRowSelect label="Country" fieldKey="th_idCountry" form={form} setForm={setForm}
        options={COUNTRIES} hint="If Foreign ID" />

      <SecHead>Name & Personal Details</SecHead>
      <FormRowInput label="Surname / Org" required fieldKey="th_surname" form={form} setForm={setForm}
        error={showVal&&errors.th_surname?errors.th_surname:undefined} />
      <FormRowInput label="Initials"    fieldKey="th_initials"   form={form} setForm={setForm} />
      <FormRowInput label="First Names" fieldKey="th_firstNames" form={form} setForm={setForm} />
      <DateGrid label="Date of Birth" value={form.th_dob}
        onChange={e => setForm(f => ({ ...f, th_dob:e.target.value }))} />
      <FormRowSelect label="Gender / Nature" fieldKey="th_gender" form={form} setForm={setForm} options={GENDER_OPTS} />
      <FormRowInput label="Email" fieldKey="th_email" form={form} setForm={setForm} type="email" />

      <SecHead>Telephone & Fax</SecHead>
      <PhoneRow label="Cell"       codeKey="th_cell_code"    numKey="th_cell_num"    form={form} setForm={setForm} />
      <PhoneRow label="Tel (Home)" codeKey="th_telHome_code" numKey="th_telHome_num" form={form} setForm={setForm} />
      <PhoneRow label="Tel (Day)"  codeKey="th_telDay_code"  numKey="th_telDay_num"  form={form} setForm={setForm} />
      <PhoneRow label="Fax"        codeKey="th_fax_code"     numKey="th_fax_num"     form={form} setForm={setForm} />

      <SecHead>Postal Address</SecHead>
      <AddressField label="Postal address" prefix="th_postal" form={form} setForm={setForm} />

      <Toggle label="Street address same as postal"
        checked={form.th_streetSameAsPostal} onChange={tog("th_streetSameAsPostal")} />
      {!form.th_streetSameAsPostal && <>
        <SecHead>Street Address</SecHead>
        <AddressField label="Street address" prefix="th_street" form={form} setForm={setForm} />
      </>}

      <SecHead>Notices</SecHead>
      <NoticeAddrPicker fieldKey="th_noticeAddr" form={form} setForm={setForm} />

      <ProxyRepBlock prefix="th" form={form} setForm={setForm} profile={profile} fillProxyFromProfile={fillProxyFromProfile} />

      <DeclarationBlock
        section={isStock
          ? "RLV Part A — Dealer (Title Holder)"
          : form.vc_financeType==="finance"
            ? "RLV Part A — Title Holder (Bank) · NCO Part B — Buyer"
            : "RLV Part A — Title Holder · NCO Part B — Buyer"}
        roles={isStock
          ? ["Owner","Motor Dealer","Organisation's Proxy","Organisation's Representative"]
          : ["Title Holder","Motor Dealer","Organisation's Proxy","Organisation's Representative"]}
        roleField="decl_b_role" placeField="decl_b_place" dateField="decl_b_date"
        sigField="decl_b_sig" confirmedField="decl_b_confirmed"
        form={form} setForm={setForm} accentColor={T.navy} />
    </SectionCard>
  );

  /* ─── SECTION B: Owner / Buyer — hidden for demo transfers ─── */
  const sectionB = isDemo ? null : (
    <SectionCard letter="B" label={
      isStock ? "Particulars of Buyer (NCO Part B)"
      : form.vc_financeType==="cash" ? "Particulars of Owner (RLV Part B)"
      : "Particulars of Owner / Buyer (RLV Part B + NCO Part B)"
    }>
      {isStock ? (
        <>
          <HintBubble form="nco">
            <strong>NCO (yellow form) — Part B: Buyer.</strong> This section records the <strong>client buying the vehicle from the dealer</strong>. Their details go into Part B of the NCO (yellow form). They will become the new registered owner once the forms are processed.
          </HintBubble>
          <HintBubble form="rlv">
            <strong>RLV (blue form) — Part B: Owner (if different from Part A).</strong> On the RLV the dealer is Part A (Title Holder). If the buyer will also be the registered owner on the RLV, their details go here too. For Dealer Stock the dealer typically remains title holder so this may not apply — check with the registering authority.
          </HintBubble>
        </>
      ) : form.vc_financeType==="cash" ? (
        <>
          <HintBubble form="rlv">
            <strong>RLV (blue form) — Part B: Owner (if different from Part A).</strong> For a Cash Sale the buyer is both Title Holder and Owner. If the same person, select "Yes" below and Section B will be left blank on the PDF — the registering authority reads Section A for the owner details.
          </HintBubble>
          <HintBubble form="nco">
            <strong>NCO (yellow form) — Part B: Buyer.</strong> For a Cash Sale, if the buyer is the same person as the Title Holder, Section B on the NCO will be left blank.
          </HintBubble>
        </>
      ) : (
        <>
          <HintBubble form="rlv">
            <strong>RLV (blue form) — Part B: Owner (if different from Part A).</strong> For a Finance deal the <strong>bank is the Title Holder (Part A)</strong> but the buyer is the registered owner who drives the vehicle. Their details go here as the Owner on the RLV.
          </HintBubble>
          <HintBubble form="nco">
            <strong>NCO (yellow form) — Part B: Buyer.</strong> The NCO records the buyer's details here. This is the person taking physical ownership and responsibility for the vehicle, even though the bank holds the title until the loan is settled.
          </HintBubble>
        </>
      )}
      <QuickFillBar slots={quickSlots} onFill={d=>fillSection("ow",d)} />
      {form.vc_financeType==="cash" ? (
        <div style={{ background:"var(--rz-input-bg,#E8F5EF)", border:"1px solid rgba(46,204,138,.3)", borderRadius:12, padding:"18px 20px", marginBottom:12 }}>
          <div style={{ fontSize:14, fontWeight:800, color:T.success, marginBottom:10 }}>
            💵 Cash Sale — Is the Owner the same as the Title Holder?
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <div onClick={() => setForm(f=>({...f, ow_same:true}))}
              style={{ flex:1, padding:"10px 0", borderRadius:8, textAlign:"center", cursor:"pointer", fontWeight:700, fontSize:13,
                background:form.ow_same?"#34A853":"var(--rz-input-bg,#fff)", color:form.ow_same?"#fff":"var(--rz-text,#5A7A8A)",
                border:`2px solid ${form.ow_same?"#34A853":"var(--rz-input-border,#DDE3F0)"}` }}>
              ✓ Yes — same person
            </div>
            <div onClick={() => setForm(f=>({...f, ow_same:false}))}
              style={{ flex:1, padding:"10px 0", borderRadius:8, textAlign:"center", cursor:"pointer", fontWeight:700, fontSize:13,
                background:!form.ow_same?"#E53935":"var(--rz-input-bg,#fff)", color:!form.ow_same?"#fff":"var(--rz-text,#5A7A8A)",
                border:`2px solid ${!form.ow_same?"#E53935":"var(--rz-input-border,#DDE3F0)"}` }}>
              ✗ No — different person
            </div>
          </div>
        </div>
      ) : (
        <Toggle label="Owner is same as Title Holder"
          sub="Section B will be left blank on the PDF"
          checked={form.ow_same} onChange={tog("ow_same")} />
      )}

      {form.ow_same
        ? <div style={{ background:"var(--rz-input-bg,#E8F5EF)", border:"1px solid rgba(46,204,138,.25)", borderRadius:10,
            padding:"14px 16px", marginTop:8, fontSize:13, color:T.success, fontWeight:700, textAlign:"center" }}>
            ✓ Section B will be left blank on the PDF
          </div>
        : <>
          <SecHead>Identification</SecHead>
          <FormRowSelect label="ID Type" fieldKey="ow_idType" form={form} setForm={setForm} options={ID_TYPES} />
          <FormRow label="ID Number">
            <input value={form.ow_idNumber||""}
              onChange={handleIDChange("ow_idNumber","ow_idType","ow_dob","ow_gender")}
              maxLength={form.ow_idType==="RSA ID"?13:undefined}
              placeholder={form.ow_idType==="RSA ID"?"13-digit ID number":"ID / Registration number"}
              style={iS} />
            {form.ow_idType==="RSA ID"&&parseSAID(form.ow_idNumber)&&(
              <div style={{ marginTop:4, fontSize:11, color:T.success, fontWeight:700 }}>✓ Valid ID · DOB & gender auto-filled</div>
            )}
          </FormRow>
          <FormRowSelect label="Country" fieldKey="ow_idCountry" form={form} setForm={setForm}
            options={COUNTRIES} hint="If Foreign ID" />

          <SecHead>Name & Personal Details</SecHead>
          <FormRowInput label="Surname / Org" fieldKey="ow_surname"    form={form} setForm={setForm} />
          <FormRowInput label="Initials"      fieldKey="ow_initials"   form={form} setForm={setForm} />
          <FormRowInput label="First Names"   fieldKey="ow_firstNames" form={form} setForm={setForm} />
          <DateGrid label="Date of Birth" value={form.ow_dob}
            onChange={e => setForm(f => ({ ...f, ow_dob:e.target.value }))} />
          <FormRowSelect label="Gender / Nature" fieldKey="ow_gender" form={form} setForm={setForm} options={GENDER_OPTS} />
          <FormRowInput label="Email" fieldKey="ow_email" form={form} setForm={setForm} type="email" />

          <SecHead>Telephone</SecHead>
          <PhoneRow label="Cell"       codeKey="ow_cell_code"    numKey="ow_cell_num"    form={form} setForm={setForm} />
          <PhoneRow label="Tel (Home)" codeKey="ow_telHome_code" numKey="ow_telHome_num" form={form} setForm={setForm} />
          <PhoneRow label="Tel (Day)"  codeKey="ow_telDay_code"  numKey="ow_telDay_num"  form={form} setForm={setForm} />
          <PhoneRow label="Fax"        codeKey="ow_fax_code"     numKey="ow_fax_num"     form={form} setForm={setForm} />

          <SecHead>Postal Address</SecHead>
          <AddressField label="Postal address" prefix="ow_postal" form={form} setForm={setForm} />

          <SecHead>Street Address</SecHead>
          <AddressField label="Street address" prefix="ow_street" form={form} setForm={setForm} />

          <SecHead>Notices</SecHead>
          <NoticeAddrPicker fieldKey="ow_noticeAddr" form={form} setForm={setForm} />

          <ProxyRepBlock prefix="ow" form={form} setForm={setForm} profile={profile} fillProxyFromProfile={fillProxyFromProfile} />
        </>
      }

      <DeclarationBlock
        section="RLV Part B — Owner"
        roles={["Owner","Organisation's Proxy","Organisation's Representative"]}
        roleField="decl_c_role" placeField="decl_c_place" dateField="decl_c_date"
        sigField="decl_c_sig" confirmedField="decl_c_confirmed"
        form={form} setForm={setForm} accentColor="#6A1B9A" />
    </SectionCard>
  );

  /* ─── SECTION C: Vehicle ─── */
  const sectionC = (
    <SectionCard letter="C" label="Identification of Motor Vehicle">
      <HintBubble form="both"><strong>RLV Part C + NCO Part C — Vehicle Details.</strong> Both the RLV (blue) and NCO (yellow) forms require the vehicle's details. Fill this section carefully — the <strong>VIN/Chassis number</strong> is the most critical field and must match the registration papers exactly.</HintBubble>

      <SecHead>Licence & Registration</SecHead>
      <FormRowInput label="Licence No."    fieldKey="vc_licenceNo" form={form} setForm={setForm} />
      <FormRowInput label="Vehicle Reg No." fieldKey="vc_vrn"      form={form} setForm={setForm} />
      <FormRowInput label="Chassis / VIN"  required fieldKey="vc_chassis" form={form} setForm={setForm}
        error={showVal&&errors.vc_chassis?errors.vc_chassis:undefined} />

      <SecHead>Make, Series & Model</SecHead>
      <FormRowInput  label="Make"   required fieldKey="vc_make"      form={form} setForm={setForm}
        error={showVal&&errors.vc_make?errors.vc_make:undefined} />
      <FormRowInput  label="Series"          fieldKey="vc_series"    form={form} setForm={setForm} />
      <FormRowInput  label="NaTIS Model No." fieldKey="vc_natisModel" form={form} setForm={setForm} />
      <FormRowSelect label="Category" required fieldKey="vc_category" form={form} setForm={setForm} options={CATEGORIES} />

      <SecHead>Engine, Power & Fuel</SecHead>
      <FormRowInput  label="Engine No."     fieldKey="vc_engineNo"     form={form} setForm={setForm} />
      <FormRowInput  label="Net Power (kW)" fieldKey="vc_kw"           form={form} setForm={setForm} />
      <FormRowInput  label="Capacity (cm³)" fieldKey="vc_cc"           form={form} setForm={setForm} />
      <FormRowSelect label="Fuel Type"      fieldKey="vc_fuelType"     form={form} setForm={setForm}
        options={["Petrol","Diesel","Electric","Hybrid","Drawn","Other"]} />
      <FormRowSelect label="Transmission"   fieldKey="vc_transmission" form={form} setForm={setForm}
        options={["None","Manual","Semi-automatic","Automatic"]} />
      <FormRowInput  label="Tare (kg)"      fieldKey="vc_tare"         form={form} setForm={setForm} />
      <FormRowInput  label="GVM (kg)"       fieldKey="vc_gvm"          form={form} setForm={setForm} />

      <SecHead>Description & Usage</SecHead>
      <FormRowSelect label="Main Colour" fieldKey="vc_colour" form={form} setForm={setForm}
        options={["White","Red","Blue","Silver","Black","Grey","Green","Brown","Yellow","Orange","Other"]} />
      {form.vc_colour==="Other"&&<FormRowInput label="Specify Colour" fieldKey="vc_colourOther" form={form} setForm={setForm} />}
      <FormRowSelect label="Description" fieldKey="vc_description" form={form} setForm={setForm}
        options={["Sedan (closed top)","Hatch back","Pick-up / Bakkie","Chassis-cab","Chassis","Other"]} />
      {form.vc_description==="Other"&&<FormRowInput label="Specify Desc." fieldKey="vc_descOther" form={form} setForm={setForm} />}
      <FormRowSelect label="Driven" fieldKey="vc_driven" form={form} setForm={setForm}
        options={["Self-propelled","Trailer (drawn)","Semi-trailer","Trailer drawn by tractor"]} />
      <FormRowSelect label="Used For" fieldKey="vc_usedFor" form={form} setForm={setForm}
        options={["Passengers","Persons for reward (taxi, ambulance, etc.)","Dangerous goods","Other"]} />
      {form.vc_usedFor==="Other"&&<FormRowInput label="Specify Use" fieldKey="vc_usedForOther" form={form} setForm={setForm} />}
      <FormRowSelect label="Econ. Sector" fieldKey="vc_sector" form={form} setForm={setForm}
        options={["Private","Agriculture","Manufacturing","Services","Wholesale / Retail","Other"]} />
      {form.vc_sector==="Other"&&<FormRowInput label="Specify Sector" fieldKey="vc_sectorOther" form={form} setForm={setForm} />}

      <HintBubble form="rlv"><strong>RLV Registration Details.</strong> These fields appear on the RLV (blue form) and tell the licensing authority <strong>why</strong> the vehicle is being registered and <strong>where</strong> it will be kept. "Ownership" is the standard reason for a change-of-owner registration.</HintBubble>
      <SecHead>Registration Details</SecHead>
      <FormRowInput  label="Odometer (km)" fieldKey="vc_odometer"  form={form} setForm={setForm} />
      <DateGrid label="Date Acquired" value={form.vc_dateAcquired}
        onChange={e => setForm(f => ({ ...f, vc_dateAcquired:e.target.value }))} />
      <FormRowSelect label="Ownership"   fieldKey="vc_ownership"  form={form} setForm={setForm}
        options={["Private","Business","MD Stock","MIB Stock"]} />
      <FormRowSelect label="Public Road?" fieldKey="vc_publicRoad" form={form} setForm={setForm}
        options={["Yes","No"]} />
      <FormRowSelect label="Reg. Reason" fieldKey="vc_regReason"  form={form} setForm={setForm}
        options={["First Registration","Ownership","Re-Registration","Repossessed","Amalgamation","Built-Up","Recovered","Estate"]} />

      <SecHead>Address Where Vehicle is Kept</SecHead>
      <AddressField label="Keep address" prefix="vc_keep" form={form} setForm={setForm} />

      {!isDemo && <>
        <HintBubble form="nco"><strong>NCO Change of Ownership details.</strong> This bottom portion of Section C on the NCO (yellow form) records <strong>why</strong> and <strong>when</strong> ownership is changing. "Sold" is the most common reason for dealer transactions.</HintBubble>
        <SecHead>NCO — Change of Ownership</SecHead>
        <FormRowSelect label="Change Reason" fieldKey="nco_changeReason" form={form} setForm={setForm}
          options={["Sold","Repossessed","Deceased Estate"]} />
        <DateGrid label="Date of Change" value={form.nco_dateOfChange}
          onChange={e => setForm(f => ({ ...f, nco_dateOfChange:e.target.value }))} />
      </>}
    </SectionCard>
  );

  /* ─── STEP 2 — DOWNLOAD ─── */
  const trialBanner = trialFormsLeft !== null ? (
    <div style={{ background: trialFormsLeft <= 2 ? T.dangerBg : T.successBg,
      border: `1px solid ${trialFormsLeft <= 2 ? T.danger : T.success}44`,
      borderRadius:10, padding:"10px 16px", marginBottom:16,
      display:"flex", alignItems:"center", justifyContent:"space-between" }}>
      <span style={{ fontSize:13, fontWeight:700, color: trialFormsLeft <= 2 ? T.danger : T.success }}>
        {trialFormsLeft <= 2 ? "⚠" : "✓"} Trial: {trialFormsLeft} form{trialFormsLeft !== 1 ? "s" : ""} remaining
      </span>
      <span style={{ fontSize:11, color:T.muted }}>30-day free trial</span>
    </div>
  ) : null;

  const stepDownload = (
    <>
      {trialBanner}
      {showVal&&hasErrors&&(
        <div style={{ background:T.dangerBg, border:`1px solid ${T.danger}33`, borderLeft:`4px solid ${T.danger}`,
          borderRadius:10, padding:"14px 18px", marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:800, color:T.danger, marginBottom:8 }}>⚠ Please fix these before downloading:</div>
          {Object.values(errs).map((e,i) => <div key={i} style={{ fontSize:12, color:T.danger, marginBottom:4 }}>• {e}</div>)}
        </div>
      )}
      <div style={{ background:"var(--rz-input-bg,#F8F9FF)", border:"1px solid var(--rz-input-border,#DDE3F0)", borderRadius:14, padding:"20px 24px", marginBottom:20 }}>
        <div style={{ fontSize:16, fontWeight:700, color:"var(--rz-text,#1A3A4A)", marginBottom:16 }}>Review Summary</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          {[
            { label:"Seller",      val:`${form.sl_surname||"—"}${form.sl_initials?" "+form.sl_initials:""}` },
            { label:"Title Holder",val:`${form.th_surname||"—"}${form.th_initials?" "+form.th_initials:""}` },
            { label:"Make",        val:form.vc_make||"—" },
            { label:"Chassis",     val:form.vc_chassis||"—" },
            { label:"Category",    val:form.vc_category||"—" },
            { label:"Transaction", val:form.vc_financeType==="cash"?"Cash Sale":form.vc_financeType==="finance"?"Finance":form.vc_stockType==="demo"?"Dealer Stock — Demo Transfer":"Dealer Stock — Trade-In" },
          ].map(r => (
            <div key={r.label} style={{ background:"var(--rz-input-bg,#fff)", borderRadius:10, padding:"10px 14px", border:"1px solid var(--rz-input-border,#E8ECF5)" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--rz-text,#5A7A8A)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:3, opacity:0.65 }}>{r.label}</div>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--rz-text,#1A3A4A)" }}>{r.val}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {(isDemo
          ? [{ which:"rlv", label:"⬇ Download RLV", sub:"Demo transfer — RLV (blue form) only", variant:"gold", big:true }]
          : [
              { which:"both", label:"⬇ Download RLV + NCO", sub:"Both forms in one click", variant:"gold", big:true },
              { which:"rlv",  label:"⬇ Download RLV only",  sub:"Registration & Licensing of Motor Vehicles", variant:"primary" },
              { which:"nco",  label:"⬇ Download NCO only",  sub:"Notification of Change of Ownership", variant:"primary" },
            ]
        ).map(btn => (
          <div key={btn.which}>
            <Btn variant={btn.variant} onClick={() => downloadPDFs(btn.which)} disabled={dlStatus==="loading"}
              style={{ width:"100%", padding:btn.big?"16px":"12px", fontSize:btn.big?16:14,
                display:"flex", flexDirection:"column", alignItems:"center", gap:3, borderRadius:12 }}>
              <span>{btn.label}</span>
              <span style={{ fontSize:11, opacity:.8, fontWeight:500 }}>{btn.sub}</span>
            </Btn>
          </div>
        ))}
      </div>
      {dlStatus==="loading"&&<div style={{ textAlign:"center", padding:"20px 0", color:T.muted, fontSize:13 }}>⏳ Generating PDFs…</div>}
      {dlStatus==="done"&&(
        <div style={{ background:"var(--rz-input-bg,#E8F5EF)", border:"1px solid rgba(46,204,138,.25)", borderRadius:10,
          padding:"14px", marginTop:16, textAlign:"center", fontSize:13, fontWeight:700, color:T.success }}>
          ✓ Download triggered — check your downloads folder
        </div>
      )}
      <div style={{ marginTop:24, display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
        <Btn variant="success" onClick={() => handleSave("complete")} style={{ padding:"12px 28px" }}>✓ Mark Complete & Save</Btn>
      </div>
    </>
  );

  const STEPS_NEW = isDemo ? [
    { label:"Seller / Bank",  icon:"1", sub:"RLV Part A — Current Title Holder" },
    { label:"Title Holder",   icon:"2", sub:"RLV Part A — Dealer" },
    { label:"Vehicle",        icon:"3", sub:"RLV Part C" },
    { label:"Download",       icon:"⬇", sub:"RLV only" },
  ] : [
    { label:"Title Holder", icon:"1", sub:"RLV Part A" },
    { label:"Owner",        icon:"2", sub:"RLV Part B" },
    { label:"Seller",       icon:"3", sub:"NCO Part A" },
    { label:"Vehicle",      icon:"4", sub:"RLV Part C" },
    { label:"Download",     icon:"⬇", sub:"Review & download" },
  ];

  const stepComplete = isDemo ? [
    !!(form.th_idNumber && form.th_surname),  // step 0 for demo = title holder (bank)
    !!(form.th_idNumber && form.th_surname),
    !!(form.vc_chassis && form.vc_make && form.vc_category),
    false,
  ] : [
    !!(form.th_idNumber && form.th_surname),
    !!(form.ow_same || (form.ow_idNumber && form.ow_surname)),
    !!(form.sl_idNumber && form.sl_surname),
    !!(form.vc_chassis && form.vc_make && form.vc_category),
    false,
  ];

  const goNext = () => {
    const errs = validateForm(form);
    setErrors(errs);
    if (step===STEPS_NEW.length-2) setShowVal(true);
    setStep(s => Math.min(s+1,STEPS_NEW.length-1));
    window.scrollTo({ top:0, behavior:"smooth" });
  };
  const goPrev = () => { setStep(s => Math.max(s-1,0)); window.scrollTo({ top:0, behavior:"smooth" }); };

  const nextLabel = () => {
    if (isDemo) {
      if (step===0) return "Title Holder →";
      if (step===1) return "Vehicle →";
      if (step===2) return "Review & Download →";
    } else {
      if (step===0) return "Owner →";
      if (step===1) return "Seller →";
      if (step===2) return "Vehicle →";
      if (step===3) return "Review & Download →";
    }
    return "Next →";
  };

  const STEP_TITLES = isDemo
    ? ["Transaction Type","Title Holder (Bank)","Vehicle","Download"]
    : ["Seller Details","Title Holder","Owner","Vehicle"];
  const STEP_SUBS = isDemo
    ? [
        "Select Demo or Trade-In",
        "Current title holder — bank or fleet (RLV Part A)",
        "Vehicle identification & specs (RLV Part C)",
        "Download RLV only",
      ]
    : [
        "Who is selling the vehicle? (NCO Part A)",
        "Who holds the title? Bank, buyer, or dealer (RLV Part A)",
        "Who owns the vehicle? (RLV Part B)",
        "Vehicle identification & specs (RLV Part C)",
      ];

  return (
    <div style={{ maxWidth:820, margin:"0 auto", padding:"0 24px 60px" }}>

      {/* Step indicator */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", gap:0, padding:"20px 0 18px", flexWrap:"wrap" }}>
        {STEPS_NEW.map((s,i) => (
          <React.Fragment key={i}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", cursor:i<step?"pointer":"default" }}
              onClick={() => i<step&&setStep(i)}>
              <div style={{ width:36, height:36, borderRadius:"50%", display:"flex", alignItems:"center",
                justifyContent:"center", fontWeight:900, fontSize:13,
                background:i===step?"#2196C9":i<step?"#2ecc8a":"var(--rz-input-border,#DDE3F0)",
                color:i===step||i<step?"#fff":"var(--rz-text,#5A7A8A)",
                border:`2px solid ${i===step?"#2196C9":i<step?"#2ecc8a":"var(--rz-input-border,#DDE3F0)"}`,
                transition:"all .2s" }}>
                {i<step?"✓":s.icon}
              </div>
              <div style={{ fontSize:10, fontWeight:i===step?700:500,
                color:i===step?"var(--rz-text,#1A3A4A)":i<step?"#2ecc8a":"var(--rz-text,#5A7A8A)",
                marginTop:4, textAlign:"center", maxWidth:72, opacity:i===step?1:i<step?0.85:0.5 }}>{s.label}</div>
            </div>
            {i<STEPS_NEW.length-1&&(
              <div style={{ width:24, height:2, background:i<step?"#2ecc8a":"var(--rz-input-border,#DDE3F0)",
                margin:"0 2px", marginBottom:20, flexShrink:0 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Section heading */}
      {step < 4 && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, padding:"0 4px" }}>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:"var(--rz-text,#1A3A4A)" }}>
              {STEP_TITLES[step]}
            </div>
            <div style={{ fontSize:12, color:"var(--rz-text,#5A7A8A)", marginTop:2, opacity:0.65 }}>{STEP_SUBS[step]}</div>
          </div>
          {stepComplete[step] && (
            <div style={{ background:"var(--rz-input-bg,#E8F5EF)", border:"1px solid rgba(46,204,138,.25)",
              borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:700, color:T.success, whiteSpace:"nowrap" }}>
              ✓ Complete
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign:"right", fontSize:11, color:savedPulse?"#2ecc8a":"var(--rz-text,#5A7A8A)",
        fontWeight:savedPulse?700:400, marginBottom:12, transition:"color .3s", paddingRight:4, opacity:0.7 }}>
        {savedPulse?"✓ Draft saved":"Auto-saving…"}
      </div>

      {/* Step content */}
      <div style={{ background:"var(--rz-input-bg,#fff)", borderRadius:16, padding:"28px 32px",
        boxShadow:"0 2px 20px rgba(0,0,0,.08)", border:"1px solid var(--rz-input-border,#DDE3F0)" }}>
        {/* Demo flow: step 0 = sub-type selector + Section A (bank), step 1 = Section A (dealer/title holder already done), step 2 = Section C, step 3 = download */}
        {/* Normal flow: step 0 = Seller, step 1 = Section A, step 2 = Section B, step 3 = Section C, step 4 = download */}
        {isDemo ? (
          <>
            {step===0 && stepSeller}
            {step===1 && <div style={{ maxWidth:"100%" }}>{sectionA}</div>}
            {step===2 && sectionC}
            {step===3 && stepDownload}
          </>
        ) : (
          <>
            {step===0 && <div style={{ maxWidth:"100%" }}>{sectionA}</div>}
            {step===1 && <div style={{ maxWidth:"100%" }}>{sectionB}</div>}
            {step===2 && stepSeller}
            {step===3 && sectionC}
            {step===4 && stepDownload}
          </>
        )}
      </div>

      {/* Navigation */}
      <div style={{
        display:"flex", justifyContent:"space-between", alignItems:"center",
        marginTop:24, padding:"16px 0 0",
        borderTop:"1px solid var(--rz-input-border,#DDE3F0)",
        flexWrap:"wrap", gap:12,
      }}>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onCancel}
            style={{ padding:"9px 18px", borderRadius:9, fontSize:13, fontWeight:500,
              background:"transparent", border:"1px solid var(--rz-input-border,#DDE3F0)",
              color:"var(--rz-text,#5A7A8A)", cursor:"pointer" }}>
            ← Dashboard
          </button>
          {step>0&&(
            <button onClick={goPrev}
              style={{ padding:"9px 18px", borderRadius:9, fontSize:13, fontWeight:500,
                background:"transparent", border:"1px solid var(--rz-input-border,#DDE3F0)",
                color:"var(--rz-text,#5A7A8A)", cursor:"pointer" }}>
              ← Back
            </button>
          )}
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {showVal&&hasErrors&&(
            <span style={{ fontSize:12, color:"#e05555", fontWeight:600 }}>
              ⚠ {Object.keys(errs).length} field{Object.keys(errs).length>1?"s":""} need attention
            </span>
          )}
          {step<STEPS_NEW.length-1&&(
            <button onClick={goNext}
              style={{ padding:"10px 28px", borderRadius:9, fontSize:14, fontWeight:600,
                background:"#F5A623", color:"#1A3A4A", border:"none", cursor:"pointer",
                boxShadow:"0 4px 14px rgba(245,166,35,.35)", letterSpacing:0.2 }}>
              {nextLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ PROFILE EDITOR ═══════════════ */

/* ═══════════════ QUICK FILL BAR ═══════════════ */
// Dropdown shown at top of each form section — pick a slot to auto-fill
function QuickFillBar({ slots, onFill, label }) {
  const [sel, setSel] = React.useState("");
  if (!slots || slots.length === 0) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16,
      background:"var(--rz-input-bg,#EEF4FF)", border:"1px solid var(--rz-input-border,#C0D4FF)",
      borderRadius:10, padding:"10px 14px" }}>
      <span style={{ fontSize:12, fontWeight:800, color:T.blue, whiteSpace:"nowrap" }}>⚡ Quick Fill</span>
      <select value={sel} onChange={e => setSel(e.target.value)}
        style={{ flex:1, padding:"6px 10px", borderRadius:7, border:"1px solid "+T.border,
          fontSize:13, color:T.navy, background:"#fff", cursor:"pointer" }}>
        <option value="">— Select saved contact —</option>
        {slots.map(s => (
          <option key={s.id} value={s.id}>{s.data.slot_name || "Unnamed"}</option>
        ))}
      </select>
      <button onClick={() => {
          const slot = slots.find(s => s.id === sel);
          if (slot) { onFill(slot.data); setSel(""); }
        }}
        disabled={!sel}
        style={{ padding:"6px 14px", borderRadius:7, border:"none", fontWeight:800, fontSize:12,
          background:sel?T.blue:"#ccc", color:"#fff", cursor:sel?"pointer":"default", whiteSpace:"nowrap" }}>
        Fill →
      </button>
    </div>
  );
}

/* ═══════════════ QUICK SLOTS EDITOR ═══════════════ */
function QuickSlotsEditor({ userId }) {
  const dark = useDark();
  const [slots,   setSlots]   = React.useState([]);
  const [active,  setActive]  = React.useState(null); // index of slot modal open
  const [saving,  setSaving]  = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [err,     setErr]     = React.useState("");

  React.useEffect(() => {
    if (!userId) return;
    supabase.from("quick_slots").select("*").eq("user_id", userId).order("slot_order", { ascending: true })
      .then(({ data }) => { setSlots((data||[]).map(r => ({ id: r.id, data: r.slot_data }))); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  const saveSlot = async (idx) => {
    const slot = slots[idx];
    setSaving(true); setErr("");
    try {
      if (slot.id) {
        const { error } = await supabase.from("quick_slots").update({ slot_data: slot.data, slot_order: idx }).eq("id", slot.id);
        if (error) { setErr("Save failed"); return; }
      } else {
        const { data, error } = await supabase.from("quick_slots").insert({ user_id: userId, slot_data: slot.data, slot_order: idx }).select().single();
        if (error) { setErr("Save failed"); return; }
        const updated = [...slots];
        updated[idx] = { ...updated[idx], id: data.id };
        setSlots(updated);
      }
      setActive(null);
    } catch(e) { setErr("Could not save slot."); }
    finally { setSaving(false); }
  };

  const deleteSlot = async (idx) => {
    const slot = slots[idx];
    if (!window.confirm("Delete \"" + (slot.data.slot_name||"this slot") + "\"?")) return;
    if (slot.id) {
      await supabase.from("quick_slots").delete().eq("id", slot.id);
    }
    setSlots(slots.filter((_,i) => i !== idx));
    if (active === idx) setActive(null);
  };

  const addSlot = () => {
    if (slots.length >= 15) return;
    setSlots([...slots, { id:null, data:{...BLANK_SLOT} }]);
    setActive(slots.length);
  };

  const upd = (idx, key, val) => {
    const updated = slots.map((s,i) => i===idx ? {...s, data:{...s.data, [key]:val}} : s);
    setSlots(updated);
  };

  if (loading) return <div style={{ color:"var(--rz-text,#6B7A9F)", fontSize:13, padding:"12px 0", opacity:0.6 }}>Loading slots…</div>;

  const slotCard = (slot, idx) => {
    const hasName = !!(slot.data.slot_name);
    const hasPerson = !!(slot.data.surname);
    const initials = slot.data.initials || (slot.data.firstNames ? slot.data.firstNames[0] : "");
    const avatarLetter = (slot.data.slot_name||slot.data.surname||"?")[0].toUpperCase();
    const cardBg   = dark ? "rgba(255,255,255,.04)" : "#fff";
    const cardBdr  = dark ? "rgba(255,255,255,.10)" : "#D0E4EF";
    return (
      <div key={idx}
        style={{ border:`1px solid ${cardBdr}`, borderRadius:14, overflow:"hidden",
          background:cardBg, display:"flex", flexDirection:"column",
          boxShadow: dark ? "none" : "0 1px 4px rgba(0,0,0,.05)",
          transition:"box-shadow .15s",
        }}>
        {/* Card top — avatar + name */}
        <div style={{ padding:"18px 18px 14px", flex:1 }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:10 }}>
            <div style={{ width:40, height:40, borderRadius:12,
              background:`linear-gradient(135deg,#2196C9,#1A3A4A)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:18, fontWeight:800, color:"#fff", flexShrink:0, letterSpacing:-1 }}>
              {avatarLetter}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:800,
                color: dark?"rgba(255,255,255,.9)":"#1A3A4A",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {hasName ? slot.data.slot_name : <span style={{ opacity:0.4, fontStyle:"italic" }}>Unnamed slot</span>}
              </div>
              {hasPerson && (
                <div style={{ fontSize:12, marginTop:2,
                  color: dark?"rgba(255,255,255,.4)":"#8aa5b5",
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {slot.data.surname}{initials ? " "+initials : ""}
                </div>
              )}
            </div>
          </div>
          {/* quick-preview chips */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {slot.data.idType && (
              <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20,
                background: dark?"rgba(33,150,201,.15)":"#EBF6FD", color:"#2196C9", border:"1px solid rgba(33,150,201,.25)" }}>
                {slot.data.idType}
              </span>
            )}
            {slot.data.email && (
              <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20,
                background: dark?"rgba(255,255,255,.08)":"#F0F6FA", color: dark?"rgba(255,255,255,.5)":"#5A7A8A",
                border: dark?"1px solid rgba(255,255,255,.1)":"1px solid #D0E4EF",
                maxWidth:130, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", display:"inline-block" }}>
                ✉ {slot.data.email}
              </span>
            )}
            {slot.data.postalCity && (
              <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20,
                background: dark?"rgba(255,255,255,.08)":"#F0F6FA", color: dark?"rgba(255,255,255,.5)":"#5A7A8A",
                border: dark?"1px solid rgba(255,255,255,.1)":"1px solid #D0E4EF" }}>
                📍 {slot.data.postalCity}
              </span>
            )}
          </div>
        </div>
        {/* Card footer actions */}
        <div style={{ display:"flex", borderTop:`1px solid ${dark?"rgba(255,255,255,.07)":"#E8EEF4"}` }}>
          <button onClick={() => setActive(active===idx ? null : idx)}
            style={{ flex:1, padding:"10px 0", background:"none", border:"none",
              fontSize:12, fontWeight:700, cursor:"pointer",
              color: dark?"rgba(255,255,255,.6)":"#2196C9",
              borderRight:`1px solid ${dark?"rgba(255,255,255,.07)":"#E8EEF4"}` }}>
            ✏ Edit
          </button>
          <button onClick={() => deleteSlot(idx)}
            style={{ padding:"10px 14px", background:"none", border:"none",
              fontSize:12, fontWeight:700, cursor:"pointer", color:"#e05555" }}>
            ✕
          </button>
        </div>
      </div>
    );
  };

  // "Add slot" placeholder tile
  const addTile = slots.length < 15 ? (
    <div onClick={addSlot}
      style={{ border:`2px dashed ${dark?"rgba(33,150,201,.3)":"#B8D8EE"}`, borderRadius:14,
        background: dark?"rgba(33,150,201,.04)":"#F6FAFD",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        minHeight:130, cursor:"pointer", gap:8, transition:"all .15s",
        color: dark?"rgba(33,150,201,.7)":"#4DAFE0" }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor="#2196C9"; e.currentTarget.style.background=dark?"rgba(33,150,201,.09)":"#EBF6FD"; }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor=dark?"rgba(33,150,201,.3)":"#B8D8EE"; e.currentTarget.style.background=dark?"rgba(33,150,201,.04)":"#F6FAFD"; }}>
      <div style={{ width:36, height:36, borderRadius:10, border:`2px dashed currentColor`,
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:300 }}>+</div>
      <div style={{ fontSize:12, fontWeight:700 }}>Add Slot</div>
      <div style={{ fontSize:11, opacity:0.6 }}>{15 - slots.length} remaining</div>
    </div>
  ) : null;

  return (
    <div>
      {/* Section header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:800,
            textTransform:"uppercase", letterSpacing:"0.08em",
            color: dark?"rgba(255,255,255,.5)":"#8aa5b5" }}>
            ⚡ Quick Fill Slots
          </div>
          <div style={{ fontSize:12, color: dark?"rgba(255,255,255,.3)":"#aabfcc", marginTop:2 }}>
            Save commonly used contacts to autofill form sections instantly — {slots.length}/15 used
          </div>
        </div>
      </div>

      {err && <div style={{ background:"rgba(224,85,85,.12)", color:"#e05555", padding:"8px 12px",
        borderRadius:8, fontSize:12, marginBottom:12, border:"1px solid rgba(224,85,85,.25)" }}>⚠ {err}</div>}

      {/* Tile grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:12 }}>
        {slots.map((slot, idx) => slotCard(slot, idx))}
        {addTile}
      </div>

      {slots.length === 0 && !addTile && null}

      {/* ── Edit modal overlay ── */}
      {active !== null && slots[active] && (() => {
        const idx = active;
        const slot = slots[idx];
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9999,
            background:"rgba(0,0,0,.55)", display:"flex", alignItems:"center", justifyContent:"center",
            padding:16 }}
            onClick={e => { if (e.target===e.currentTarget) setActive(null); }}>
            <div style={{ background: dark?"#182c3a":"#fff", borderRadius:18,
              width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto",
              boxShadow:"0 24px 80px rgba(0,0,0,.4)",
              border:`1px solid ${dark?"rgba(255,255,255,.1)":"#D0E4EF"}` }}>
              {/* Modal header */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"18px 24px", borderBottom:`1px solid ${dark?"rgba(255,255,255,.08)":"#E8EEF4"}`,
                position:"sticky", top:0, background: dark?"#182c3a":"#fff", zIndex:1, borderRadius:"18px 18px 0 0" }}>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color: dark?"rgba(255,255,255,.9)":"#1A3A4A" }}>
                    {slot.data.slot_name || "Edit Slot"}
                  </div>
                  <div style={{ fontSize:12, color: dark?"rgba(255,255,255,.35)":"#8aa5b5", marginTop:2 }}>
                    Quick fill contact slot {idx+1} of {slots.length}
                  </div>
                </div>
                <button onClick={() => setActive(null)}
                  style={{ background:"none", border:"none", cursor:"pointer", fontSize:20,
                    color: dark?"rgba(255,255,255,.4)":"#aabfcc", lineHeight:1, padding:"4px 8px" }}>✕</button>
              </div>
              {/* Modal body */}
              <div style={{ padding:"20px 24px 24px" }}>
                <FI label="Slot Name (e.g. FNB Bank, John Smith)" value={slot.data.slot_name}
                  onChange={e=>upd(idx,"slot_name",e.target.value)} />
                <SecHead>Identification</SecHead>
                <G2>
                  <FS label="ID Type" value={slot.data.idType} onChange={e=>upd(idx,"idType",e.target.value)} options={ID_TYPES} />
                  <FI label="ID Number" value={slot.data.idNumber} onChange={e=>upd(idx,"idNumber",e.target.value)} />
                </G2>
                <G2>
                  <FS label="Gender / Nature" value={slot.data.gender} onChange={e=>upd(idx,"gender",e.target.value)}
                    options={["","Male","Female","One-man business","Private company","CC"]} />
                  <FI label="Date of Birth" type="date" value={slot.data.dob} onChange={e=>upd(idx,"dob",e.target.value)} />
                </G2>
                <SecHead>Name</SecHead>
                <FI label="Surname / Organisation" value={slot.data.surname} onChange={e=>upd(idx,"surname",e.target.value)} />
                <G2>
                  <FI label="Initials" value={slot.data.initials} onChange={e=>upd(idx,"initials",e.target.value)} />
                  <FI label="First Names" value={slot.data.firstNames} onChange={e=>upd(idx,"firstNames",e.target.value)} />
                </G2>
                <SecHead>Contact</SecHead>
                <FI label="Email" type="email" value={slot.data.email} onChange={e=>upd(idx,"email",e.target.value)} />
                <G2>
                  <FI label="Cell" value={slot.data.cell} onChange={e=>upd(idx,"cell",e.target.value)} />
                  <FI label="Tel (Day)" value={slot.data.telDay} onChange={e=>upd(idx,"telDay",e.target.value)} />
                </G2>
                <SecHead>Postal Address</SecHead>
                <FI label="Line 1" value={slot.data.postalAddr}  onChange={e=>upd(idx,"postalAddr",e.target.value)} />
                <FI label="Line 2" value={slot.data.postalAddr2} onChange={e=>upd(idx,"postalAddr2",e.target.value)} />
                <FI label="Line 3" value={slot.data.postalAddr3} onChange={e=>upd(idx,"postalAddr3",e.target.value)} />
                <G2>
                  <FI label="Suburb"    value={slot.data.postalSuburb} onChange={e=>upd(idx,"postalSuburb",e.target.value)} />
                  <FI label="City/Town" value={slot.data.postalCity}   onChange={e=>upd(idx,"postalCity",e.target.value)} />
                </G2>
                <FI label="Postal Code" value={slot.data.postalCode} onChange={e=>upd(idx,"postalCode",e.target.value)} />
                <SecHead>Street Address</SecHead>
                <FI label="Line 1" value={slot.data.streetAddr}  onChange={e=>upd(idx,"streetAddr",e.target.value)} />
                <FI label="Line 2" value={slot.data.streetAddr2} onChange={e=>upd(idx,"streetAddr2",e.target.value)} />
                <FI label="Line 3" value={slot.data.streetAddr3} onChange={e=>upd(idx,"streetAddr3",e.target.value)} />
                <G2>
                  <FI label="Suburb"    value={slot.data.streetSuburb} onChange={e=>upd(idx,"streetSuburb",e.target.value)} />
                  <FI label="City/Town" value={slot.data.streetCity}   onChange={e=>upd(idx,"streetCity",e.target.value)} />
                </G2>
                <FI label="Postal Code" value={slot.data.streetCode} onChange={e=>upd(idx,"streetCode",e.target.value)} />
                <SecHead>Proxy / Representative</SecHead>
                <G2>
                  <FS label="ID Type" value={slot.data.proxy_idType} onChange={e=>upd(idx,"proxy_idType",e.target.value)} options={ID_TYPES} />
                  <FI label="ID Number" value={slot.data.proxy_idNumber} onChange={e=>upd(idx,"proxy_idNumber",e.target.value)} />
                </G2>
                <FS label="Country of Issue" value={slot.data.proxy_country} onChange={e=>upd(idx,"proxy_country",e.target.value)} options={COUNTRIES} />
                <G2>
                  <FI label="Surname" value={slot.data.proxy_surname} onChange={e=>upd(idx,"proxy_surname",e.target.value)} />
                  <FI label="Initials" value={slot.data.proxy_initials} onChange={e=>upd(idx,"proxy_initials",e.target.value)} />
                </G2>
                <div style={{ display:"flex", gap:10, marginTop:20 }}>
                  <Btn variant="gold" onClick={()=>saveSlot(idx)} style={{ flex:1, padding:"12px 0" }}>
                    {saving?"Saving…":"Save Slot"}
                  </Btn>
                  <Btn variant="outline" onClick={()=>setActive(null)} style={{ padding:"12px 20px" }}>Cancel</Btn>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ═══════════════ PERSONAL PROFILE EDITOR ═══════════════ */
function PersonalProfileEditor({ personal, onSave, onCancel }) {
  const dark = useDark();
  const [p, setP] = useState({ ...personal });
  const sp = k => e => setP(prev => ({ ...prev, [k]: e.target.value }));

  const card = (children, opts = {}) => (
    <div style={{ background: dark?"rgba(255,255,255,.04)":"#fff",
      border:`1px solid ${dark?"rgba(255,255,255,.08)":"#dce8f0"}`,
      borderRadius:14, padding:"20px 22px", marginBottom:16,
      boxShadow: dark?"none":"0 1px 4px rgba(0,0,0,.04)" }}>
      {opts.title && (
        <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase",
          letterSpacing:"0.1em", color: dark?"rgba(255,255,255,.3)":"#8aa5b5",
          marginBottom:14 }}>{opts.icon} {opts.title}</div>
      )}
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"0 20px 40px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, margin:0,
            color: dark?"rgba(255,255,255,.9)":"#1A3A4A", letterSpacing:-0.5 }}>
            👤 My Profile
          </h1>
          <p style={{ fontSize:13, margin:"4px 0 0", color: dark?"rgba(255,255,255,.35)":"#8aa5b5" }}>
            Your personal details and account settings
          </p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="outline" onClick={onCancel} style={{ padding:"10px 20px" }}>Cancel</Btn>
          <Btn variant="gold" onClick={() => onSave(p)} style={{ padding:"10px 24px" }}>Save</Btn>
        </div>
      </div>

      {card(<>
        <G2>
          <FI label="First Name(s)" value={p.firstName} onChange={sp("firstName")} />
          <FI label="Surname" value={p.surname} onChange={sp("surname")} />
        </G2>
        <G2>
          <FI label="ID / Passport Number" value={p.idNumber} onChange={sp("idNumber")} />
          <FI label="Cell Number" value={p.cell} onChange={sp("cell")} />
        </G2>
        <FI label="Personal Email" type="email" value={p.personalEmail} onChange={sp("personalEmail")} />
      </>, { title:"Personal Details", icon:"👤" })}

      {card(<PasswordChangeCard />, { title:"Change Password", icon:"🔒" })}

      <div style={{ display:"flex", gap:12, justifyContent:"flex-end", marginTop:8 }}>
        <Btn variant="outline" onClick={onCancel} style={{ padding:"11px 24px" }}>Cancel</Btn>
        <Btn variant="gold" onClick={() => onSave(p)} style={{ padding:"11px 28px" }}>Save</Btn>
      </div>
    </div>
  );
}

function ProfileEditor({ profile, onSave, onCancel, userId }) {
  const dark = useDark();
  const [p, setP] = useState({ ...BLANK_PROFILE, ...profile });
  const sp = k => e => setP(x => ({ ...x, [k]:e.target.value }));

  const card = (children, opts={}) => (
    <div style={{
      background: dark?"rgba(255,255,255,.04)":"#fff",
      border:`1px solid ${dark?"rgba(255,255,255,.10)":"#D0E4EF"}`,
      borderRadius:16, padding:"22px 24px",
      boxShadow: dark?"none":"0 1px 6px rgba(0,0,0,.06)",
      ...opts.style
    }}>
      {opts.title && (
        <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em",
          color: dark?"rgba(255,255,255,.35)":"#8aa5b5", marginBottom:16, paddingBottom:10,
          borderBottom:`1px solid ${dark?"rgba(255,255,255,.07)":"#E8EEF4"}` }}>
          {opts.icon} {opts.title}
        </div>
      )}
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth:960, margin:"0 auto", padding:"0 20px 40px" }}>

      {/* Page header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, margin:0,
            color: dark?"rgba(255,255,255,.9)":"#1A3A4A", letterSpacing:-0.5 }}>
            🏢 Dealer / Company Profile
          </h1>
          <p style={{ fontSize:13, margin:"4px 0 0", color: dark?"rgba(255,255,255,.35)":"#8aa5b5" }}>
            Your business details — auto-fills into every form you create
          </p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="outline" onClick={onCancel} style={{ padding:"10px 20px" }}>Cancel</Btn>
          <Btn variant="gold" onClick={() => onSave(p)} style={{ padding:"10px 24px" }}>Save Profile</Btn>
        </div>
      </div>

      {/* ── Top row: 3 tiles ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:16, marginBottom:16 }}>

        {/* Tile 1 — Business */}
        {card(<>
          <FI label="Company / Trading Name" value={p.companyName} onChange={sp("companyName")} />
          <G2>
            <FI label="Reg Number" value={p.regNumber} onChange={sp("regNumber")} />
            <FI label="VAT Number" value={p.vatNumber} onChange={sp("vatNumber")} />
          </G2>
          <G2>
            <FI label="Email" type="email" value={p.email} onChange={sp("email")} />
            <FI label="Phone" value={p.phone} onChange={sp("phone")} />
          </G2>
        </>, { title:"Business Details", icon:"🏢" })}

        {/* Tile 2 — Postal Address */}
        {card(<>
          <FI label="Line 1" value={p.postalAddr}   onChange={sp("postalAddr")} />
          <FI label="Line 2" value={p.postalAddr2}  onChange={sp("postalAddr2")} />
          <FI label="Line 3" value={p.postalAddr3}  onChange={sp("postalAddr3")} />
          <G2>
            <FI label="Suburb"    value={p.postalSuburb} onChange={sp("postalSuburb")} />
            <FI label="City/Town" value={p.postalCity}   onChange={sp("postalCity")} />
          </G2>
          <FI label="Postal Code" value={p.postalCode} onChange={sp("postalCode")} />
        </>, { title:"Postal Address", icon:"📬" })}

        {/* Tile 3 — Street Address */}
        {card(<>
          <FI label="Line 1" value={p.address}  onChange={sp("address")} />
          <FI label="Line 2" value={p.address2} onChange={sp("address2")} />
          <FI label="Line 3" value={p.address3} onChange={sp("address3")} />
          <G2>
            <FI label="Suburb"    value={p.addressSuburb} onChange={sp("addressSuburb")} />
            <FI label="City/Town" value={p.addressCity}   onChange={sp("addressCity")} />
          </G2>
          <FI label="Postal Code" value={p.addressCode} onChange={sp("addressCode")} />
        </>, { title:"Street Address", icon:"📍" })}

      </div>

      {/* ── Proxy tile (full width, compact) ── */}
      {card(<>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:12 }}>
          <FS label="ID Type" value={p.proxy_idType} onChange={sp("proxy_idType")} options={ID_TYPES} />
          <FI label="ID Number" value={p.proxy_idNumber} onChange={sp("proxy_idNumber")} />
          <FS label="Country of Issue" value={p.proxy_country} onChange={sp("proxy_country")} options={COUNTRIES} />
          <FI label="Surname" value={p.proxy_surname} onChange={sp("proxy_surname")} />
          <FI label="Initials" value={p.proxy_initials} onChange={sp("proxy_initials")} />
        </div>
      </>, { title:"Default Proxy / Representative", icon:"👤", style:{ marginBottom:16 } })}

      {/* Bottom save bar */}
      <div style={{ display:"flex", gap:12, marginTop:20, justifyContent:"flex-end" }}>
        <Btn variant="outline" onClick={onCancel} style={{ padding:"11px 24px" }}>Cancel</Btn>
        <Btn variant="gold" onClick={() => onSave(p)} style={{ padding:"11px 28px" }}>Save Profile</Btn>
      </div>
    </div>
  );
}

function PasswordChangeCard() {
  const dark = useDark();
  const [next,    setNext]    = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg,     setMsg]     = useState(null); // { type:"success"|"error", text }
  const [saving,  setSaving]  = useState(false);

  const submit = async () => {
    if (!next)            { setMsg({ type:"error", text:"New password is required." }); return; }
    if (next.length < 6)  { setMsg({ type:"error", text:"Password must be at least 6 characters." }); return; }
    if (next !== confirm) { setMsg({ type:"error", text:"Passwords do not match." }); return; }
    setSaving(true); setMsg(null);
    const { error } = await supabase.auth.updateUser({ password: next });
    setSaving(false);
    if (error) { setMsg({ type:"error", text: error.message }); return; }
    setMsg({ type:"success", text:"Password updated successfully!" });
    setNext(""); setConfirm("");
  };

  const inpStyle = {
    width:"100%", padding:"10px 14px", borderRadius:9, fontSize:14,
    border:`1.5px solid ${dark?"rgba(255,255,255,.14)":"#DDE3F0"}`,
    background: dark?"rgba(255,255,255,.06)":"#fff",
    color: dark?"#e8f0f5":"#1A3A4A", outline:"none", boxSizing:"border-box", marginBottom:12,
  };

  return (
    <div style={{ maxWidth:400 }}>
      {msg && (
        <div style={{ padding:"10px 14px", borderRadius:9, marginBottom:14, fontSize:13, fontWeight:600,
          background: msg.type==="success" ? (dark?"rgba(46,204,138,.15)":"#E8F5EF") : (dark?"rgba(224,85,85,.15)":"#FDEAEA"),
          color: msg.type==="success" ? "#2ecc8a" : "#C0392B",
          border:`1px solid ${msg.type==="success"?"#2ecc8a33":"#C0392B33"}` }}>
          {msg.type==="success" ? "✓ " : "⚠ "}{msg.text}
        </div>
      )}
      <input type="password" placeholder="New password" value={next} onChange={e => setNext(e.target.value)} style={inpStyle} />
      <input type="password" placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inpStyle} />
      <Btn variant="primary" onClick={submit} disabled={saving} style={{ padding:"10px 24px" }}>
        {saving ? "Updating…" : "Update Password"}
      </Btn>
    </div>
  );
}

/* ═══════════════ APP SHELL ═══════════════ */
export default function App() {
  const [user,        setUser]       = useState(null);
  const [view,        setView]       = useState("loading");
  const [txns,        setTxns]       = useState([]);
  const [editing,     setEditing]    = useState(null);
  const [profile,     setProfile]    = useState({...BLANK_PROFILE});
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState("");
  const [personalProfile, setPersonalProfile] = useState({ firstName:"", surname:"", idNumber:"", cell:"", personalEmail:"" });
  const [pendingType, setPendingType] = useState("cash");
  const [quickSlots,  setQuickSlots]  = useState([]);
  const [darkMode,    setDarkMode]   = useState(() => LS.get("rezi_dark", false));
  const [dashView,    setDashView]   = useState("history");
  const [pendingPlan, setPendingPlan] = useState("Small Dealer");
  // eslint-disable-next-line no-unused-vars
  const [autoDraftId, setAutoDraftId] = useState(null);

  /* ── Load all user data from Supabase after login ── */
  const loadUserData = async (userId) => {
    const [{ data: txnData }, { data: profileData }, { data: slotData }] = await Promise.all([
      supabase.from("transactions").select("*").eq("user_id", userId).order("updated_at", { ascending: false }),
      supabase.from("profiles").select("*").eq("id", userId).single(),
      supabase.from("quick_slots").select("*").eq("user_id", userId).order("slot_order", { ascending: true }),
    ]);
    if (txnData) setTxns(txnData.map(r => ({ id: r.id, form: r.form_data, status: r.status, updatedAt: r.updated_at, __autoDraft: r.is_auto_draft })));
    if (profileData) setProfile({
      companyName: profileData.company_name || "", regNumber: profileData.reg_number || "",
      vatNumber: profileData.vat_number || "", email: profileData.email || "", phone: profileData.phone || "",
      address: profileData.address?.line1 || "", address2: profileData.address?.line2 || "",
      address3: profileData.address?.line3 || "", addressSuburb: profileData.address?.suburb || "",
      addressCity: profileData.address?.city || "", addressCode: profileData.address?.code || "",
      postalAddr: profileData.postal?.line1 || "", postalAddr2: profileData.postal?.line2 || "",
      postalAddr3: profileData.postal?.line3 || "", postalSuburb: profileData.postal?.suburb || "",
      postalCity: profileData.postal?.city || "", postalCode: profileData.postal?.code || "",
      proxy_idType: profileData.proxy?.idType || "", proxy_idNumber: profileData.proxy?.idNumber || "",
      proxy_country: profileData.proxy?.country || "", proxy_surname: profileData.proxy?.surname || "",
      proxy_initials: profileData.proxy?.initials || "",
    });
    if (slotData) setQuickSlots(slotData.map(r => r.slot_data));
    // Also load personal fields from users table
    const { data: userData } = await supabase.from("users").select("first_name,surname,id_number,cell,personal_email").eq("id", userId).single();
    if (userData) setPersonalProfile({
      firstName: userData.first_name || "", surname: userData.surname || "",
      idNumber: userData.id_number || "", cell: userData.cell || "",
      personalEmail: userData.personal_email || "",
    });
  };

  /* ── Restore session on boot ── */
  useEffect(() => {
    // Check URL hash for recovery token BEFORE checking session
    // When user clicks reset link, Supabase embeds type=recovery in the hash
    const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
    const isRecovery = hashParams.get("type") === "recovery";

    if (isRecovery) {
      // Strip the hash so it doesn't linger in the URL
      window.history.replaceState(null, "", window.location.pathname);
      setView("reset-password");
      return;
    }

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const { data: profile } = await supabase.from("users").select("*").eq("id", session.user.id).single();
        const u = profile || { id: session.user.id, email: session.user.email, plan: "Individual", subscribed: false };
        setUser(u);
        await loadUserData(u.id);
        setView("dashboard");
      } else {
        setView("login");
      }
    });
    // Listen for sign-out and password recovery from other tabs
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT")        { setUser(null); setTxns([]); setProfile({...BLANK_PROFILE}); setView("login"); }
      if (event === "PASSWORD_RECOVERY") { setView("reset-password"); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Poll session validity every 90 seconds — kicks this device if another login has occurred
  useEffect(() => {
    const check = async () => {
      const sess = JSON.parse(sessionStorage.getItem("rezi-session") || "{}");
      if (!sess.userId || !sess.token) return;
      try {
        const res = await fetch("/api/verify-session", {
          method: "POST",
          headers: { "X-User-Id": sess.userId, "X-Session-Token": sess.token },
        });
        if (res.status === 401) window.__reziForceLogout?.();
      } catch { /* network error — don't log out, just skip */ }
    };
    const interval = setInterval(check, 90_000);
    return () => clearInterval(interval);
  }, []);

  // Inject CSS vars for dark mode — affects iS inputs across the whole app
  useEffect(() => {
    const r = document.documentElement;
    // Remove any existing injected style
    const existing = document.getElementById("rezi-dark-style");
    if (existing) existing.remove();
    if (darkMode) {
      r.style.setProperty("--rz-input-bg",     "#182c3a");
      r.style.setProperty("--rz-input-border",  "rgba(255,255,255,0.14)");
      r.style.setProperty("--rz-text",          "#e8f0f5");
      r.style.setProperty("--rz-select-opt-bg", "#182c3a");
      // Inject stylesheet to style native select options (can't be done inline)
      const style = document.createElement("style");
      style.id = "rezi-dark-style";
      style.textContent = `
        select option { background: #182c3a !important; color: #e8f0f5 !important; }
        select { color-scheme: dark; }
        input[type=date] { color-scheme: dark; }
        :root { color-scheme: dark; }
        body { background: #0d1e28; }
      `;
      document.head.appendChild(style);
    } else {
      r.style.setProperty("--rz-input-bg",     "#ffffff");
      r.style.setProperty("--rz-input-border",  "#DDE3F0");
      r.style.setProperty("--rz-text",          "#1A3A4A");
      r.style.setProperty("--rz-select-opt-bg", "#ffffff");
      const style = document.createElement("style");
      style.id = "rezi-dark-style";
      style.textContent = `
        select option { background: #ffffff !important; color: #1A3A4A !important; }
        select { color-scheme: light; }
        input[type=date] { color-scheme: light; }
        :root { color-scheme: light; }
        body { background: #F0F6FA; }
      `;
      document.head.appendChild(style);
    }
  }, [darkMode]);

  const toggleDark = () => { const v=!darkMode; setDarkMode(v); LS.set("rezi_dark",v); };

  // Bridge so Login (which has no direct setView prop) can navigate to legal pages
  React.useEffect(() => { window.__reziSetView = setView; }, [setView]);

  // Bridge for force-logout from anywhere (e.g. 401 from API call in standalone functions)
  React.useEffect(() => {
    window.__reziForceLogout = async (msg) => {
      sessionStorage.removeItem("rezi-session");
      await supabase.auth.signOut();
      setUser(null); setTxns([]); setProfile({...BLANK_PROFILE});
      setSessionExpiredMsg(msg || "Your session has ended. Please sign in again.");
      setView("login");
    };
    return () => { window.__reziForceLogout = null; };
  }, []);

  const login = async (u) => {
    setUser(u);
    await loadUserData(u.id);
    setView("dashboard");
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null); setTxns([]); setProfile({...BLANK_PROFILE});
    setView("login");
  };

  // Trial check
  const isTrial      = user && user.trial_expires_at && !user.subscribed;
  const trialExpired = isTrial && new Date() > new Date(user.trial_expires_at);
  const TRIAL_FORM_LIMIT = 20;
  const trialFormsUsed = user?.trial_forms_used || 0;
  const trialFormLimitHit = isTrial && !trialExpired && trialFormsUsed >= TRIAL_FORM_LIMIT;

  // Live token object — changes with dark mode
  const T2 = makeT(darkMode);

  const saveTxn = async (form, status) => {
    const now = new Date().toISOString();
    const isEdit = editing !== null;
    const id = isEdit ? txns[editing].id : crypto.randomUUID();

    await supabase.from("transactions").upsert({
      id,
      user_id: user.id,
      form_data: form,
      status: status,
      is_auto_draft: false,
      form_version: 1,
      updated_at: now,
    }, { onConflict: "id" });

    await supabase.from("transactions").delete().eq("user_id", user.id).eq("is_auto_draft", true).neq("id", id);

    const { data } = await supabase
      .from("transactions")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });

    if (data) {
      setTxns(data.map(t => ({
        id: t.id,
        form: t.form_data,
        status: t.status,
        updatedAt: t.updated_at,
        __autoDraft: t.is_auto_draft || false
      })));
    }

    setAutoDraftId(null);
    setEditing(null);
    setView("dashboard");
  };

  // Auto-save draft in place — updates txns list without navigating away
  const autoSaveTxn = React.useCallback((form, currentEditingId) => {
    const now = new Date().toISOString();
    setTxns(prev => {
      let updated;
      let upsertId, upsertForm;
      if (currentEditingId !== null) {
        updated = prev.map((t,i) => i===currentEditingId ? { ...t, form, status:"draft", updatedAt:now } : t);
        upsertId = prev[currentEditingId].id;
        upsertForm = form;
      } else if (prev.length > 0 && prev[0].__autoDraft) {
        updated = [{ ...prev[0], form, updatedAt:now }, ...prev.slice(1)];
        upsertId = prev[0].id;
        upsertForm = form;
      } else {
        const entry = { id: crypto.randomUUID(), form, status:"draft", updatedAt:now, __autoDraft:true };
        updated = [entry, ...prev];
        upsertId = entry.id;
        upsertForm = entry.form;
      }
      // Fire-and-forget save
      supabase.from("transactions").upsert({
        id: upsertId, user_id: user?.id, form_data: upsertForm,
        status: "draft", is_auto_draft: true, form_version: 1, updated_at: now,
      }, { onConflict: "id" }).then(() => {});
      return updated;
    });
  }, [user?.id]);

  const saveProfile = async (p) => {
    setProfile(p);
    await supabase.from("profiles").upsert({
      id: user.id,
      company_name: p.companyName, reg_number: p.regNumber, vat_number: p.vatNumber,
      email: p.email, phone: p.phone,
      address: { line1: p.address, line2: p.address2, line3: p.address3, suburb: p.addressSuburb, city: p.addressCity, code: p.addressCode },
      postal: { line1: p.postalAddr, line2: p.postalAddr2, line3: p.postalAddr3, suburb: p.postalSuburb, city: p.postalCity, code: p.postalCode },
      proxy: { idType: p.proxy_idType, idNumber: p.proxy_idNumber, country: p.proxy_country, surname: p.proxy_surname, initials: p.proxy_initials },
      updated_at: new Date().toISOString(),
    }, { onConflict: "id" });
    setView("dashboard");
  };

  const savePersonalProfile = async (p) => {
    setPersonalProfile(p);
    await supabase.from("users").update({
      first_name: p.firstName, surname: p.surname,
      id_number: p.idNumber, cell: p.cell, personal_email: p.personalEmail,
    }).eq("id", user.id);
    setView("dashboard");
  };

  const deleteTxn = async (i) => {
    if (!window.confirm("Delete this transaction?")) return;
    const txnId = txns[i].id;
    const updated = txns.filter((_,idx) => idx!==i);
    setTxns(updated);
    await supabase.from("transactions").delete().eq("id", txnId);
  };

  if (view==="loading") return (
    <div style={{ minHeight:"100vh", background:"#0d1e28",
      display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <img src="/Rezi_Logo.jpeg" alt="ReZi"
        style={{ width:72, height:72, borderRadius:16, objectFit:"cover", opacity:0.9 }} />
      <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", letterSpacing:"0.1em" }}>Loading…</div>
    </div>
  );

  if (trialExpired)      return <TrialExpiredWall plan={user.plan} onSignOut={logout} reason="expired" />;
  if (trialFormLimitHit) return <TrialExpiredWall plan={user.plan} onSignOut={logout} reason="limit" />;
  if (view==="login")   return <Login onLogin={login} onPricing={() => setView("pricing")} onSignup={() => setView("pricing")} onForgot={() => setView("forgot")} sessionExpiredMsg={sessionExpiredMsg} onClearExpiredMsg={() => setSessionExpiredMsg("")} />;
  if (view==="forgot")        return <ForgotPassword onBack={() => setView("login")} />;
  if (view==="reset-password") return <ResetPassword onDone={() => setView("login")} />;
  if (view==="pricing") return <Pricing onBack={() => setView("login")} onSignup={planName => { setPendingPlan(planName); setView("signup"); }} />;
  if (view==="signup")  return <Signup onLogin={login} onBack={() => setView("pricing")} initialPlan={pendingPlan} />;
  if (view==="terms")   return <TermsPage onBack={() => setView(user ? "dashboard" : "login")} />;
  if (view==="privacy") return <PrivacyPage onBack={() => setView(user ? "dashboard" : "login")} />;

  /* ── Dark mode toggle button ── */
  const DarkBtn = () => (
    <button onClick={toggleDark}
      title={darkMode?"Switch to light mode":"Switch to dark mode"}
      style={{ background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.18)",
        borderRadius:8, padding:"7px 12px", cursor:"pointer", fontSize:16, lineHeight:1,
        color:"#fff", transition:"background .2s" }}>
      {darkMode ? "☀" : "🌙"}
    </button>
  );

  if (view==="new"||view==="edit")
    return (
      <DarkCtx.Provider value={darkMode}>
        <div style={{ minHeight:"100vh", background:T2.bg, display:"flex", flexDirection:"column" }}>
          {/* ── Form nav bar ── */}
          <div style={{
            background: darkMode ? "rgba(15,37,48,0.95)" : "#1A3A4A",
            borderBottom:`1px solid rgba(255,255,255,.1)`,
            padding:"0 20px", height:52,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            position:"sticky", top:0, zIndex:100,
            backdropFilter:"blur(8px)",
          }}>
            <button onClick={() => { setEditing(null); setView("dashboard"); }}
              style={{ display:"flex", alignItems:"center", gap:7,
                background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.15)",
                borderRadius:7, padding:"6px 14px", color:"rgba(255,255,255,.8)",
                fontSize:12, fontWeight:500, cursor:"pointer", letterSpacing:0.2 }}>
              ← Dashboard
            </button>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <img src="/Rezi_Logo.jpeg" alt="ReZi"
                style={{ height:30, borderRadius:6, objectFit:"cover" }} />
              <span style={{ fontSize:18, fontWeight:800, letterSpacing:-0.5 }}>
                <span style={{ color:"#F5A623" }}>Re</span><span style={{ color:"#fff" }}>Zi</span>
              </span>
            </div>
            <DarkBtn />
          </div>
          {/* ── Form body ── */}
          <div style={{ flex:1, paddingTop:24, paddingBottom:60 }}>
            <TransactionForm user={user} profile={profile} quickSlots={quickSlots}
              initial={view==="edit"&&editing!==null?txns[editing].form:{...BLANK_FORM,vc_financeType:pendingType,vc_cashSale:pendingType==="cash"}}
              onSave={saveTxn}
              onAutoSave={autoSaveTxn}
              editingIndex={editing}
              onFormDownloaded={async () => {
                if (!isTrial) return;
                const updated = { ...user, trial_forms_used: (user.trial_forms_used||0) + 1 };
                setUser(updated);
                await supabase.rpc("increment_trial_forms", { user_id: user.id });
              }}
              trialFormsLeft={isTrial ? Math.max(0, TRIAL_FORM_LIMIT - trialFormsUsed) : null}
              onCancel={() => { setEditing(null); setView("dashboard"); }} />
          </div>
        </div>
      </DarkCtx.Provider>
    );

  const viewHeader = (title) => (
    <div style={{
      background: darkMode ? "rgba(15,37,48,0.95)" : "#1A3A4A",
      borderBottom:`1px solid rgba(255,255,255,.1)`,
      padding:"0 20px", height:52,
      display:"flex", alignItems:"center", justifyContent:"space-between",
      backdropFilter:"blur(8px)",
    }}>
      <button onClick={() => setView("dashboard")}
        style={{ display:"flex", alignItems:"center", gap:7,
          background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.15)",
          borderRadius:7, padding:"6px 14px", color:"rgba(255,255,255,.8)",
          fontSize:12, fontWeight:500, cursor:"pointer" }}>
        ← Dashboard
      </button>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <img src="/Rezi_Logo.jpeg" alt="ReZi" style={{ height:30, borderRadius:6, objectFit:"cover" }} />
        <span style={{ fontSize:18, fontWeight:800, letterSpacing:-0.5 }}>
          <span style={{ color:"#F5A623" }}>Re</span><span style={{ color:"#fff" }}>Zi</span>
        </span>
      </div>
      <DarkBtn />
    </div>
  );

  if (view==="profile")
    return (
      <DarkCtx.Provider value={darkMode}>
        <div style={{ minHeight:"100vh", background:T2.bg }}>
          {viewHeader("My Profile")}
          <div style={{ paddingTop:36 }}>
            <PersonalProfileEditor personal={personalProfile} onSave={savePersonalProfile} onCancel={() => setView("dashboard")} />
          </div>
        </div>
      </DarkCtx.Provider>
    );

  if (view==="business")
    return (
      <DarkCtx.Provider value={darkMode}>
        <div style={{ minHeight:"100vh", background:T2.bg }}>
          {viewHeader("Business Profile")}
          <div style={{ paddingTop:36 }}>
            <ProfileEditor profile={profile} onSave={saveProfile} onCancel={() => setView("dashboard")} userId={user?.id} />
          </div>
        </div>
      </DarkCtx.Provider>
    );

  if (view==="quickslots")
    return (
      <DarkCtx.Provider value={darkMode}>
        <div style={{ minHeight:"100vh", background:T2.bg }}>
          {viewHeader("Business Profile & Quick Slots")}
          <div style={{ maxWidth:900, margin:"0 auto", padding:"36px 20px" }}>
            <div style={{ marginBottom:24, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div>
                <h1 style={{ fontSize:22, fontWeight:700, color: darkMode?"rgba(255,255,255,.9)":"#1A3A4A",
                  margin:0, letterSpacing:-0.5 }}>🏢 Business Profile & Quick Slots</h1>
                <p style={{ fontSize:13, color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa", margin:"4px 0 0" }}>
                  Your company details and saved contacts for fast form filling
                </p>
              </div>
              <button onClick={() => setView("business")}
                style={{ background:"#2196C9", border:"none", borderRadius:8, padding:"9px 18px",
                  color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}>
                Edit Business Details →
              </button>
            </div>
            {profile?.companyName && (
              <div style={{ background: darkMode?"rgba(33,150,201,.08)":"#EBF6FD",
                border:`1px solid ${darkMode?"rgba(33,150,201,.2)":"#b3d9f0"}`,
                borderRadius:10, padding:"12px 16px", marginBottom:20,
                display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:20 }}>🏢</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color: darkMode?"rgba(255,255,255,.85)":"#1A3A4A" }}>{profile.companyName}</div>
                  {profile.regNumber && <div style={{ fontSize:12, color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa" }}>Reg: {profile.regNumber}{profile.vatNumber ? ` · VAT: ${profile.vatNumber}` : ""}</div>}
                </div>
              </div>
            )}
            <QuickSlotsEditor userId={user?.id} />
          </div>
        </div>
      </DarkCtx.Provider>
    );

  /* ════════════════════════════════════════════
     DASHBOARD
  ════════════════════════════════════════════ */
  const txnTypes = [
    { value:"cash",    icon:"💵", label:"Cash Sale",    sub:"Buyer = Title Holder",    accent:"#2ecc8a" },
    { value:"finance", icon:"🏦", label:"Finance",       sub:"Bank holds title",        accent:"#2196C9" },
    { value:"stock",   icon:"🏢", label:"Dealer Stock",  sub:"Dealer buys from client", accent:"#9c6fdc" },
  ];

  const sideNavItems = [
    { icon:"＋", label:"New Form",   action:()=>setDashView("transactions"), active:dashView==="transactions" },
    { icon:"◷",  label:"History",    action:()=>setDashView("history"),      active:dashView==="history"      },
    { icon:"⚙",  label:"My Profile",    action:()=>setView("profile"),          active:false                     },
    { icon:"🏢", label:"Business",      action:()=>setView("quickslots"),       active:false                     },
    { icon:"💳", label:"Billing",    action:()=>setDashView("billing"),      active:dashView==="billing"      },
  ];

  const SideItem = ({icon, label, action, active}) => (
    <div onClick={action||undefined}
      style={{
        display:"flex", alignItems:"center", gap:10,
        padding:"9px 12px", borderRadius:8, marginBottom:2,
        cursor: action ? "pointer" : "default",
        background: active
          ? (darkMode ? "rgba(33,150,201,0.18)" : "rgba(33,150,201,0.12)")
          : "transparent",
        borderLeft: active ? "2px solid #2196C9" : "2px solid transparent",
        transition:"all .15s",
      }}
      onMouseEnter={e => { if(!active && action) e.currentTarget.style.background = darkMode?"rgba(255,255,255,.06)":"rgba(0,0,0,.04)"; }}
      onMouseLeave={e => { if(!active) e.currentTarget.style.background = "transparent"; }}>
      <span style={{ fontSize:14, width:18, textAlign:"center", flexShrink:0, opacity:0.7 }}>{icon}</span>
      <span style={{
        fontSize:13, fontWeight: active ? 600 : 400,
        color: active ? "#2196C9" : (darkMode ? "rgba(255,255,255,.55)" : "#5A7A8A"),
      }}>{label}</span>
    </div>
  );

  return (
    <DarkCtx.Provider value={darkMode}>
    <div style={{ minHeight:"100vh", background:T2.bg, display:"flex", flexDirection:"column" }}>

      {/* ══ TOP NAV ══ */}
      <div style={{
        background: darkMode ? "rgba(15,37,48,0.97)" : "#1A3A4A",
        borderBottom: `1px solid rgba(255,255,255,.08)`,
        padding:"0 20px", height:52,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        position:"sticky", top:0, zIndex:100,
        backdropFilter:"blur(8px)",
        flexShrink:0,
      }}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <img src="/Rezi_Logo.jpeg" alt="ReZi"
            style={{ height:32, borderRadius:7, objectFit:"cover",
              boxShadow:"0 2px 8px rgba(0,0,0,.3)" }} />
          <span style={{ fontSize:20, fontWeight:800, letterSpacing:-0.5, lineHeight:1 }}>
            <span style={{ color:"#F5A623" }}>Re</span><span style={{ color:"#fff" }}>Zi</span>
          </span>
        </div>

        {/* Centre — empty */ }
        <div />

        {/* Right controls */}
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {user?.plan && (
            <span style={{
              padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
              background:"rgba(245,166,35,.15)", color:"#F5A623",
              border:"1px solid rgba(245,166,35,.2)",
            }}>{user.plan}</span>
          )}
          <button onClick={() => setView("quickslots")}
            style={{ background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.14)",
              borderRadius:7, padding:"6px 12px", color:"rgba(255,255,255,.75)",
              fontSize:12, fontWeight:500, cursor:"pointer" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,.15)"}
            onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,.08)"}>
            Business & Slots
          </button>
          <button onClick={() => setView("profile")}
            style={{ background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.14)",
              borderRadius:7, padding:"6px 12px", color:"rgba(255,255,255,.75)",
              fontSize:12, fontWeight:500, cursor:"pointer" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,.15)"}
            onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,.08)"}>
            My Profile
          </button>
          <DarkBtn />
          <button onClick={logout}
            style={{ background:"transparent", border:"1px solid rgba(255,255,255,.1)",
              borderRadius:7, padding:"6px 12px", color:"rgba(255,255,255,.38)",
              fontSize:12, cursor:"pointer" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* ══ BODY: SIDEBAR + CONTENT ══ */}
      <div style={{ display:"flex", flex:1, minHeight:0 }}>

        {/* ── Sidebar ── */}
        <div style={{
          width:200, flexShrink:0,
          background: darkMode ? "rgba(10,26,34,0.6)" : "#f4f8fb",
          borderRight: `1px solid ${darkMode ? "rgba(255,255,255,.07)" : "#dce8f0"}`,
          padding:"20px 10px",
          position:"sticky", top:52, height:"calc(100vh - 52px)",
          overflowY:"auto",
        }}>
          <div style={{
            fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color: darkMode ? "rgba(255,255,255,.25)" : "#8aa5b5",
            padding:"0 12px", marginBottom:8,
          }}>Navigation</div>

          {sideNavItems.map(item => (
            <SideItem key={item.label} {...item} />
          ))}

          <div style={{
            borderTop:`1px solid ${darkMode ? "rgba(255,255,255,.07)" : "#dce8f0"}`,
            margin:"16px 0 12px",
          }} />

          <div style={{
            fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color: darkMode ? "rgba(255,255,255,.25)" : "#8aa5b5",
            padding:"0 12px", marginBottom:8,
          }}>New Transaction</div>

          {txnTypes.map(t => (
            <div key={t.value}
              onClick={() => { LS.del("rezi_draft"); setEditing(null); setPendingType(t.value); setView("new"); }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"9px 12px", borderRadius:8, marginBottom:2,
                cursor:"pointer", transition:"all .15s",
                borderLeft:"2px solid transparent",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = darkMode ? `${t.accent}22` : `${t.accent}18`;
                e.currentTarget.style.borderLeft = `2px solid ${t.accent}`;
                e.currentTarget.style.paddingLeft = "10px";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderLeft = "2px solid transparent";
              }}>
              <span style={{ fontSize:14, width:18, textAlign:"center" }}>{t.icon}</span>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color: darkMode ? "rgba(255,255,255,.7)" : "#2a4a5a" }}>{t.label}</div>
                <div style={{ fontSize:10, color: darkMode ? "rgba(255,255,255,.3)" : "#8aa5b5", marginTop:1 }}>{t.sub}</div>
              </div>
            </div>
          ))}

          {/* Trial info at bottom of sidebar */}
          {isTrial && (
            <div style={{
              marginTop:24, padding:"10px 12px", borderRadius:8,
              background: darkMode ? "rgba(245,166,35,.1)" : "#FEF6E7",
              border:`1px solid rgba(245,166,35,.25)`,
            }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#F5A623", marginBottom:3 }}>
                Free Trial
              </div>
              <div style={{ fontSize:10, color: darkMode ? "rgba(255,255,255,.4)" : "#8a7060" }}>
                {TRIAL_FORM_LIMIT - trialFormsUsed} of {TRIAL_FORM_LIMIT} forms left
              </div>
              <div onClick={() => setView("pricing")}
                style={{ fontSize:10, color:"#2196C9", fontWeight:600, marginTop:5, cursor:"pointer" }}>
                Subscribe →
              </div>
            </div>
          )}
        </div>

        {/* ── Main content ── */}
        <div style={{ flex:1, padding:"28px 32px 60px", overflowY:"auto", minWidth:0 }}>

          {/* ══ TRANSACTIONS VIEW ══ */}
          {dashView==="transactions" && <>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, marginBottom:24, flexWrap:"wrap" }}>
              <div>
                <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:-0.5, margin:0, color: darkMode?"rgba(255,255,255,.9)":"#1A3A4A" }}>New Transaction</h1>
                <p style={{ fontSize:13, margin:"4px 0 0", color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa" }}>Select a transaction type to begin</p>
              </div>
              {!profile?.companyName && (
                <div style={{
                  background: darkMode ? "rgba(245,166,35,.08)" : "#FFFBF0",
                  border:`1px solid rgba(245,166,35,.3)`,
                  borderRadius:10, padding:"12px 16px", maxWidth:280,
                  display:"flex", gap:10, alignItems:"flex-start",
                }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>💡</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color: darkMode?"#F5A623":"#b07a00", marginBottom:3 }}>Complete your profile</div>
                    <div style={{ fontSize:11, color: darkMode?"rgba(255,255,255,.45)":"#7a6a50", lineHeight:1.5 }}>
                      Add your company details in <span onClick={() => setView("profile")} style={{ color:"#2196C9", cursor:"pointer", fontWeight:600 }}>Profile</span> and set up Quick Slots to autofill info you use often.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:"flex", gap:10, marginBottom:28, flexWrap:"wrap" }}>
              {txnTypes.map(t => (
                <button key={t.value}
                  onClick={() => { LS.del("rezi_draft"); setEditing(null); setPendingType(t.value); setView("new"); }}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 18px", borderRadius:10, cursor:"pointer", background: darkMode?"rgba(255,255,255,.04)":"#fff", border:`1.5px solid ${darkMode?"rgba(255,255,255,.1)":"#dce8f0"}`, transition:"all .18s", flex:"1 1 160px", boxShadow: darkMode?"none":"0 1px 4px rgba(0,0,0,.05)" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=t.accent; e.currentTarget.style.background=darkMode?`${t.accent}18`:`${t.accent}10`; e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 4px 16px ${t.accent}33`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=darkMode?"rgba(255,255,255,.1)":"#dce8f0"; e.currentTarget.style.background=darkMode?"rgba(255,255,255,.04)":"#fff"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow=darkMode?"none":"0 1px 4px rgba(0,0,0,.05)"; }}>
                  <span style={{ fontSize:18 }}>{t.icon}</span>
                  <div style={{ textAlign:"left" }}>
                    <div style={{ fontSize:13, fontWeight:600, color: darkMode?"rgba(255,255,255,.85)":"#1A3A4A" }}>{t.label}</div>
                    <div style={{ fontSize:11, color: darkMode?"rgba(255,255,255,.35)":"#8aa5b5", marginTop:1 }}>{t.sub}</div>
                  </div>
                  <span style={{ marginLeft:"auto", fontSize:14, color: darkMode?"rgba(255,255,255,.2)":"#bdd0da" }}>→</span>
                </button>
              ))}
            </div>
            {/* Recent — last 5 */}
            {txns.length > 0 && <>
              <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", color: darkMode?"rgba(255,255,255,.3)":"#8aa5b5", marginBottom:10 }}>Recent</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {txns.filter(txn => txn.form?.vc_make || txn.form?.th_surname || txn.form?.vc_series).slice(0,5).map((txn,i) => {
                  const f=txn.form;
                  const buyer=`${f.th_surname||""}${f.th_firstNames?" "+f.th_firstNames:""}`.trim()||"—";
                  const veh=`${f.vc_make||""} ${f.vc_series||""}`.trim()||"Unknown vehicle";
                  const date=new Date(txn.updatedAt).toLocaleDateString("en-ZA");
                  const isComplete = txn.status==="complete";
                  const typeLabel = f.vc_financeType==="cash"?"Cash":f.vc_financeType==="finance"?"Finance":"Stock";
                  const typeColor = f.vc_financeType==="cash"?"#2ecc8a":f.vc_financeType==="finance"?"#2196C9":"#9c6fdc";
                  return (
                    <div key={txn.id}
                      onClick={() => { setEditing(i); setView("edit"); }}
                      style={{
                        display:"flex", alignItems:"center", gap:14,
                        padding:"13px 16px", borderRadius:10, cursor:"pointer",
                        background: darkMode ? "rgba(255,255,255,.03)" : "#fff",
                        border:`1px solid ${darkMode ? "rgba(255,255,255,.07)" : "#dce8f0"}`,
                        transition:"all .15s",
                        boxShadow: darkMode ? "none" : "0 1px 3px rgba(0,0,0,.04)",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = "#2196C9";
                        e.currentTarget.style.background = darkMode ? "rgba(33,150,201,.06)" : "#f0f8fd";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = darkMode ? "rgba(255,255,255,.07)" : "#dce8f0";
                        e.currentTarget.style.background = darkMode ? "rgba(255,255,255,.03)" : "#fff";
                      }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:typeColor, flexShrink:0, boxShadow:`0 0 6px ${typeColor}88` }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:600, color: darkMode ? "rgba(255,255,255,.85)" : "#1A3A4A", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{buyer}</div>
                        <div style={{ fontSize:12, marginTop:2, color: darkMode ? "rgba(255,255,255,.35)" : "#8aa5b5" }}>{veh}{f.vc_chassis ? ` · ${f.vc_chassis}` : ""}</div>
                      </div>
                      <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20, flexShrink:0, background:`${typeColor}22`, color:typeColor, border:`1px solid ${typeColor}44` }}>{typeLabel}</span>
                      <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20, flexShrink:0, background: isComplete?(darkMode?"rgba(46,204,138,.15)":"#E8F5EF"):(darkMode?"rgba(245,166,35,.13)":"#FEF6E7"), color: isComplete?"#2ecc8a":"#F5A623" }}>{isComplete?"✓ Done":"◷ Draft"}</span>
                      <span style={{ fontSize:11, color: darkMode?"rgba(255,255,255,.25)":"#aabfcc", flexShrink:0, minWidth:60, textAlign:"right" }}>{date}</span>
                      <button onClick={e => { e.stopPropagation(); deleteTxn(i); }}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, padding:"2px 6px", borderRadius:5, color: darkMode?"rgba(255,255,255,.2)":"#bbb", flexShrink:0 }}
                        onMouseEnter={e => e.currentTarget.style.color="#e05555"}
                        onMouseLeave={e => e.currentTarget.style.color=darkMode?"rgba(255,255,255,.2)":"#bbb"}>✕</button>
                    </div>
                  );
                })}
              </div>
              {txns.length > 5 && (
                <div onClick={() => setDashView("history")}
                  style={{ marginTop:12, textAlign:"center", fontSize:12, color:"#2196C9", cursor:"pointer", fontWeight:600 }}>
                  View all {txns.length} transactions →
                </div>
              )}
            </>}
            {txns.length===0 && (
              <div style={{ textAlign:"center", padding:"48px 0", color: darkMode?"rgba(255,255,255,.25)":"#aabfcc" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📋</div>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:4, color: darkMode?"rgba(255,255,255,.5)":"#4a6a7a" }}>No transactions yet</div>
                <div style={{ fontSize:13 }}>Click a type above to get started</div>
              </div>
            )}
          </>}

          {/* ══ HISTORY VIEW ══ */}
          {dashView==="history" && <>
            <div style={{ marginBottom:24 }}>
              <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:-0.5, margin:0, color: darkMode?"rgba(255,255,255,.9)":"#1A3A4A" }}>History</h1>
              <p style={{ fontSize:13, margin:"4px 0 0", color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa" }}>
                {txns.length} transaction{txns.length!==1?"s":""} saved
              </p>
            </div>
            {txns.length===0 ? (
              <div style={{ textAlign:"center", padding:"56px 0", color: darkMode?"rgba(255,255,255,.25)":"#aabfcc" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>◷</div>
                <div style={{ fontSize:15, fontWeight:600, color: darkMode?"rgba(255,255,255,.5)":"#4a6a7a" }}>No history yet</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {txns.filter(txn => txn.form?.vc_make || txn.form?.th_surname || txn.form?.vc_series).map((txn,i) => {
                  const f=txn.form;
                  const buyer=`${f.th_surname||""}${f.th_firstNames?" "+f.th_firstNames:""}`.trim()||"—";
                  const veh=`${f.vc_make||""} ${f.vc_series||""}`.trim()||"Unknown vehicle";
                  const date=new Date(txn.updatedAt).toLocaleDateString("en-ZA");
                  const isComplete = txn.status==="complete";
                  const typeLabel = f.vc_financeType==="cash"?"Cash":f.vc_financeType==="finance"?"Finance":"Stock";
                  const typeColor = f.vc_financeType==="cash"?"#2ecc8a":f.vc_financeType==="finance"?"#2196C9":"#9c6fdc";
                  return (
                    <div key={txn.id}
                      onClick={() => { setEditing(i); setView("edit"); }}
                      style={{
                        display:"flex", alignItems:"center", gap:14,
                        padding:"13px 16px", borderRadius:10, cursor:"pointer",
                        background: darkMode ? "rgba(255,255,255,.03)" : "#fff",
                        border:`1px solid ${darkMode ? "rgba(255,255,255,.07)" : "#dce8f0"}`,
                        transition:"all .15s",
                        boxShadow: darkMode ? "none" : "0 1px 3px rgba(0,0,0,.04)",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = "#2196C9";
                        e.currentTarget.style.background = darkMode ? "rgba(33,150,201,.06)" : "#f0f8fd";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = darkMode ? "rgba(255,255,255,.07)" : "#dce8f0";
                        e.currentTarget.style.background = darkMode ? "rgba(255,255,255,.03)" : "#fff";
                      }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:typeColor, flexShrink:0, boxShadow:`0 0 6px ${typeColor}88` }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:600, color: darkMode ? "rgba(255,255,255,.85)" : "#1A3A4A", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{buyer}</div>
                        <div style={{ fontSize:12, marginTop:2, color: darkMode ? "rgba(255,255,255,.35)" : "#8aa5b5" }}>{veh}{f.vc_chassis ? ` · ${f.vc_chassis}` : ""}</div>
                      </div>
                      <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20, flexShrink:0, background:`${typeColor}22`, color:typeColor, border:`1px solid ${typeColor}44` }}>{typeLabel}</span>
                      <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20, flexShrink:0, background: isComplete?(darkMode?"rgba(46,204,138,.15)":"#E8F5EF"):(darkMode?"rgba(245,166,35,.13)":"#FEF6E7"), color: isComplete?"#2ecc8a":"#F5A623" }}>{isComplete?"✓ Done":"◷ Draft"}</span>
                      <span style={{ fontSize:11, color: darkMode?"rgba(255,255,255,.25)":"#aabfcc", flexShrink:0, minWidth:60, textAlign:"right" }}>{date}</span>
                      <button onClick={e => { e.stopPropagation(); downloadFromFormData(txn.form,"rlv"); }}
                        title="Download RLV (blue form)"
                        style={{ background:"rgba(33,150,201,.12)", border:"1px solid rgba(33,150,201,.3)", cursor:"pointer", fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:5, color:"#2196C9", flexShrink:0 }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(33,150,201,.25)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="rgba(33,150,201,.12)"; }}>RLV</button>
                      <button onClick={e => { e.stopPropagation(); downloadFromFormData(txn.form,"nco"); }}
                        title="Download NCO (yellow form)"
                        style={{ background:"rgba(245,166,35,.12)", border:"1px solid rgba(245,166,35,.3)", cursor:"pointer", fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:5, color:"#F5A623", flexShrink:0 }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(245,166,35,.25)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="rgba(245,166,35,.12)"; }}>NCO</button>
                      <button onClick={e => { e.stopPropagation(); deleteTxn(i); }}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, padding:"2px 6px", borderRadius:5, color: darkMode?"rgba(255,255,255,.2)":"#bbb", flexShrink:0 }}
                        onMouseEnter={e => e.currentTarget.style.color="#e05555"}
                        onMouseLeave={e => e.currentTarget.style.color=darkMode?"rgba(255,255,255,.2)":"#bbb"}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </>}

          {/* ══ BILLING VIEW ══ */}
          {dashView==="billing" && <>
            <div style={{ marginBottom:28 }}>
              <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:-0.5, margin:0, color: darkMode?"rgba(255,255,255,.9)":"#1A3A4A" }}>Billing</h1>
              <p style={{ fontSize:13, margin:"4px 0 0", color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa" }}>Subscription and usage</p>
            </div>
            {/* Current plan card */}
            <div style={{ background: darkMode?"rgba(255,255,255,.04)":"#fff", border:`1px solid ${darkMode?"rgba(255,255,255,.1)":"#dce8f0"}`, borderRadius:12, padding:"20px 24px", marginBottom:16, boxShadow: darkMode?"none":"0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: darkMode?"rgba(255,255,255,.3)":"#8aa5b5", marginBottom:12 }}>Current Plan</div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                <div>
                  <div style={{ fontSize:20, fontWeight:700, color: darkMode?"rgba(255,255,255,.9)":"#1A3A4A" }}>{user?.plan || "Individual"}</div>
                  <div style={{ fontSize:13, color: darkMode?"rgba(255,255,255,.4)":"#7a9aaa", marginTop:3 }}>
                    {user?.subscribed ? "Active subscription" : isTrial ? `Free trial · ${TRIAL_FORM_LIMIT - trialFormsUsed} of ${TRIAL_FORM_LIMIT} forms remaining` : "No active subscription"}
                  </div>
                </div>
                {isTrial && (
                  <div style={{ background:"rgba(245,166,35,.13)", border:"1px solid rgba(245,166,35,.3)", borderRadius:8, padding:"6px 14px", fontSize:12, color:"#F5A623", fontWeight:600 }}>
                    Trial expires {new Date(user.trial_expires_at).toLocaleDateString("en-ZA")}
                  </div>
                )}
              </div>
            </div>
            {/* Usage bar */}
            {isTrial && (
              <div style={{ background: darkMode?"rgba(255,255,255,.04)":"#fff", border:`1px solid ${darkMode?"rgba(255,255,255,.1)":"#dce8f0"}`, borderRadius:12, padding:"20px 24px", marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: darkMode?"rgba(255,255,255,.3)":"#8aa5b5", marginBottom:12 }}>Trial Usage</div>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:13, color: darkMode?"rgba(255,255,255,.7)":"#2a4a5a" }}>Forms used</span>
                  <span style={{ fontSize:13, fontWeight:600, color: darkMode?"rgba(255,255,255,.85)":"#1A3A4A" }}>{trialFormsUsed} / {TRIAL_FORM_LIMIT}</span>
                </div>
                <div style={{ height:6, borderRadius:3, background: darkMode?"rgba(255,255,255,.08)":"#e8eef2", overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:3, width:`${Math.min(100,(trialFormsUsed/TRIAL_FORM_LIMIT)*100)}%`, background: trialFormsUsed>=5?"#F5A623":trialFormsUsed>=7?"#e05555":"#2196C9", transition:"width .4s" }} />
                </div>
              </div>
            )}
            {/* Subscribe CTA */}
            {!user?.subscribed && (
              <div style={{ background:"linear-gradient(135deg,#1A3A4A,#2196C9)", borderRadius:12, padding:"24px", textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:700, color:"#fff", marginBottom:6 }}>Subscribe to ReZi</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.65)", marginBottom:16 }}>From R99/month · Cancel anytime · R15/form overage</div>
                <button onClick={() => setView("pricing")}
                  style={{ background:"#F5A623", color:"#1A3A4A", border:"none", borderRadius:9, padding:"10px 28px", fontWeight:700, fontSize:14, cursor:"pointer" }}>
                  View Plans →
                </button>
              </div>
            )}
          </>}

        </div>
      </div>
    </div>
    </DarkCtx.Provider>
  );
}
